// ============================================================
// LUCENT APP — Self-contained single file
// All modules inlined: Firebase · Audio Engine · ErrorBoundary
// ============================================================
//
// 🔐 ENVIRONMENT VARIABLES (.env)
// ─────────────────────────────────────────────────────────────
// Create a .env file in your project root (never commit this):
//
//   VITE_FIREBASE_API_KEY=your_api_key
//   VITE_FIREBASE_AUTH_DOMAIN=your_project.firebaseapp.com
//   VITE_FIREBASE_PROJECT_ID=your_project_id
//   VITE_FIREBASE_STORAGE_BUCKET=your_project.appspot.com
//   VITE_FIREBASE_MESSAGING_SENDER_ID=your_sender_id
//   VITE_FIREBASE_APP_ID=1:your_app_id
//   VITE_FIREBASE_MEASUREMENT_ID=G-XXXXXXXXXX
//
// 🛡️  REQUIRED FIRESTORE SECURITY RULES
// ─────────────────────────────────────────────────────────────
// Paste these into Firebase Console → Firestore → Rules:
//
//   rules_version = '2';
//   service cloud.firestore {
//     match /databases/{database}/documents {
//       // Users — only the owner can write their own profile
//       match /users/{userId} {
//         allow read: if request.auth != null;
//         allow write: if request.auth != null && request.auth.uid == userId;
//       }
//       // Shared collections — any signed-in user can read/write
//       match /posts/{doc}    { allow read, write: if request.auth != null; }
//       match /events/{doc}   { allow read, write: if request.auth != null; }
//       match /expenses/{doc} { allow read, write: if request.auth != null; }
//       match /notes/{doc}    { allow read, write: if request.auth != null; }
//       match /assignments/{doc} {
//         allow read, write: if request.auth != null &&
//                               request.auth.uid == resource.data.userId;
//         allow create: if request.auth != null;
//       }
//       match /messages/{doc} { allow read, write: if request.auth != null; }
//       // Private per-user collections — owner only
//       match /habits/{doc} {
//         allow read, write: if request.auth != null &&
//                               request.auth.uid == resource.data.userId;
//         allow create: if request.auth != null;
//       }
//       match /schedule/{doc} {
//         allow read, write: if request.auth != null &&
//                               request.auth.uid == resource.data.userId;
//         allow create: if request.auth != null;
//       }
//     }
//   }
// ============================================================
import { useState, useEffect, useRef, useCallback, useMemo, Component } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, updateProfile
} from "firebase/auth";
import {
  getFirestore, collection, addDoc, onSnapshot,
  query, orderBy, serverTimestamp, doc, setDoc,
  getDoc, getDocs, updateDoc, deleteDoc, where
} from "firebase/firestore";

// ============================================================
// FIREBASE CONFIG
// ============================================================
// Netlify Drop version — credentials inlined (private friend-group app only)
const firebaseConfig = {
  apiKey:            "AIzaSyBHkBvs1iGvEQqgOs8RexzmJ1U_aXoKhFQ",
  authDomain:        "lucent-830cf.firebaseapp.com",
  projectId:         "lucent-830cf",
  storageBucket:     "lucent-830cf.firebasestorage.app",
  messagingSenderId: "361591846739",
  appId:             "1:361591846739:web:700cab5568ecb15680d3a6",
  measurementId:     "G-XC2FC4J1LP",
};
const _app = initializeApp(firebaseConfig);
const auth = getAuth(_app);
const db   = getFirestore(_app);

// ============================================================
// AUDIO ENGINE (memory-leak-free — nodes auto-disconnect)
// ============================================================
let _ctx = null;
const getCtx = () => {
  if (!SFX_ENABLED) return null;
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
};
// Always-on audio context for timer sounds — not affected by Performance Mode
const getCtxAlways = () => {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
};
const VOL = 0.32;
function autoClean(nodes, dur) {
  setTimeout(() => { try { nodes.forEach(n => n.disconnect()); } catch(e) {} }, (dur + 0.1) * 1000);
}
const SFX = {
  tap: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const o1 = ctx.createOscillator(), o2 = ctx.createOscillator();
      const g = ctx.createGain(), f = ctx.createBiquadFilter();
      o1.type = "sine"; o1.frequency.setValueAtTime(1900, t); o1.frequency.exponentialRampToValueAtTime(950, t + 0.07);
      o2.type = "sine"; o2.frequency.setValueAtTime(2600, t); o2.frequency.exponentialRampToValueAtTime(1300, t + 0.055);
      f.type = "bandpass"; f.frequency.value = 2000; f.Q.value = 2.5;
      g.gain.setValueAtTime(VOL * 0.38, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.11);
      [o1,o2].forEach(o=>{o.connect(f);o.start(t);o.stop(t+0.14);});
      f.connect(g); g.connect(ctx.destination);
      autoClean([o1,o2,f,g], 0.2);
    } catch(e){}
  },
  nav: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const o = ctx.createOscillator(), g = ctx.createGain(), f = ctx.createBiquadFilter();
      o.type = "sine"; o.frequency.setValueAtTime(480, t); o.frequency.exponentialRampToValueAtTime(240, t + 0.14);
      f.type = "lowpass"; f.frequency.value = 700;
      g.gain.setValueAtTime(VOL * 0.28, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.17);
      o.connect(f); f.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + 0.2);
      autoClean([o,f,g], 0.25);
    } catch(e){}
  },
  confirm: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const nodes = [];
      [440,660,880].forEach((freq,i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "sine"; o.frequency.value = freq;
        g.gain.setValueAtTime(0, t+i*0.07); g.gain.linearRampToValueAtTime(VOL*0.22, t+i*0.07+0.01); g.gain.exponentialRampToValueAtTime(0.001, t+i*0.07+0.38);
        o.connect(g); g.connect(ctx.destination); o.start(t+i*0.07); o.stop(t+i*0.07+0.42);
        nodes.push(o,g);
      });
      autoClean(nodes, 0.7);
    } catch(e){}
  },
  like: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.setValueAtTime(650, t); o.frequency.exponentialRampToValueAtTime(1000, t+0.04); o.frequency.exponentialRampToValueAtTime(650, t+0.09);
      g.gain.setValueAtTime(VOL*0.28, t); g.gain.exponentialRampToValueAtTime(0.001, t+0.14);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t+0.16);
      autoClean([o,g], 0.2);
    } catch(e){}
  },
  message: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const sz = ctx.sampleRate * 0.1, buf = ctx.createBuffer(1,sz,ctx.sampleRate);
      const d = buf.getChannelData(0);
      for(let i=0;i<sz;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/sz,2.5);
      const src = ctx.createBufferSource(); src.buffer=buf;
      const f=ctx.createBiquadFilter(); f.type="bandpass"; f.frequency.value=2200; f.Q.value=0.9;
      const g=ctx.createGain(); g.gain.setValueAtTime(VOL*0.38,t); g.gain.exponentialRampToValueAtTime(0.001,t+0.1);
      src.connect(f); f.connect(g); g.connect(ctx.destination); src.start(t);
      autoClean([src,f,g], 0.15);
    } catch(e){}
  },
  error: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const o=ctx.createOscillator(), g=ctx.createGain();
      o.type="sawtooth"; o.frequency.setValueAtTime(200,t); o.frequency.exponentialRampToValueAtTime(80,t+0.22);
      g.gain.setValueAtTime(VOL*0.18,t); g.gain.exponentialRampToValueAtTime(0.001,t+0.26);
      o.connect(g); g.connect(ctx.destination); o.start(t); o.stop(t+0.28);
      autoClean([o,g], 0.35);
    } catch(e){}
  },
  slam: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const nodes = [];
      const sub=ctx.createOscillator(), sg=ctx.createGain();
      sub.type="sine"; sub.frequency.setValueAtTime(95,t); sub.frequency.exponentialRampToValueAtTime(25,t+0.5);
      sg.gain.setValueAtTime(VOL*1.4,t); sg.gain.exponentialRampToValueAtTime(0.001,t+0.55);
      sub.connect(sg); sg.connect(ctx.destination); sub.start(t); sub.stop(t+0.6);
      nodes.push(sub,sg);
      const crack=ctx.createOscillator(), cg=ctx.createGain();
      crack.type="sawtooth"; crack.frequency.setValueAtTime(3500,t); crack.frequency.exponentialRampToValueAtTime(300,t+0.08);
      cg.gain.setValueAtTime(VOL*0.6,t); cg.gain.exponentialRampToValueAtTime(0.001,t+0.1);
      crack.connect(cg); cg.connect(ctx.destination); crack.start(t); crack.stop(t+0.12);
      nodes.push(crack,cg);
      const bsz=Math.floor(ctx.sampleRate*0.08), nb=ctx.createBuffer(1,bsz,ctx.sampleRate);
      const nd=nb.getChannelData(0); for(let i=0;i<bsz;i++) nd[i]=(Math.random()*2-1)*Math.pow(1-i/bsz,2.2);
      const ns=ctx.createBufferSource(); ns.buffer=nb;
      const nf=ctx.createBiquadFilter(); nf.type="highpass"; nf.frequency.value=1100;
      const ng=ctx.createGain(); ng.gain.setValueAtTime(VOL*0.7,t); ng.gain.exponentialRampToValueAtTime(0.001,t+0.08);
      ns.connect(nf); nf.connect(ng); ng.connect(ctx.destination); ns.start(t);
      nodes.push(ns,nf,ng);
      autoClean(nodes, 0.7);
    } catch(e){}
  },
  ambient: (dur=4.5) => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const nodes = [];
      [52,78,104].forEach((freq,i)=>{
        const o=ctx.createOscillator(), g=ctx.createGain(), lfo=ctx.createOscillator(), lg=ctx.createGain();
        o.type="sine"; o.frequency.value=freq;
        lfo.type="sine"; lfo.frequency.value=0.22+i*0.07; lg.gain.value=2;
        lfo.connect(lg); lg.connect(o.frequency);
        g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(VOL*0.12,t+1.2); g.gain.setValueAtTime(VOL*0.12,t+dur-1); g.gain.linearRampToValueAtTime(0,t+dur);
        o.connect(g); g.connect(ctx.destination);
        lfo.start(t); o.start(t); lfo.stop(t+dur); o.stop(t+dur);
        nodes.push(o,g,lfo,lg);
      });
      autoClean(nodes, dur + 0.2);
    } catch(e){}
  },
  goldReveal: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const nodes = [];
      [1200,1600,2100,2700,3400].forEach((freq,i)=>{
        const o=ctx.createOscillator(), g=ctx.createGain();
        o.type="sine"; o.frequency.value=freq;
        g.gain.setValueAtTime(0,t+i*0.065); g.gain.linearRampToValueAtTime(VOL*0.12,t+i*0.065+0.04); g.gain.exponentialRampToValueAtTime(0.001,t+i*0.065+0.5);
        o.connect(g); g.connect(ctx.destination); o.start(t+i*0.065); o.stop(t+i*0.065+0.55);
        nodes.push(o,g);
      });
      autoClean(nodes, 0.9);
    } catch(e){}
  },
  cinematicIntro: () => {
    try {
      const ctx = getCtx(); if (!ctx) return;
      const t = ctx.currentTime;
      const sr = ctx.sampleRate;
      const allNodes = [];
      const masterComp = ctx.createDynamicsCompressor();
      masterComp.threshold.value = -14; masterComp.knee.value = 6;
      masterComp.ratio.value = 4; masterComp.attack.value = 0.003; masterComp.release.value = 0.18;
      const masterGain = ctx.createGain(); masterGain.gain.value = 1.35;
      masterComp.connect(masterGain); masterGain.connect(ctx.destination);
      const OUT = masterComp;
      allNodes.push(masterComp, masterGain);
      const mkReverb = (decaySec, wet) => {
        const len = Math.floor(sr * decaySec);
        const irBuf = ctx.createBuffer(2, len, sr);
        for (let ch = 0; ch < 2; ch++) {
          const d = irBuf.getChannelData(ch);
          for (let i = 0; i < len; i++) {
            const env = Math.pow(1 - i/len, 1.2);
            d[i] = (Math.random()*2-1) * env * (ch===0 ? 1 : 0.82);
          }
        }
        const conv = ctx.createConvolver(); conv.buffer = irBuf;
        const dryG = ctx.createGain(); dryG.gain.value = 1 - wet;
        const wetG = ctx.createGain(); wetG.gain.value = wet;
        const inG  = ctx.createGain(); inG.gain.value  = 1;
        const outG = ctx.createGain(); outG.gain.value = 1;
        inG.connect(dryG); dryG.connect(outG);
        inG.connect(conv); conv.connect(wetG); wetG.connect(outG);
        outG.connect(OUT);
        allNodes.push(conv,dryG,wetG,inG,outG);
        return inG;
      };
      const reverbLong  = mkReverb(5.5, 0.55);
      const reverbShort = mkReverb(1.2, 0.45);
      [[30,38,0,0.6,0.95,4.5,7.5],[60,76,0,0.8,0.55,4.5,7.5],[90,114,0,1.0,0.28,4.5,7]].forEach(([f1,f2,st,at,amp,hold,end],idx)=>{
        const o=ctx.createOscillator(), g=ctx.createGain();
        const hp=idx===0?ctx.createBiquadFilter():null;
        o.type=idx===2?"triangle":"sine";
        o.frequency.setValueAtTime(f1,t); o.frequency.linearRampToValueAtTime(f2,t+2.5);
        o.frequency.setValueAtTime(f2,t+2.5); o.frequency.linearRampToValueAtTime(f1,t+5);
        g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(amp,t+at);
        g.gain.setValueAtTime(amp,t+hold); g.gain.linearRampToValueAtTime(0,t+end);
        if(hp){hp.type="highpass";hp.frequency.value=18;o.connect(hp);hp.connect(g);allNodes.push(hp);}
        else o.connect(g);
        g.connect(OUT); o.start(t); o.stop(t+8);
        allNodes.push(o,g);
      });
      const noiseLen = Math.floor(sr * 8);
      const noiseBuf = ctx.createBuffer(2, noiseLen, sr);
      for (let ch = 0; ch < 2; ch++) {
        const d = noiseBuf.getChannelData(ch); let last = 0;
        for (let i = 0; i < noiseLen; i++) { const w=Math.random()*2-1; last=last*0.99+w*0.01; d[i]=last*8+w*0.35; }
      }
      const noiseSrc = ctx.createBufferSource(); noiseSrc.buffer = noiseBuf;
      const noiseF1 = ctx.createBiquadFilter(); noiseF1.type="bandpass"; noiseF1.frequency.value=80; noiseF1.Q.value=0.4;
      const noiseF2 = ctx.createBiquadFilter(); noiseF2.type="highshelf"; noiseF2.frequency.value=4000; noiseF2.gain.value=-18;
      const noiseG = ctx.createGain();
      noiseG.gain.setValueAtTime(0,t); noiseG.gain.linearRampToValueAtTime(0.07,t+1.8);
      noiseG.gain.setValueAtTime(0.07,t+5.5); noiseG.gain.linearRampToValueAtTime(0,t+7.5);
      noiseSrc.connect(noiseF1); noiseF1.connect(noiseF2); noiseF2.connect(noiseG); noiseG.connect(reverbLong);
      noiseSrc.start(t);
      allNodes.push(noiseSrc,noiseF1,noiseF2,noiseG);
      [55,55.25,82.4,82.7,110,110.3].forEach((freq,i)=>{
        const osc=ctx.createOscillator(), oscF=ctx.createBiquadFilter(), oscG=ctx.createGain();
        const panNode=ctx.createStereoPanner?ctx.createStereoPanner():null;
        osc.type="sawtooth"; osc.frequency.setValueAtTime(freq,t+0.3); osc.frequency.linearRampToValueAtTime(freq*1.018,t+5);
        oscF.type="lowpass"; oscF.frequency.setValueAtTime(120+i*30,t+0.3); oscF.frequency.linearRampToValueAtTime(600+i*60,t+3); oscF.Q.value=1.8;
        const startT=0.3+i*0.1;
        oscG.gain.setValueAtTime(0,t+startT); oscG.gain.linearRampToValueAtTime(0.18-i*0.015,t+startT+1.2);
        oscG.gain.setValueAtTime(0.18-i*0.015,t+5.5); oscG.gain.linearRampToValueAtTime(0,t+7.5);
        osc.connect(oscF); oscF.connect(oscG);
        if(panNode){panNode.pan.value=(i%2===0?-1:1)*(0.2+Math.floor(i/2)*0.25);oscG.connect(panNode);panNode.connect(reverbLong);allNodes.push(panNode);}
        else oscG.connect(reverbLong);
        osc.start(t+0.3); osc.stop(t+8);
        allNodes.push(osc,oscF,oscG);
      });
      const whooshLen=Math.floor(sr*0.7);
      const wBuf=ctx.createBuffer(2,whooshLen,sr);
      for(let ch=0;ch<2;ch++){const wd=wBuf.getChannelData(ch);for(let i=0;i<whooshLen;i++)wd[i]=(Math.random()*2-1)*Math.pow(i/whooshLen,0.25)*(1-i/whooshLen)*(ch===0?1:0.78);}
      const wSrc=ctx.createBufferSource(); wSrc.buffer=wBuf;
      const wF=ctx.createBiquadFilter(); wF.type="bandpass";
      wF.frequency.setValueAtTime(300,t+7.0); wF.frequency.exponentialRampToValueAtTime(4000,t+7.6); wF.Q.value=0.7;
      const wG=ctx.createGain(); wG.gain.setValueAtTime(0.5,t+7.0); wG.gain.linearRampToValueAtTime(0,t+7.7);
      wSrc.connect(wF); wF.connect(wG); wG.connect(reverbShort); wSrc.start(t+7.0);
      allNodes.push(wSrc,wF,wG);
      autoClean(allNodes, 8.5);
    } catch(e) { console.warn("cinematicIntro err:", e); }
  },
  timerTick: () => {
    try {
      const ctx=getCtxAlways(), t=ctx.currentTime, sr=ctx.sampleRate;
      const nodes=[];
      const clickLen=Math.floor(sr*0.008), clickBuf=ctx.createBuffer(1,clickLen,sr);
      const cd=clickBuf.getChannelData(0);
      for(let i=0;i<clickLen;i++) cd[i]=(Math.random()*2-1)*Math.pow(1-i/clickLen,0.6);
      const clickSrc=ctx.createBufferSource(); clickSrc.buffer=clickBuf;
      const clickF=ctx.createBiquadFilter(); clickF.type="bandpass"; clickF.frequency.value=3800; clickF.Q.value=1.2;
      const clickG=ctx.createGain(); clickG.gain.setValueAtTime(0.9,t); clickG.gain.exponentialRampToValueAtTime(0.001,t+0.008);
      clickSrc.connect(clickF); clickF.connect(clickG); clickG.connect(ctx.destination); clickSrc.start(t);
      nodes.push(clickSrc,clickF,clickG);
      const bodyLen=Math.floor(sr*0.055), bodyBuf=ctx.createBuffer(1,bodyLen,sr);
      const bd=bodyBuf.getChannelData(0);
      for(let i=0;i<bodyLen;i++) bd[i]=Math.sin(2*Math.PI*900*i/sr)*Math.pow(1-i/bodyLen,3.2);
      const bodySrc=ctx.createBufferSource(); bodySrc.buffer=bodyBuf;
      const bodyG=ctx.createGain(); bodyG.gain.setValueAtTime(0.38,t); bodyG.gain.exponentialRampToValueAtTime(0.001,t+0.055);
      bodySrc.connect(bodyG); bodyG.connect(ctx.destination); bodySrc.start(t);
      nodes.push(bodySrc,bodyG);
      const sub=ctx.createOscillator(), subG=ctx.createGain();
      sub.type="sine"; sub.frequency.setValueAtTime(180,t); sub.frequency.exponentialRampToValueAtTime(55,t+0.04);
      subG.gain.setValueAtTime(0.22,t); subG.gain.exponentialRampToValueAtTime(0.001,t+0.045);
      sub.connect(subG); subG.connect(ctx.destination); sub.start(t); sub.stop(t+0.05);
      nodes.push(sub,subG);
      autoClean(nodes, 0.1);
    } catch(e){}
  },
  timerStart: () => {
    try {
      const ctx=getCtxAlways(), t=ctx.currentTime, sr=ctx.sampleRate;
      const nodes=[];
      const comp=ctx.createDynamicsCompressor();
      comp.threshold.value=-12; comp.ratio.value=4; comp.attack.value=0.002; comp.release.value=0.1;
      comp.connect(ctx.destination); nodes.push(comp);
      const sub=ctx.createOscillator(), subG=ctx.createGain();
      sub.type="sine"; sub.frequency.setValueAtTime(55,t); sub.frequency.linearRampToValueAtTime(80,t+0.08); sub.frequency.exponentialRampToValueAtTime(40,t+0.35);
      subG.gain.setValueAtTime(0,t); subG.gain.linearRampToValueAtTime(1.1,t+0.012); subG.gain.exponentialRampToValueAtTime(0.001,t+0.4);
      sub.connect(subG); subG.connect(comp); sub.start(t); sub.stop(t+0.45);
      nodes.push(sub,subG);
      const clickLen=Math.floor(sr*0.04), clickBuf=ctx.createBuffer(1,clickLen,sr);
      const cd=clickBuf.getChannelData(0);
      for(let i=0;i<clickLen;i++) cd[i]=(Math.random()*2-1)*Math.pow(1-i/clickLen,1.8);
      const clickSrc=ctx.createBufferSource(); clickSrc.buffer=clickBuf;
      const clickF=ctx.createBiquadFilter(); clickF.type="bandpass"; clickF.frequency.value=1800; clickF.Q.value=1.5;
      const clickG=ctx.createGain(); clickG.gain.setValueAtTime(0.7,t); clickG.gain.exponentialRampToValueAtTime(0.001,t+0.04);
      clickSrc.connect(clickF); clickF.connect(clickG); clickG.connect(comp); clickSrc.start(t);
      nodes.push(clickSrc,clickF,clickG);
      const sweep=ctx.createOscillator(), sweepF=ctx.createBiquadFilter(), sweepG=ctx.createGain();
      sweep.type="sawtooth"; sweep.frequency.setValueAtTime(220,t+0.05); sweep.frequency.exponentialRampToValueAtTime(880,t+0.5);
      sweepF.type="lowpass"; sweepF.frequency.setValueAtTime(400,t+0.05); sweepF.frequency.linearRampToValueAtTime(2200,t+0.5); sweepF.Q.value=2;
      sweepG.gain.setValueAtTime(0,t+0.05); sweepG.gain.linearRampToValueAtTime(0.18,t+0.12); sweepG.gain.exponentialRampToValueAtTime(0.001,t+0.55);
      sweep.connect(sweepF); sweepF.connect(sweepG); sweepG.connect(comp); sweep.start(t+0.05); sweep.stop(t+0.6);
      nodes.push(sweep,sweepF,sweepG);
      [880,1320,1760].forEach((freq,i)=>{
        const p=ctx.createOscillator(), pg=ctx.createGain();
        p.type="sine"; p.frequency.value=freq;
        pg.gain.setValueAtTime(0,t+0.08+i*0.05); pg.gain.linearRampToValueAtTime(0.14-i*0.03,t+0.09+i*0.05); pg.gain.exponentialRampToValueAtTime(0.001,t+0.08+i*0.05+0.45);
        p.connect(pg); pg.connect(comp); p.start(t+0.08+i*0.05); p.stop(t+0.6+i*0.05);
        nodes.push(p,pg);
      });
      autoClean(nodes, 0.7);
    } catch(e){}
  },
  timerPause: () => {
    try {
      const ctx=getCtxAlways(), t=ctx.currentTime;
      const nodes=[];
      const sub=ctx.createOscillator(), subG=ctx.createGain();
      sub.type="sine"; sub.frequency.setValueAtTime(110,t); sub.frequency.exponentialRampToValueAtTime(28,t+0.3);
      subG.gain.setValueAtTime(0.9,t); subG.gain.exponentialRampToValueAtTime(0.001,t+0.35);
      sub.connect(subG); subG.connect(ctx.destination); sub.start(t); sub.stop(t+0.4);
      nodes.push(sub,subG);
      const glide=ctx.createOscillator(), glideF=ctx.createBiquadFilter(), glideG=ctx.createGain();
      glide.type="sawtooth"; glide.frequency.setValueAtTime(440,t); glide.frequency.exponentialRampToValueAtTime(165,t+0.4);
      glideF.type="lowpass"; glideF.frequency.setValueAtTime(1200,t); glideF.frequency.exponentialRampToValueAtTime(280,t+0.4); glideF.Q.value=3;
      glideG.gain.setValueAtTime(0,t+0.01); glideG.gain.linearRampToValueAtTime(0.25,t+0.03); glideG.gain.exponentialRampToValueAtTime(0.001,t+0.45);
      glide.connect(glideF); glideF.connect(glideG); glideG.connect(ctx.destination); glide.start(t); glide.stop(t+0.5);
      nodes.push(glide,glideF,glideG);
      autoClean(nodes, 0.6);
    } catch(e){}
  },
  timerReset: () => {
    try {
      const ctx=getCtxAlways(), t=ctx.currentTime, sr=ctx.sampleRate;
      const nodes=[];
      const sweep=ctx.createOscillator(), sweepF=ctx.createBiquadFilter(), sweepG=ctx.createGain();
      sweep.type="sawtooth"; sweep.frequency.setValueAtTime(1200,t); sweep.frequency.exponentialRampToValueAtTime(80,t+0.35);
      sweepF.type="lowpass"; sweepF.frequency.setValueAtTime(3000,t); sweepF.frequency.exponentialRampToValueAtTime(200,t+0.35); sweepF.Q.value=1.5;
      sweepG.gain.setValueAtTime(0.32,t); sweepG.gain.exponentialRampToValueAtTime(0.001,t+0.38);
      sweep.connect(sweepF); sweepF.connect(sweepG); sweepG.connect(ctx.destination); sweep.start(t); sweep.stop(t+0.42);
      nodes.push(sweep,sweepF,sweepG);
      const whooshLen=Math.floor(sr*0.4), wBuf=ctx.createBuffer(1,whooshLen,sr);
      const wd=wBuf.getChannelData(0);
      for(let i=0;i<whooshLen;i++) wd[i]=(Math.random()*2-1)*Math.pow(1-i/whooshLen,0.5);
      const wSrc=ctx.createBufferSource(); wSrc.buffer=wBuf;
      const wF=ctx.createBiquadFilter(); wF.type="bandpass"; wF.frequency.setValueAtTime(3500,t); wF.frequency.exponentialRampToValueAtTime(180,t+0.38); wF.Q.value=1.0;
      const wG=ctx.createGain(); wG.gain.setValueAtTime(0.45,t); wG.gain.exponentialRampToValueAtTime(0.001,t+0.4);
      wSrc.connect(wF); wF.connect(wG); wG.connect(ctx.destination); wSrc.start(t);
      nodes.push(wSrc,wF,wG);
      autoClean(nodes, 0.5);
    } catch(e){}
  },
  timerDone: () => {
    try {
      const ctx=getCtxAlways(), t=ctx.currentTime, sr=ctx.sampleRate;
      const nodes=[];
      const comp=ctx.createDynamicsCompressor();
      comp.threshold.value=-10; comp.ratio.value=3.5; comp.attack.value=0.001; comp.release.value=0.25;
      comp.connect(ctx.destination); nodes.push(comp);
      const revLen=Math.floor(sr*4.5), revBuf=ctx.createBuffer(2,revLen,sr);
      for(let ch=0;ch<2;ch++){const d=revBuf.getChannelData(ch);for(let i=0;i<revLen;i++)d[i]=(Math.random()*2-1)*Math.pow(1-i/revLen,1.1)*(ch===0?1:0.85);}
      const rev=ctx.createConvolver(); rev.buffer=revBuf;
      const revG=ctx.createGain(); revG.gain.value=0.5;
      rev.connect(revG); revG.connect(comp);
      nodes.push(rev,revG);
      const send=(node)=>{node.connect(comp);node.connect(rev);};
      [{freq:68,amp:1.0,decay:5.5},{freq:134,amp:0.62,decay:3.8},{freq:205,amp:0.48,decay:2.8},
       {freq:272,amp:0.34,decay:2.1},{freq:389,amp:0.25,decay:1.6},{freq:541,amp:0.16,decay:1.1},{freq:720,amp:0.10,decay:0.75}
      ].forEach(({freq,amp,decay})=>{
        const o=ctx.createOscillator(), g=ctx.createGain();
        o.type="sine"; o.frequency.value=freq;
        g.gain.setValueAtTime(0,t); g.gain.linearRampToValueAtTime(amp*0.9,t+0.012); g.gain.exponentialRampToValueAtTime(0.001,t+decay);
        o.connect(g); send(g); o.start(t); o.stop(t+decay+0.2);
        nodes.push(o,g);
      });
      autoClean(nodes, 6.0);
    } catch(e){}
  },
};
const haptic = (p=[8]) => { try{ navigator.vibrate&&navigator.vibrate(p); }catch(e){} };

// ============================================================
// HABIT AUTO-RESET
// Runs silently on login. Daily habits need no reset — the
// date-string system handles it naturally. Weekly habits get
// their current-week completions cleared when a new ISO week
// begins, while keeping all past data for streak history.
// ============================================================
function getISOWeek(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return `${d.getFullYear()}-W${String(1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)).padStart(2, "0")}`;
}

async function autoResetHabits(uid) {
  try {
    const snap = await getDocs(query(collection(db, "habits"), where("userId", "==", uid)));
    const currentWeek = getISOWeek();
    const batch = [];
    snap.forEach(d => {
      const h = d.data();
      const freq = h.frequency || "daily";
      if (freq === "weekly" && h.lastWeek !== currentWeek) {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - ((today.getDay() + 6) % 7));
        startOfWeek.setHours(0, 0, 0, 0);
        const kept = (h.completedDays || []).filter(ds => new Date(ds) < startOfWeek);
        batch.push(updateDoc(doc(db, "habits", d.id), { completedDays: kept, lastWeek: currentWeek }));
      }
    });
    await Promise.all(batch);
  } catch (e) {
    console.warn("[Lucent] autoResetHabits failed:", e);
  }
}

// ============================================================
// AUTO-ARCHIVE
// Runs silently on login. Notes older than 30 days and events
// whose date has passed by more than 7 days get flagged
// archived:true. They disappear from the main view but are
// still accessible via the "Archived" filter.
// ============================================================
async function autoArchiveOldDocs() {
  try {
    const now = Date.now();
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    const SEVEN_DAYS  =  7 * 24 * 60 * 60 * 1000;
    const batch = [];

    // Archive notes older than 30 days
    const notesSnap = await getDocs(query(collection(db, "notes"), where("archived", "==", false)));
    notesSnap.forEach(d => {
      const data = d.data();
      const created = data.createdAt?.toMillis?.() || 0;
      if (created && now - created > THIRTY_DAYS) {
        batch.push(updateDoc(doc(db, "notes", d.id), { archived: true }));
      }
    });

    // Archive past events (date passed by >7 days)
    const eventsSnap = await getDocs(query(collection(db, "events"), where("archived", "==", false)));
    eventsSnap.forEach(d => {
      const data = d.data();
      if (data.date) {
        const eventMs = new Date(data.date).getTime();
        if (!isNaN(eventMs) && now - eventMs > SEVEN_DAYS) {
          batch.push(updateDoc(doc(db, "events", d.id), { archived: true }));
        }
      }
    });

    await Promise.all(batch);
    if (batch.length > 0) console.log(`[Lucent] Auto-archived ${batch.length} doc(s).`);
  } catch (e) {
    console.warn("[Lucent] autoArchiveOldDocs failed:", e);
  }
}

// ============================================================
// PERFORMANCE MODE — manually toggled only (no auto-detection)
// ============================================================
const _perfOverride = (typeof window !== "undefined" && typeof localStorage !== "undefined")
  ? localStorage.getItem("lucent_perf_mode")
  : null;
let isLowEnd = _perfOverride === "1";
const SFX_ENABLED = !isLowEnd;
// ============================================================
// ONESIGNAL NOTIFICATION SCHEDULER
// ============================================================
const OS_APP_ID = "5492803a-68b2-4160-a6ab-e550f4dc2f1e";
// OS_API_KEY removed — server-side only. See scripts/sendAlarms.js.

// ============================================================
// ESCALATING REMINDERS — handled server-side by scripts/sendAlarms.js
// Removed from client: was broadcasting to ALL subscribers (not just the owner)
// and required the secret OS_API_KEY to be in the browser bundle.
// The GitHub Actions cron now handles per-user reminders via FCM.
// ============================================================
function scheduleEscalatingReminders(title, dueDateStr, dueTimeStr) {
  // no-op — reminders are sent by the server-side cron, not the browser
  if (dueDateStr && dueTimeStr) {
    console.log(`[Lucent] Reminders for "${title}" will be sent by server cron.`);
  }
}

// Send an instant push notification to everyone in the circle via Cloudflare Worker
const CF_WORKER_URL = "https://lucentpush.aurumapp.workers.dev";
async function sendInstantNotification(heading, content) {
  try {
    await fetch(CF_WORKER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ heading, content, url: "https://lucent.aurumapp.workers.dev" }),
    });
  } catch(e) {
    console.warn("[Lucent] Push failed:", e);
  }
}
// ============================================================
// ADMIN + LOCATION SYSTEM
// ============================================================
const ADMIN_UID = "IhtHCl3n7iSzR0iYzymIFhf2B1G3";
const EMERGENCY_CODE = "0000";

// Save current user's location to Firebase silently
async function saveUserLocation(uid, name) {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(async (pos) => {
    try {
      await setDoc(doc(db, "locations", uid), {
        uid, name,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        updatedAt: serverTimestamp(),
        updatedAtMs: Date.now(),
      }, { merge: true });
    } catch(e) {}
  }, ()=>{}, { enableHighAccuracy: true, timeout: 8000 });
}

// Start silent background location saving every 3 minutes
function startLocationTracking(uid, name) {
  saveUserLocation(uid, name);
  return setInterval(() => saveUserLocation(uid, name), 3 * 60 * 1000);
}

// Admin panel component — shows all members' last known location
function AdminLocationPanel({ onClose }) {
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    return onSnapshot(collection(db, "locations"), snap => {
      setLocs(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (b.updatedAtMs||0)-(a.updatedAtMs||0)));
    });
  }, []);

  const timeAgo = ms => {
    if (!ms) return "unknown";
    const diff = Date.now() - ms;
    const m = Math.floor(diff / 60000);
    const h = Math.floor(m / 60);
    const d = Math.floor(h / 24);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    if (m > 0) return `${m}m ago`;
    return "just now";
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:500,background:"#050505",display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{padding:"52px 24px 16px",borderBottom:"1px solid rgba(201,168,76,0.1)"}}>
        <div style={{fontSize:9,letterSpacing:3,color:"rgba(201,168,76,0.5)",fontFamily:"Jost,sans-serif",marginBottom:6}}>ADMIN — RESTRICTED</div>
        <div style={{fontSize:22,fontFamily:"'Cormorant Garamond',Georgia,serif",color:"#F2EDE2",fontWeight:300}}>Last Known Locations</div>
        <button onClick={onClose} style={{position:"absolute",top:52,right:24,background:"none",border:"none",color:"#4A4440",fontSize:20,cursor:"pointer"}}>✕</button>
      </div>

      {/* Location list */}
      <div style={{flex:1,overflowY:"auto",padding:"12px 0"}}>
        {locs.length === 0 && (
          <div style={{padding:"60px 24px",textAlign:"center",color:"#4A4440",fontFamily:"Jost,sans-serif",fontSize:13}}>No location data yet. Members need to open the app first.</div>
        )}
        {locs.map(l => (
          <div key={l.id} style={{padding:"16px 24px",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{fontSize:14,color:"#F2EDE2",fontFamily:"Jost,sans-serif",fontWeight:300}}>{l.name||"Unknown"}</div>
              <div style={{fontSize:10,color: Date.now()-(l.updatedAtMs||0) < 10*60*1000 ? "#4CAF50" : "#4A4440",fontFamily:"Jost,sans-serif",letterSpacing:1}}>{timeAgo(l.updatedAtMs)}</div>
            </div>
            <div style={{fontSize:11,color:"#4A4440",fontFamily:"Space Mono,monospace",marginBottom:10}}>
              {l.lat?.toFixed(6)}, {l.lng?.toFixed(6)}
              {l.accuracy && <span style={{marginLeft:8,opacity:0.6}}>±{Math.round(l.accuracy)}m</span>}
            </div>
            <a
              href={`https://www.google.com/maps?q=${l.lat},${l.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 14px",background:"rgba(201,168,76,0.06)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:10,color:"#C9A84C",fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:1.5,textDecoration:"none"}}
            >
              📍 OPEN IN MAPS
            </a>
          </div>
        ))}
      </div>

      <div style={{padding:"16px 24px",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
        <div style={{fontSize:9,color:"#4A4440",fontFamily:"Jost,sans-serif",letterSpacing:1,textAlign:"center"}}>LOCATION UPDATES EVERY 3 MINUTES WHILE APP IS OPEN</div>
      </div>
    </div>
  );
}

// ============================================================
// ERROR BOUNDARY
// ============================================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(`[Lucent] Tab crashed — ${this.props.name}:`, error, info);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{padding:"60px 24px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
        <div style={{width:56,height:56,borderRadius:"50%",background:"rgba(229,115,115,0.08)",border:"1px solid rgba(229,115,115,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>⚠</div>
        <div style={{fontSize:18,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif",color:"#F2EDE2"}}>{this.props.name||"This section"} encountered an error</div>
        <div style={{fontSize:12,color:"#4A4440",fontFamily:"Jost,sans-serif",letterSpacing:0.5,maxWidth:280,lineHeight:1.7}}>The rest of Lucent is still running. Try navigating away and back.</div>
        <button onClick={()=>this.setState({hasError:false,error:null})} style={{background:"none",border:"1px solid rgba(201,168,76,0.3)",borderRadius:12,padding:"10px 20px",color:"#C9A84C",fontSize:11,fontFamily:"Jost,sans-serif",letterSpacing:2,cursor:"pointer",marginTop:8}}>TRY AGAIN</button>
        {process.env.NODE_ENV==="development"&&this.state.error&&(
          <details style={{marginTop:16,textAlign:"left",width:"100%",background:"rgba(229,115,115,0.04)",border:"1px solid rgba(229,115,115,0.1)",borderRadius:12,padding:"12px 16px"}}>
            <summary style={{fontSize:10,color:"#E57373",fontFamily:"monospace",cursor:"pointer",letterSpacing:1}}>ERROR DETAILS (dev only)</summary>
            <pre style={{fontSize:10,color:"#4A4440",fontFamily:"monospace",marginTop:10,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{this.state.error.toString()}</pre>
          </details>
        )}
      </div>
    );
  }
}

// ============================================================
// DESIGN TOKENS
// ============================================================
const C = {
  black:"#050505", surface:"#0D0D0D", s2:"#111111", s3:"#191919",
  s4:"#222222", s5:"#2A2A2A", s6:"#323232",
  gold:"#C9A84C", goldDim:"#7A6230", goldPale:"#EDD98A",
  goldGlow:"rgba(201,168,76,0.12)", goldGlowStrong:"rgba(201,168,76,0.25)",
  goldGlowMax:"rgba(201,168,76,0.45)",
  text:"#F2EDE2", textMid:"#A09080", textDim:"#4A4440",
  error:"#E57373", online:"#4CAF50",
  glass:"rgba(255,255,255,0.03)", glassBorder:"rgba(255,255,255,0.06)",
  glassBorderHot:"rgba(201,168,76,0.2)",
};

// ============================================================
// GLOBAL CSS
// ============================================================
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@200;300;400;500;600&family=Space+Mono:wght@400;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;-webkit-tap-highlight-color:transparent;margin:0;padding:0;}
  html,body{background:#050505;overflow-x:hidden;}
  ::-webkit-scrollbar{width:2px;height:2px;}
  ::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:rgba(201,168,76,0.2);border-radius:2px;}
  ::-webkit-scrollbar-thumb:hover{background:rgba(201,168,76,0.4);}
  input,textarea,button{font-family:inherit;}
  input::placeholder,textarea::placeholder{color:#3A3530;}
  ::selection{background:rgba(201,168,76,0.25);color:#EDD98A;}
  @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes scaleIn{from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}}
  @keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}
  @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
  @keyframes breathe{0%,100%{opacity:0.5;transform:scale(1)}50%{opacity:1;transform:scale(1.02)}}
  @keyframes slamIn{0%{transform:translateY(-70px) scaleY(2.4) scaleX(0.4);opacity:0;filter:blur(10px);}52%{transform:translateY(5px) scaleY(0.88) scaleX(1.06);opacity:1;filter:blur(0);}74%{transform:translateY(-2px) scaleY(1.04) scaleX(0.98);}100%{transform:translateY(0) scaleY(1) scaleX(1);opacity:1;}}
  @keyframes glowPulse{0%,100%{text-shadow:0 0 20px rgba(201,168,76,0.3),0 0 60px rgba(201,168,76,0.1);}50%{text-shadow:0 0 30px rgba(201,168,76,0.75),0 0 90px rgba(201,168,76,0.35),0 0 4px rgba(237,217,138,0.6);}}
  @keyframes glowPulseBox{0%,100%{box-shadow:0 0 0 1px rgba(201,168,76,0.1),0 4px 24px rgba(0,0,0,0.8);}50%{box-shadow:0 0 0 1px rgba(201,168,76,0.35),0 4px 40px rgba(201,168,76,0.12),0 0 80px rgba(201,168,76,0.06);}}
  @keyframes letterFloat{0%,100%{transform:translateY(0px);}50%{transform:translateY(-6px);}}
  @keyframes cinematicLetterDrop{0%{opacity:0;transform:translateY(-120px) scaleY(3) scaleX(0.3) rotateX(60deg);filter:blur(20px);}45%{transform:translateY(8px) scaleY(0.85) scaleX(1.08) rotateX(0deg);opacity:1;filter:blur(0);}68%{transform:translateY(-3px) scaleY(1.05) scaleX(0.97);}84%{transform:translateY(1px) scaleY(0.98) scaleX(1.01);}100%{transform:translateY(0) scaleY(1) scaleX(1);opacity:1;}}
  @keyframes chromaShift{0%{text-shadow:-8px 0 0 rgba(255,0,0,0.7),8px 0 0 rgba(0,255,255,0.7);letter-spacing:0.5em;}60%{text-shadow:-2px 0 0 rgba(255,0,0,0.2),2px 0 0 rgba(0,255,255,0.2);letter-spacing:0.24em;}100%{text-shadow:0 0 0 transparent,0 0 0 transparent;letter-spacing:0.24em;}}
  @keyframes goldBurn{0%{color:#FFFFFF;text-shadow:0 0 80px #FFF,0 0 40px #C9A84C;}40%{color:#EDD98A;text-shadow:0 0 40px rgba(237,217,138,0.8),0 0 80px rgba(201,168,76,0.4);}100%{color:#C9A84C;text-shadow:0 0 20px rgba(201,168,76,0.3),0 0 60px rgba(201,168,76,0.1);}}
  @keyframes scanLineSwipe{0%{transform:translateY(-100%);}100%{transform:translateY(200vh);}}
  @keyframes orbitalRing{from{stroke-dashoffset:0;}to{stroke-dashoffset:-440;}}
  @keyframes subRingOrbit{from{stroke-dashoffset:0;}to{stroke-dashoffset:280;}}
  @keyframes runicFadeIn{from{opacity:0;transform:scale(0.8);}to{opacity:1;transform:scale(1);}}
  @keyframes cornerBracketTL{from{width:0;height:0;opacity:0;}to{width:28px;height:28px;opacity:1;}}
  @keyframes statusPingPulse{0%{opacity:1;transform:scale(1);}70%{opacity:0;transform:scale(2.5);}100%{opacity:0;transform:scale(2.5);}}
  @keyframes taglineReveal{from{opacity:0;letter-spacing:12px;filter:blur(4px);}to{opacity:1;letter-spacing:5px;filter:blur(0);}}
  @keyframes navPop{0%{transform:scale(1);}35%{transform:scale(1.22) translateY(-2px);}100%{transform:scale(1) translateY(0);}}
  @keyframes cardReveal{from{opacity:0;transform:translateY(22px) scale(0.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes ripple{0%{transform:scale(0);opacity:0.5;}100%{transform:scale(4);opacity:0;}}
  @keyframes tabSlideIn{from{opacity:0;transform:translateX(16px);}to{opacity:1;transform:translateX(0);}}
  @keyframes btnPress{0%{transform:scale(1);}40%{transform:scale(0.96);}100%{transform:scale(1);}}
  @keyframes liquidMorph{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}33%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;}66%{border-radius:50% 50% 20% 80%/25% 80% 20% 75%;}}
  @keyframes timerPulseRing{0%,100%{opacity:0.1;transform:scale(1);}50%{opacity:0.28;transform:scale(1.03);}}
  @keyframes timerDoneFlash{0%,100%{opacity:1;}50%{opacity:0.3;}}
  @keyframes timerDoneGlow{0%{box-shadow:0 0 0px rgba(201,168,76,0);}50%{box-shadow:0 0 60px rgba(201,168,76,0.5),0 0 120px rgba(201,168,76,0.2);}100%{box-shadow:0 0 0px rgba(201,168,76,0);}}
  @keyframes presetSlide{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
  @keyframes dotBounce{from{transform:translateY(0);opacity:0.3;}to{transform:translateY(-7px);opacity:1;}}
  @keyframes numberTick{0%{transform:translateY(8px);opacity:0;}100%{transform:translateY(0);opacity:1;}}
  @keyframes gradientShift{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes goldLine{from{width:0;opacity:0;}to{width:100%;opacity:1;}}
  @keyframes popIn{0%{transform:scale(0.4);opacity:0;}60%{transform:scale(1.1);}100%{transform:scale(1);opacity:1;}}
  @keyframes splashHoloSweep{0%{transform:translateX(-100%) skewX(-12deg);opacity:0;}15%{opacity:1;}100%{transform:translateX(450%) skewX(-12deg);opacity:0;}}
  @keyframes splashCornerGrow{from{width:0;height:0;opacity:0;}to{width:32px;height:32px;opacity:1;}}
  @keyframes splashTagReveal{0%{opacity:0;letter-spacing:20px;filter:blur(6px);}60%{letter-spacing:7px;opacity:0.8;}100%{opacity:1;letter-spacing:6px;filter:blur(0);}}
  @keyframes splashSubReveal{0%{opacity:0;transform:translateY(10px);}100%{opacity:1;transform:translateY(0);}}
  @keyframes splashOrbitCW{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
  @keyframes splashOrbitCCW{from{transform:rotate(0deg);}to{transform:rotate(-360deg);}}
  @keyframes splashPingRing{0%{transform:scale(1);opacity:0.5;}100%{transform:scale(4);opacity:0;}}
  @keyframes splashBarFill{from{width:0%;}to{width:100%;}}
  @keyframes splashFlicker{0%,100%{opacity:1;}8%{opacity:0.7;}12%{opacity:1;}58%{opacity:1;}62%{opacity:0.6;}67%{opacity:1;}}
  @keyframes splashGlitchH{0%,100%{clip-path:inset(0 0 100% 0);}10%{clip-path:inset(15% 0 40% 0);transform:translateX(-4px);}20%{clip-path:inset(60% 0 10% 0);transform:translateX(4px);}30%{clip-path:inset(30% 0 60% 0);transform:translateX(-2px);}40%{clip-path:inset(80% 0 5% 0);transform:translateX(3px);}50%{clip-path:inset(0 0 100% 0);}100%{clip-path:inset(0 0 100% 0);}}
  @keyframes splashLetterDrop{0%{opacity:0;transform:translateY(-160px) scaleY(4) scaleX(0.25) rotateX(70deg);filter:blur(24px) brightness(5);}30%{transform:translateY(10px) scaleY(0.82) scaleX(1.1) rotateX(-6deg);opacity:1;filter:blur(0) brightness(2.5);}52%{transform:translateY(-4px) scaleY(1.06) scaleX(0.97) rotateX(2deg);filter:brightness(1.6);}70%{transform:translateY(2px) scaleY(0.98) scaleX(1.01);filter:brightness(1.15);}86%{transform:translateY(-1px) scaleY(1.005);}100%{transform:translateY(0) scaleY(1) scaleX(1);opacity:1;filter:brightness(1);}}
  @keyframes splashLetterBurn{0%{color:#FFFFFF;text-shadow:0 0 60px #fff,0 0 30px #C9A84C,0 0 4px #fff;}35%{color:#EDD98A;text-shadow:0 0 30px rgba(237,217,138,0.9),0 0 60px rgba(201,168,76,0.5);}100%{color:#C9A84C;text-shadow:0 0 18px rgba(201,168,76,0.35),0 0 50px rgba(201,168,76,0.12);}}
  @keyframes splashHairlineGrow{0%{width:0;opacity:0;}100%{width:80px;opacity:0.5;}}
  @keyframes splashHairlineExpand{0%{width:80px;opacity:0.5;}100%{width:220px;opacity:1;}}
  @keyframes splashCursorBlink{0%,100%{opacity:1;}50%{opacity:0;}}
  @keyframes splashIdleWordmark{0%{opacity:0;transform:translateY(18px);filter:blur(6px);}100%{opacity:1;transform:translateY(0);filter:blur(0);}}
  @keyframes splashIdleTagline{0%{opacity:0;letter-spacing:14px;}100%{opacity:1;letter-spacing:5px;}}
  @keyframes splashFinalCollapse{0%{letter-spacing:0.25em;}100%{letter-spacing:0.18em;}}
  @keyframes splashFinalGlow{0%,100%{text-shadow:0 0 20px rgba(201,168,76,0.3),0 0 60px rgba(201,168,76,0.08);}40%{text-shadow:0 0 35px rgba(201,168,76,0.8),0 0 90px rgba(201,168,76,0.35),0 0 3px rgba(237,217,138,0.5);}80%{text-shadow:0 0 22px rgba(201,168,76,0.4),0 0 70px rgba(201,168,76,0.15);}}
  @keyframes splashVignetteIn{from{opacity:0;}to{opacity:1;}}
  @keyframes splashExit{0%{opacity:1;transform:scale(1) translateY(0);}25%{opacity:1;transform:scale(1.015) translateY(-4px);}100%{opacity:0;transform:scale(1.06) translateY(-18px);filter:blur(4px);}}
  @keyframes toastIn{from{transform:translateY(20px) scale(0.96);opacity:0;}to{transform:translateY(0) scale(1);opacity:1;}}
  @keyframes toastOut{from{transform:translateY(0) scale(1);opacity:1;}to{transform:translateY(-12px) scale(0.95);opacity:0;}}
  @keyframes heartBeat{0%,100%{transform:scale(1);}15%{transform:scale(1.35);}30%{transform:scale(1);}45%{transform:scale(1.2);}60%{transform:scale(1);}}
  .btn-press:active{animation:btnPress 0.18s ease both;}
  .card-hover{transition:transform 0.22s cubic-bezier(0.16,1,0.3,1),border-color 0.22s ease,box-shadow 0.22s ease;}
  .card-hover:active{transform:scale(0.975)!important;}
  .no-scroll{-ms-overflow-style:none;scrollbar-width:none;}
  .no-scroll::-webkit-scrollbar{display:none;}
  .glass-card{background:rgba(255,255,255,0.025);backdrop-filter:blur(20px) saturate(1.4);-webkit-backdrop-filter:blur(20px) saturate(1.4);border:1px solid rgba(255,255,255,0.06);}
  .glass-gold{background:linear-gradient(135deg,rgba(201,168,76,0.08) 0%,rgba(201,168,76,0.03) 100%);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid rgba(201,168,76,0.18);}
  .lux-input{background:rgba(255,255,255,0.025);border:1px solid rgba(255,255,255,0.07);transition:border-color 0.2s,box-shadow 0.2s,background 0.2s;}
  .lux-input:focus-within{background:rgba(201,168,76,0.04);border-color:rgba(201,168,76,0.3);box-shadow:0 0 0 3px rgba(201,168,76,0.07),0 4px 20px rgba(0,0,0,0.4);}
  @media (max-width:430px){
    .glass-card,.glass-gold{backdrop-filter:none;-webkit-backdrop-filter:none;}
    * { -webkit-tap-highlight-color: transparent; }
  }
  @media (prefers-reduced-motion: reduce) {
    *,*::before,*::after{animation-duration:0.01ms!important;animation-iteration-count:1!important;transition-duration:0.01ms!important;}
  }
`;

// ============================================================
// TOAST SYSTEM
// Uses a stable module-level ref (not a raw variable) so ToastContainer
// can remount freely without losing the setter reference.
// ============================================================
let _toastId = 0;
const _toastRef = { current: null }; // stable ref object — never reassigned

function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  // Store the setter in the stable ref on every render (safe — same identity after mount)
  _toastRef.current = setToasts;
  // Cleanup on unmount so stale setter is never called
  useEffect(() => () => { _toastRef.current = null; }, []);
  return (
    <div style={{position:"fixed",bottom:100,left:"50%",transform:"translateX(-50%)",zIndex:9000,display:"flex",flexDirection:"column",gap:8,alignItems:"center",pointerEvents:"none"}}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background:t.type==="error"?`rgba(229,115,115,0.12)`:`rgba(201,168,76,0.1)`,
          border:`1px solid ${t.type==="error"?"rgba(229,115,115,0.3)":"rgba(201,168,76,0.25)"}`,
          borderRadius:20,padding:"9px 18px",
          backdropFilter:isLowEnd?"none":"blur(20px)",WebkitBackdropFilter:isLowEnd?"none":"blur(20px)",
          fontSize:12,fontFamily:"Jost,sans-serif",fontWeight:400,
          color:t.type==="error"?"#E57373":"#EDD98A",
          letterSpacing:1,whiteSpace:"nowrap",
          animation:t.leaving?"toastOut 0.3s ease both":"toastIn 0.3s cubic-bezier(0.16,1,0.3,1) both",
          display:"flex",alignItems:"center",gap:8,
        }}>
          <span style={{fontSize:14}}>{t.type==="error"?"✕":"✦"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

const toast = (msg, type="success", duration=2500) => {
  const setToasts = _toastRef.current;
  if (!setToasts) return;
  const id = ++_toastId;
  setToasts(t => [...t, {id, msg, type}]);
  setTimeout(() => {
    const fn = _toastRef.current; if (!fn) return;
    fn(t => t.map(x => x.id===id ? {...x,leaving:true} : x));
    setTimeout(() => { const fn2 = _toastRef.current; if (fn2) fn2(t => t.filter(x => x.id!==id)); }, 350);
  }, duration);
};

// ============================================================
// REUSABLE UI COMPONENTS
// ============================================================
function Grain() {
  if (isLowEnd) return null;
  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,pointerEvents:"none",
      backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
      opacity:0.5,mixBlendMode:"overlay"}}/>
  );
}

function Avatar({ name="?", size=36, gold=false, photo=null, ring=false }) {
  const initials = name.split(" ").map(w=>w[0]||"").join("").slice(0,2).toUpperCase();
  return (
    <div style={{
      width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:photo?"none":gold?`linear-gradient(135deg,${C.goldDim},${C.gold})`:`linear-gradient(135deg,${C.s3},${C.s4})`,
      display:"flex",alignItems:"center",justifyContent:"center",
      fontSize:size*0.32,fontWeight:500,fontFamily:"Jost,sans-serif",
      color:gold?C.black:C.gold,
      border:ring?`2px solid ${C.gold}`:`1px solid ${gold?C.goldDim:C.s5}`,
      overflow:"hidden",letterSpacing:0.5,
      boxShadow:ring?`0 0 12px rgba(201,168,76,0.3)`:"none",
    }}>
      {photo?<img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:initials}
    </div>
  );
}

function GoldDivider({ margin="4px 0 20px" }) {
  return <div style={{height:1,background:`linear-gradient(90deg,transparent,${C.goldDim}44,${C.gold}66,${C.goldDim}44,transparent)`,margin}}/>;
}

function Spinner({ size=20 }) {
  return <div style={{width:size,height:size,border:`1.5px solid rgba(255,255,255,0.08)`,borderTop:`1.5px solid ${C.gold}`,borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>;
}

function LoadingDots() {
  return (
    <div style={{display:"flex",gap:5,justifyContent:"center",alignItems:"center",height:22}}>
      {[0,1,2].map(i=>(
        <div key={i} style={{width:5,height:5,borderRadius:"50%",background:C.goldDim,animation:`dotBounce 0.6s ${i*0.1}s infinite ease-in-out alternate`}}/>
      ))}
    </div>
  );
}

function StatusDot({ status }) {
  const col = status==="online"?C.online:status==="away"?C.gold:C.s5;
  return (
    <div style={{position:"relative",width:8,height:8}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:col,border:`1.5px solid ${C.black}`}}/>
      {status==="online"&&!isLowEnd&&<div style={{position:"absolute",inset:0,borderRadius:"50%",background:C.online,animation:"statusPingPulse 2s ease-out infinite"}}/>}
    </div>
  );
}

function RippleBtn({ children, onClick, style={}, className="", disabled=false }) {
  const [ripples, setRipples] = useState([]);
  const handleClick = (e) => {
    if (disabled) return;
    const rect=e.currentTarget.getBoundingClientRect();
    const x=e.clientX-rect.left, y=e.clientY-rect.top;
    const id=Date.now();
    setRipples(r=>[...r,{id,x,y}]);
    setTimeout(()=>setRipples(r=>r.filter(rp=>rp.id!==id)),600);
    // ✅ No SFX.tap() here — callers manage their own sound to prevent doubles.
    // Only fire a subtle haptic as a universal tactile layer.
    haptic([4]);
    onClick&&onClick(e);
  };
  return (
    <div onClick={handleClick} className={`btn-press ${className}`}
      style={{position:"relative",overflow:"hidden",cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.5:1,...style}}>
      {children}
      {ripples.map(rp=>(
        <span key={rp.id} style={{position:"absolute",left:rp.x,top:rp.y,width:32,height:32,borderRadius:"50%",background:`radial-gradient(circle,${C.gold}33,transparent 70%)`,transform:"translate(-50%,-50%) scale(0)",animation:"ripple 0.55s ease-out both",pointerEvents:"none"}}/>
      ))}
    </div>
  );
}

function Card({ children, style={}, onClick, gold=false, glow=false }) {
  const [hov,setHov]=useState(false);
  const [pressed,setPressed]=useState(false);
  return (
    <div
      onClick={()=>{if(onClick){haptic([7]);onClick();}}}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>{setHov(false);setPressed(false);}}
      onMouseDown={()=>setPressed(true)} onMouseUp={()=>setPressed(false)}
      onTouchStart={()=>setPressed(true)} onTouchEnd={()=>setPressed(false)}
      className="card-hover"
      style={{
        background:gold?`linear-gradient(135deg,rgba(201,168,76,0.07) 0%,rgba(201,168,76,0.02) 100%)`:`rgba(255,255,255,0.025)`,
        backdropFilter:isLowEnd?"none":"blur(20px)",WebkitBackdropFilter:isLowEnd?"none":"blur(20px)",
        border:`1px solid ${hov&&onClick?C.glassBorderHot:gold?"rgba(201,168,76,0.2)":C.glassBorder}`,
        borderRadius:18,padding:20,cursor:onClick?"pointer":"default",
        transform:pressed&&onClick?"scale(0.975)":"none",
        boxShadow:glow||gold?`0 4px 32px rgba(201,168,76,0.08),0 1px 0 rgba(255,255,255,0.04) inset`:hov&&onClick?`0 4px 32px rgba(0,0,0,0.5),0 0 0 1px rgba(201,168,76,0.1)`:`0 2px 16px rgba(0,0,0,0.4),0 1px 0 rgba(255,255,255,0.03) inset`,
        transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",...style
      }}>
      {children}
    </div>
  );
}

function GoldBtn({ children, onClick, disabled=false, outline=false, style={}, small=false }) {
  const [pressed,setPressed]=useState(false);
  const [hov,setHov]=useState(false);
  return (
    <button
      onClick={()=>{if(!disabled){haptic([8]);onClick&&onClick();}}}
      disabled={disabled}
      onMouseDown={()=>setPressed(true)} onMouseUp={()=>setPressed(false)}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>{setHov(false);setPressed(false);}}
      onTouchStart={()=>setPressed(true)} onTouchEnd={()=>setPressed(false)}
      style={{
        background:outline?"none":disabled?C.s3:hov?`linear-gradient(135deg,${C.gold},${C.goldPale},${C.gold})`:`linear-gradient(135deg,${C.goldDim},${C.gold},${C.goldPale}88)`,
        backgroundSize:"200% 200%",animation:!disabled&&!outline?"gradientShift 3s ease infinite":"none",
        border:`1px solid ${outline?"rgba(201,168,76,0.35)":disabled?C.s4:C.goldDim}`,
        color:outline?C.gold:disabled?C.textDim:C.black,
        borderRadius:14,padding:small?"9px 16px":"13px 22px",
        fontFamily:"Jost,sans-serif",fontWeight:600,fontSize:small?11:12,
        letterSpacing:small?2:2.5,cursor:disabled?"not-allowed":"pointer",
        transition:"all 0.2s",width:"100%",
        transform:pressed&&!disabled?"scale(0.97)":"scale(1)",
        boxShadow:pressed||disabled||outline?"none":`0 4px 20px rgba(201,168,76,0.25),0 1px 0 rgba(255,255,255,0.15) inset`,
        ...style,
      }}>
      {disabled?<LoadingDots/>:children}
    </button>
  );
}

function InputField({ label, value, onChange, type="text", placeholder, suffix, hint }) {
  const [focused,setFocused]=useState(false);
  return (
    <div style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:9,letterSpacing:3.5,color:focused?C.gold:C.textDim,fontFamily:"Jost,sans-serif",fontWeight:500,transition:"color 0.2s"}}>{label}</div>
        {hint&&<div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{hint}</div>}
      </div>
      <div className="lux-input" style={{display:"flex",alignItems:"center",borderRadius:14,padding:"0 16px"}}>
        <input type={type} value={value} onChange={e=>onChange(e.target.value)}
          onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
          placeholder={placeholder}
          style={{flex:1,background:"none",border:"none",outline:"none",color:C.text,fontSize:15,padding:"14px 0",fontFamily:"Jost,sans-serif",fontWeight:300,letterSpacing:0.3}}/>
        {suffix}
      </div>
    </div>
  );
}

function EmptyState({ icon="✦", text, sub="" }) {
  return (
    <div style={{textAlign:"center",padding:"60px 24px",color:C.textDim}}>
      <div style={{fontSize:40,marginBottom:14,opacity:0.3,animation:"breathe 4s infinite"}}>{icon}</div>
      <div style={{fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:300,letterSpacing:1,color:C.textDim,marginBottom:6}}>{text}</div>
      {sub&&<div style={{fontSize:12,fontFamily:"Jost,sans-serif",color:C.textDim,opacity:0.6}}>{sub}</div>}
    </div>
  );
}

function SectionLabel({ children, action, onAction }) {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
      <div style={{fontSize:9,letterSpacing:4,color:C.textDim,fontFamily:"Jost,sans-serif",fontWeight:500}}>{children}</div>
      {action&&<button onClick={onAction} style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:C.gold,fontFamily:"Jost,sans-serif",letterSpacing:1.5,padding:0}}>{action}</button>}
    </div>
  );
}

function Pill({ children, active=false, onClick }) {
  return (
    <button onClick={()=>{if(onClick){SFX.tap();haptic([5]);onClick();}}}
      style={{
        background:active?`linear-gradient(135deg,${C.goldDim}44,${C.gold}22)`:"rgba(255,255,255,0.03)",
        border:`1px solid ${active?"rgba(201,168,76,0.35)":"rgba(255,255,255,0.06)"}`,
        borderRadius:20,padding:"7px 14px",fontFamily:"Jost,sans-serif",fontSize:10,letterSpacing:2,
        color:active?C.gold:C.textDim,cursor:"pointer",transition:"all 0.2s",whiteSpace:"nowrap",flexShrink:0,
        boxShadow:active?`0 2px 12px rgba(201,168,76,0.15)`:"none",
      }}>{children}</button>
  );
}

// ============================================================
// SPLASH SCREEN
// ============================================================
function SplashScreen({ onDone }) {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(0); // 0=idle 1=ignition 2=reveal 3=final
  const [tapped, setTapped] = useState(false);
  const animRef = useRef(null);
  const startRef = useRef(null);
  const [exitAnim, setExitAnim] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [statusText, setStatusText] = useState("INITIALISING SYSTEM");
  const ambientRef = useRef(null);
  const tapTimeRef = useRef(null);
  const STATUS_SEQUENCE = ["INITIALISING SYSTEM", "LOADING MODULES", "CONNECTING CIRCLE", "AUTHENTICATING", "READY"];

  // ── ambient pad ──────────────────────────────────────────────
  useEffect(() => {
    if (tapped) {
      if (ambientRef.current) {
        try {
          const { gainNode, ctx, oscillators } = ambientRef.current;
          gainNode.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
          setTimeout(() => {
            try { oscillators.forEach(o => o.stop()); } catch (e) {}
            try { ambientRef.current = null; } catch (e) {}
          }, 700);
        } catch (e) {}
      }
      return;
    }
    const timer = setTimeout(() => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const master = ctx.createGain();
        master.gain.setValueAtTime(0, ctx.currentTime); master.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 2.5);
        master.connect(ctx.destination);
        const sub = ctx.createOscillator(); sub.type = "sine"; sub.frequency.value = 38;
        const subG = ctx.createGain(); subG.gain.value = 0.65;
        sub.connect(subG); subG.connect(master); sub.start();
        const pad = ctx.createOscillator(); pad.type = "triangle"; pad.frequency.value = 76;
        const padF = ctx.createBiquadFilter(); padF.type = "lowpass"; padF.frequency.value = 280;
        const padG = ctx.createGain(); padG.gain.value = 0.35;
        pad.connect(padF); padF.connect(padG); padG.connect(master); pad.start();
        const shimmer = ctx.createOscillator(); shimmer.type = "sine"; shimmer.frequency.value = 228;
        const shimG = ctx.createGain(); shimG.gain.value = 0.06;
        shimmer.connect(shimG); shimG.connect(master); shimmer.start();
        const lfo = ctx.createOscillator(); lfo.type = "sine"; lfo.frequency.value = 0.14;
        const lfoG = ctx.createGain(); lfoG.gain.value = 2.2;
        lfo.connect(lfoG); lfoG.connect(pad.frequency); lfo.start();
        ambientRef.current = { gainNode: master, ctx, oscillators: [sub, pad, shimmer, lfo] };
      } catch (e) {}
    }, 300);
    return () => clearTimeout(timer);
  }, [tapped]);

  // ── canvas engine ────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    if (isLowEnd) return; // skip heavy canvas on low-end devices
    const ctx = canvas.getContext('2d');
    const setSize = () => { canvas.width = canvas.offsetWidth || 430; canvas.height = canvas.offsetHeight || 900; };
    setSize();
    const W = canvas.width, H = canvas.height;
    const CX = W / 2, CY = H / 2;

    // constellation nodes
    const NODES = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.22, vy: (Math.random() - 0.5) * 0.22,
      r: Math.random() * 1.4 + 0.4,
      alpha: Math.random() * 0.6 + 0.2,
      phase: Math.random() * Math.PI * 2,
      pSpeed: Math.random() * 0.018 + 0.006,
    }));

    // DNA helix columns
    const HELIX_N = 22;
    // light rays
    const RAYS = Array.from({ length: 10 }, (_, i) => ({
      angle: (i / 10) * Math.PI * 2,
      width: Math.random() * 40 + 12,
      len: H * (1.4 + Math.random() * 0.6),
      baseAlpha: Math.random() * 0.04 + 0.015,
      speed: Math.random() * 0.0005 + 0.0002,
      phase: Math.random() * Math.PI * 2,
    }));

    // digital rain columns
    const COLS = Math.floor(W / 18);
    const RAIN = Array.from({ length: COLS }, (_, i) => ({
      x: i * 18 + 9, y: Math.random() * H,
      speed: Math.random() * 3 + 1.5,
      chars: Array.from({ length: 18 }, () => String.fromCharCode(0x30A0 + Math.floor(Math.random() * 60))),
      alpha: Math.random() * 0.18 + 0.04,
      len: Math.floor(Math.random() * 10 + 4),
    }));

    let shockRings = [];
    let explodeParticles = [];
    let active = true;
    let t = 0;
    let tapT = null;

    canvas._triggerShockwave = () => {
      tapT = performance.now();
      shockRings = Array.from({ length: 7 }, (_, i) => ({
        r: 8 + i * 8, maxR: 260 + i * 90,
        alpha: i === 0 ? 1.0 : 0.7 - i * 0.09,
        born: performance.now() + i * 48,
        color: i < 2 ? '#EDD98A' : '#C9A84C',
        thick: i === 0 ? 3 : i < 3 ? 2 : 1.2,
      }));
      explodeParticles = Array.from({ length: 120 }, () => {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 8 + 2;
        return { x: CX, y: CY, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, r: Math.random() * 2.5 + 0.5, life: 1, decay: Math.random() * 0.02 + 0.012, gold: Math.random() > 0.35 };
      });
    };

    const draw = (ts) => {
      if (!active) return;
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      t += 0.016;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#050505'; ctx.fillRect(0, 0, W, H);

      const idle = Math.min(1, Math.max(0, (elapsed - 200) / 1200));
      const afterTap = tapT ? Math.min(1, (ts - tapT) / 2000) : 0;

      // ── digital rain (pre-tap) ──────────────────────────────
      if (idle > 0.3 && afterTap < 0.8) {
        const rainA = idle * (1 - afterTap * 1.2) * 0.9;
        RAIN.forEach(col => {
          col.y += col.speed;
          if (col.y > H + col.len * 16) col.y = -col.len * 16;
          // shuffle chars occasionally
          if (Math.random() < 0.04) col.chars[Math.floor(Math.random() * col.chars.length)] = String.fromCharCode(0x30A0 + Math.floor(Math.random() * 60));
          for (let j = 0; j < col.len; j++) {
            const cy = col.y - j * 16;
            if (cy < 0 || cy > H) continue;
            const bright = j === 0 ? 1 : 1 - j / col.len;
            const a = col.alpha * bright * rainA;
            if (a < 0.005) continue;
            ctx.fillStyle = j === 0 ? `rgba(237,217,138,${Math.min(a * 1.8, 0.9)})` : `rgba(201,168,76,${a})`;
            ctx.font = `${j === 0 ? 13 : 11}px 'Space Mono', monospace`;
            ctx.fillText(col.chars[j % col.chars.length], col.x - 5, cy);
          }
        });
      }

      // ── light rays ─────────────────────────────────────────
      const rayPow = Math.min(1, Math.max(0, (elapsed - 800) / 1000)) * (0.5 + 0.5 * afterTap);
      if (rayPow > 0) {
        RAYS.forEach(ray => {
          ray.phase += ray.speed;
          const pulse = (Math.sin(ray.phase) + 1) / 2;
          const a = ray.baseAlpha * rayPow * (0.4 + 0.6 * pulse);
          if (a < 0.003) return;
          ctx.save(); ctx.translate(CX, CY); ctx.rotate(ray.angle + t * 0.008);
          const g = ctx.createLinearGradient(0, 0, 0, ray.len);
          g.addColorStop(0, `rgba(201,168,76,${a})`);
          g.addColorStop(0.35, `rgba(201,168,76,${a * 0.6})`);
          g.addColorStop(1, 'rgba(201,168,76,0)');
          ctx.fillStyle = g; ctx.fillRect(-ray.width / 2, 0, ray.width, ray.len);
          ctx.restore();
        });
      }

      // ── constellation + edges ──────────────────────────────
      if (idle > 0) {
        NODES.forEach(n => {
          n.x += n.vx; n.y += n.vy; n.phase += n.pSpeed;
          if (n.x < 0) n.x = W; if (n.x > W) n.x = 0;
          if (n.y < 0) n.y = H; if (n.y > H) n.y = 0;
        });
        for (let i = 0; i < NODES.length; i++) {
          for (let j = i + 1; j < NODES.length; j++) {
            const dx = NODES[i].x - NODES[j].x, dy = NODES[i].y - NODES[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 90) {
              const a = idle * (1 - dist / 90) * 0.18;
              ctx.strokeStyle = `rgba(201,168,76,${a})`; ctx.lineWidth = 0.5;
              ctx.beginPath(); ctx.moveTo(NODES[i].x, NODES[i].y); ctx.lineTo(NODES[j].x, NODES[j].y); ctx.stroke();
            }
          }
        }
        NODES.forEach(n => {
          const a = idle * n.alpha * (0.5 + 0.5 * Math.sin(n.phase));
          ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(201,168,76,${a * 0.7})`; ctx.fill();
        });
      }

      // ── DNA helix (idle) ──────────────────────────────────
      if (idle > 0.5 && afterTap < 0.6) {
        const helixA = Math.min(1, (idle - 0.5) / 0.5) * (1 - afterTap * 1.5);
        const helixH = H * 0.55, helixY = CY - helixH / 2;
        const helixX = CX + 80;
        for (let i = 0; i < HELIX_N; i++) {
          const frac = i / (HELIX_N - 1);
          const yPos = helixY + frac * helixH;
          const phase1 = frac * Math.PI * 3 + t * 0.7;
          const phase2 = phase1 + Math.PI;
          const x1 = helixX + Math.cos(phase1) * 18, x2 = helixX + Math.cos(phase2) * 18;
          const a = helixA * (0.5 + 0.5 * Math.sin(frac * Math.PI));
          // rung
          ctx.strokeStyle = `rgba(201,168,76,${a * 0.3})`; ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.moveTo(x1, yPos); ctx.lineTo(x2, yPos); ctx.stroke();
          // strand dots
          [{ x: x1 }, { x: x2 }].forEach(pt => {
            ctx.beginPath(); ctx.arc(pt.x, yPos, 2.5, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(237,217,138,${a * 0.8})`; ctx.fill();
          });
        }
        // connecting splines
        for (let strand = 0; strand < 2; strand++) {
          ctx.beginPath(); ctx.lineWidth = 1;
          for (let i = 0; i < HELIX_N; i++) {
            const frac = i / (HELIX_N - 1);
            const yPos = helixY + frac * helixH;
            const phase = frac * Math.PI * 3 + t * 0.7 + (strand === 1 ? Math.PI : 0);
            const xPos = helixX + Math.cos(phase) * 18;
            if (i === 0) ctx.moveTo(xPos, yPos); else ctx.lineTo(xPos, yPos);
          }
          ctx.strokeStyle = `rgba(201,168,76,${helixA * 0.45})`; ctx.stroke();
        }
      }

      // ── shockwave rings ──────────────────────────────────────
      const now = performance.now();
      shockRings = shockRings.filter(ring => {
        if (now < ring.born) return true;
        const age = (now - ring.born) / 1000;
        const r = ring.r + age * (ring.maxR - ring.r) * 2.8;
        const a = ring.alpha * Math.max(0, 1 - age * 2.0);
        if (a < 0.004) return false;
        ctx.beginPath(); ctx.arc(CX, CY, r, 0, Math.PI * 2);
        ctx.strokeStyle = ring.color === '#EDD98A' ? `rgba(237,217,138,${a})` : `rgba(201,168,76,${a})`;
        ctx.lineWidth = ring.thick; ctx.stroke();
        return true;
      });

      // ── explosion particles ───────────────────────────────────
      explodeParticles = explodeParticles.filter(p => {
        p.x += p.vx; p.y += p.vy; p.vx *= 0.96; p.vy *= 0.96; p.life -= p.decay;
        if (p.life <= 0) return false;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
        ctx.fillStyle = p.gold ? `rgba(237,217,138,${p.life * 0.9})` : `rgba(201,168,76,${p.life * 0.7})`; ctx.fill();
        return true;
      });

      // ── central orb (post-tap glow) ──────────────────────────
      if (afterTap > 0) {
        const orbA = afterTap * 0.5 * Math.sin(t * 2.5);
        if (orbA > 0) {
          const grd = ctx.createRadialGradient(CX, CY, 0, CX, CY, 160 * afterTap);
          grd.addColorStop(0, `rgba(237,217,138,${orbA * 0.12})`);
          grd.addColorStop(0.5, `rgba(201,168,76,${orbA * 0.06})`);
          grd.addColorStop(1, 'rgba(201,168,76,0)');
          ctx.fillStyle = grd; ctx.fillRect(0, 0, W, H);
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => { active = false; cancelAnimationFrame(animRef.current); };
  }, []);

  const handleTap = () => {
    if (tapped) return;
    setTapped(true);
    tapTimeRef.current = performance.now();
    if (canvasRef.current?._triggerShockwave) canvasRef.current._triggerShockwave();
    if (!isLowEnd) SFX.cinematicIntro();
    haptic([10, 30, 10, 30, 50]);
    setPhase(1);
    // progress bar: 0→100 over ~5s with non-linear easing (starts fast, slows near 100)
    let p = 0; let tick = 0;
    const iv = setInterval(() => {
      tick++;
      // accelerate early, decelerate near end
      const rate = p < 40 ? 2.2 : p < 75 ? 1.6 : p < 92 ? 0.9 : 0.3;
      p = Math.min(p + rate, 100);
      setProgressVal(p);
      // cycle status text at milestones
      if (p >= 20 && p < 21) setStatusText("LOADING MODULES");
      if (p >= 45 && p < 46) setStatusText("CONNECTING CIRCLE");
      if (p >= 72 && p < 73) setStatusText("AUTHENTICATING");
      if (p >= 96) setStatusText("READY");
      if (p >= 100) clearInterval(iv);
    }, 70);
    if (typeof window !== "undefined") window._lucentSplashIv = iv;
    setTimeout(() => setPhase(2), 1400);
    setTimeout(() => setPhase(3), 4400);
    setTimeout(() => setExitAnim(true), 7200);
    setTimeout(() => onDone(), 7900);
  };

  // ── HUD corner brackets ─────────────────────────────────────
  const Bracket = ({ corner }) => {
    const s = { position: "absolute", width: 32, height: 32 };
    if (corner === "tl") { s.top = 0; s.left = 0; }
    if (corner === "tr") { s.top = 0; s.right = 0; s.transform = "scaleX(-1)"; }
    if (corner === "bl") { s.bottom = 0; s.left = 0; s.transform = "scaleY(-1)"; }
    if (corner === "br") { s.bottom = 0; s.right = 0; s.transform = "scale(-1,-1)"; }
    return (
      <div style={{ ...s, animation: `splashCornerGrow 0.6s ${corner === "tl" ? 0 : corner === "tr" ? 0.1 : corner === "bl" ? 0.15 : 0.2}s ease both` }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M2 28 L2 2 L28 2" stroke="rgba(201,168,76,0.7)" strokeWidth="1.5" fill="none" />
          <circle cx="2" cy="2" r="2" fill="rgba(237,217,138,0.9)" />
        </svg>
      </div>
    );
  };

  // ── orbital rings SVG ───────────────────────────────────────
  const OrbitalRings = () => (
    <div style={{ position: "relative", width: 160, height: 160 }}>
      {/* outer ring */}
      <div style={{ position: "absolute", inset: 0, animation: "splashOrbitCW 8s linear infinite" }}>
        <svg width="160" height="160" viewBox="0 0 160 160">
          <circle cx="80" cy="80" r="74" stroke="rgba(201,168,76,0.15)" strokeWidth="1" fill="none" strokeDasharray="8 6" />
          <circle cx="80" cy="6" r="3.5" fill="rgba(237,217,138,0.9)" />
          <circle cx="154" cy="80" r="2" fill="rgba(201,168,76,0.6)" />
        </svg>
      </div>
      {/* middle ring */}
      <div style={{ position: "absolute", inset: 14, animation: "splashOrbitCCW 5s linear infinite" }}>
        <svg width="132" height="132" viewBox="0 0 132 132">
          <circle cx="66" cy="66" r="60" stroke="rgba(201,168,76,0.22)" strokeWidth="1.5" fill="none" strokeDasharray="4 8" />
          <circle cx="66" cy="6" r="4.5" fill="rgba(237,217,138,1)" filter="url(#glow)" />
        </svg>
      </div>
      {/* inner ring */}
      <div style={{ position: "absolute", inset: 30, animation: "splashOrbitCW 3.5s linear infinite" }}>
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" stroke="rgba(201,168,76,0.35)" strokeWidth="2" fill="none" strokeDasharray="2 4" />
          <circle cx="50" cy="8" r="5" fill="rgba(237,217,138,1)" />
        </svg>
      </div>
      {/* core */}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "radial-gradient(circle, rgba(237,217,138,0.9), rgba(201,168,76,0.4))", boxShadow: "0 0 20px rgba(201,168,76,0.8), 0 0 40px rgba(201,168,76,0.3)", animation: "breathe 2s infinite" }} />
      </div>
      {/* ping */}
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 24, height: 24, borderRadius: "50%", border: "1px solid rgba(201,168,76,0.6)", animation: "splashPingRing 1.8s ease-out infinite" }} />
      </div>
    </div>
  );

  return (
    <div
      onClick={!tapped ? handleTap : undefined}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, overflow: "hidden",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: tapped ? "default" : "pointer",
        animation: exitAnim ? "splashExit 0.8s cubic-bezier(0.16,1,0.3,1) both" : undefined,
      }}
    >
      {/* canvas bg */}
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />

      {/* radial vignette — deepens edges, focuses center */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1,
        background: "radial-gradient(ellipse 70% 60% at 50% 50%, transparent 30%, rgba(5,5,5,0.55) 100%)",
        animation: "splashVignetteIn 2s 0.5s ease both",
      }} />

      {/* scan line overlay */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.04) 3px, rgba(0,0,0,0.04) 4px)", pointerEvents: "none", zIndex: 1 }} />

      {/* HUD frame (post-tap) */}
      {phase >= 1 && (
        <div style={{ position: "absolute", inset: 24, border: "1px solid rgba(201,168,76,0.08)", borderRadius: 2, pointerEvents: "none", zIndex: 2 }}>
          <Bracket corner="tl" /> <Bracket corner="tr" /> <Bracket corner="bl" /> <Bracket corner="br" />
        </div>
      )}

      {/* holographic sweep (post-tap) */}
      {phase >= 1 && (
        <div style={{
          position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 3,
        }}>
          <div style={{
            position: "absolute", top: 0, left: "-30%", width: "30%", height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.06), rgba(237,217,138,0.12), rgba(201,168,76,0.06), transparent)",
            animation: "splashHoloSweep 2.2s 0.3s ease-in-out both",
          }} />
          <div style={{
            position: "absolute", top: 0, left: "-30%", width: "20%", height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(237,217,138,0.08), transparent)",
            animation: "splashHoloSweep 1.8s 2.8s ease-in-out both",
          }} />
        </div>
      )}

      {/* ── PHASE 0: idle ── */}
      {phase === 0 && (
        <div style={{ position: "relative", zIndex: 4, textAlign: "center" }}>
          {/* orbital rings — offset higher */}
          <div style={{ marginBottom: 44, display: "flex", justifyContent: "center", animation: "fadeIn 1.8s ease both" }}>
            <OrbitalRings />
          </div>

          {/* hairline divider that grows in */}
          <div style={{
            width: 80, height: 1, margin: "0 auto 28px",
            background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
            animation: "splashHairlineGrow 1.2s 0.4s ease both",
          }} />

          {/* wordmark — floats up into view */}
          <div style={{
            fontSize: 58, fontWeight: 300, letterSpacing: 16, color: C.gold,
            fontFamily: "'Cormorant Garamond',Georgia,serif",
            animation: "splashIdleWordmark 1.4s 0.2s cubic-bezier(0.16,1,0.3,1) both, glowPulse 3.5s 2s infinite",
            lineHeight: 1,
          }}>
            LUCENT
          </div>

          {/* fine rule below wordmark */}
          <div style={{
            width: 60, height: 1,
            background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
            margin: "18px auto 14px", opacity: 0.45,
            animation: "splashIdleWordmark 1.2s 0.5s ease both",
          }} />

          {/* tagline */}
          <div style={{
            fontSize: 8, letterSpacing: 5, color: C.goldDim,
            fontFamily: "Jost,sans-serif", fontWeight: 200,
            animation: "splashIdleTagline 1.6s 0.8s cubic-bezier(0.16,1,0.3,1) both, breathe 4s 2.6s infinite",
          }}>
            YOUR CIRCLE · ALWAYS
          </div>

          {/* tap prompt with blinking cursor */}
          <div style={{
            marginTop: 52, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            animation: "fadeIn 1s 2s ease both",
          }}>
            <div style={{ width: 24, height: 1, background: `linear-gradient(90deg, transparent, ${C.goldDim})`, opacity: 0.6 }} />
            <div style={{
              fontSize: 8, letterSpacing: 5, color: C.goldDim,
              fontFamily: "Jost,sans-serif", fontWeight: 200,
              animation: "breathe 2.4s 2s infinite",
            }}>
              TAP TO ENTER
            </div>
            <div style={{
              width: 5, height: 10, background: C.gold, opacity: 0.7,
              animation: "splashCursorBlink 1.1s 2s step-end infinite",
            }} />
            <div style={{ width: 24, height: 1, background: `linear-gradient(90deg, ${C.goldDim}, transparent)`, opacity: 0.6 }} />
          </div>
        </div>
      )}

      {/* ── PHASE 1: ignition — letters drop + burn to gold ── */}
      {phase >= 1 && phase < 3 && (
        <div style={{ position: "relative", zIndex: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* vertical accent line above */}
          <div style={{
            width: 1, height: 20, marginBottom: 16,
            background: `linear-gradient(to bottom, transparent, ${C.gold})`,
            animation: "splashSubReveal 0.4s 0.4s ease both",
          }} />

          {/* letters — drop + chroma burst → gold burn */}
          <div style={{ letterSpacing: 12, lineHeight: 1, position: "relative" }}>
            {"LUCENT".split("").map((l, i) => (
              <span key={i} style={{
                display: "inline-block",
                fontSize: 66, fontWeight: 300,
                fontFamily: "'Cormorant Garamond',Georgia,serif",
                color: C.gold,
                animation: [
                  `splashLetterDrop 1.05s ${0.06 + i * 0.08}s cubic-bezier(0.16,1,0.3,1) both`,
                  `splashLetterBurn 1.4s ${0.5 + i * 0.08}s ease both`,
                ].join(", "),
                willChange: "transform, filter, color",
              }}>{l}</span>
            ))}
          </div>

          {/* tagline */}
          <div style={{
            fontSize: 8, letterSpacing: 6, color: C.goldDim,
            fontFamily: "Jost,sans-serif", fontWeight: 200, marginTop: 18,
            animation: "splashTagReveal 1.2s 1s cubic-bezier(0.16,1,0.3,1) both",
          }}>
            YOUR CIRCLE · ALWAYS
          </div>

          {/* divider rule */}
          <div style={{
            width: 0, height: 1, margin: "22px auto 0",
            background: `linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)`,
            animation: "goldLine 0.8s 1.3s ease both",
          }} />

          {/* status line with cycling text */}
          <div style={{
            marginTop: 20, fontFamily: "Space Mono, monospace",
            fontSize: 9, letterSpacing: 3, color: "rgba(201,168,76,0.5)",
            animation: "splashSubReveal 0.5s 1.5s ease both",
            minWidth: 200, textAlign: "center",
            transition: "opacity 0.3s ease",
          }}>
            {statusText}
          </div>

          {/* progress bar — segmented aesthetic */}
          <div style={{
            marginTop: 14, width: 220, height: 2,
            background: "rgba(255,255,255,0.04)", borderRadius: 1, overflow: "hidden",
            animation: "splashSubReveal 0.4s 1.6s ease both",
            position: "relative",
          }}>
            <div style={{
              height: "100%", borderRadius: 1,
              background: `linear-gradient(90deg, rgba(201,168,76,0.35), ${C.gold} 60%, #EDD98A)`,
              width: `${progressVal}%`, transition: "width 0.1s linear",
              boxShadow: `0 0 10px rgba(237,217,138,0.8), 0 0 4px ${C.gold}`,
            }} />
            {/* leading glow dot */}
            <div style={{
              position: "absolute", top: -2, height: 6, width: 6, borderRadius: "50%",
              background: "#EDD98A",
              boxShadow: "0 0 8px #EDD98A, 0 0 16px rgba(201,168,76,0.7)",
              left: `calc(${progressVal}% - 3px)`,
              transition: "left 0.1s linear",
              opacity: progressVal > 0 && progressVal < 100 ? 1 : 0,
            }} />
          </div>

          {/* percentage */}
          <div style={{
            marginTop: 10, fontFamily: "Space Mono, monospace",
            fontSize: 11, color: "rgba(201,168,76,0.65)", letterSpacing: 2,
            animation: "splashSubReveal 0.4s 1.7s ease both",
            fontVariantNumeric: "tabular-nums",
          }}>
            {Math.round(progressVal).toString().padStart(3, '0')}
            <span style={{ fontSize: 8, opacity: 0.6 }}>%</span>
          </div>

          {/* vertical accent line below */}
          <div style={{
            width: 1, height: 22, marginTop: 14,
            background: `linear-gradient(to bottom, ${C.gold}, transparent)`,
            animation: "splashSubReveal 0.4s 0.5s ease both",
          }} />
        </div>
      )}

      {/* ── PHASE 3: cinematic final hold ── */}
      {phase >= 3 && (
        <div style={{
          position: "relative", zIndex: 4,
          display: "flex", flexDirection: "column", alignItems: "center",
          animation: "fadeIn 0.6s ease both",
        }}>
          {/* wordmark collapses letter-spacing to settled state */}
          <div style={{
            letterSpacing: 14, lineHeight: 1,
            animation: "splashFinalCollapse 1.2s 0.2s cubic-bezier(0.16,1,0.3,1) both",
          }}>
            {"LUCENT".split("").map((l, i) => (
              <span key={i} style={{
                display: "inline-block",
                fontSize: 66, fontWeight: 300,
                fontFamily: "'Cormorant Garamond',Georgia,serif",
                color: C.gold,
                animation: `splashFinalGlow 2.8s ${0.3 + i * 0.1}s ease-in-out infinite`,
              }}>{l}</span>
            ))}
          </div>

          {/* expanding hairline */}
          <div style={{
            height: 1, margin: "20px auto 16px",
            background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)`,
            animation: "splashHairlineExpand 0.9s 0.1s cubic-bezier(0.16,1,0.3,1) both",
          }} />

          {/* tagline */}
          <div style={{
            fontSize: 8, letterSpacing: 6, color: C.goldDim,
            fontFamily: "Jost,sans-serif", fontWeight: 200,
            animation: "splashTagReveal 1s 0.3s ease both",
          }}>
            YOUR CIRCLE · ALWAYS
          </div>

          {/* subtle status */}
          <div style={{
            marginTop: 24, fontFamily: "Space Mono, monospace",
            fontSize: 9, letterSpacing: 3, color: "rgba(201,168,76,0.35)",
            animation: "fadeIn 0.5s 0.6s ease both",
          }}>
            READY
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AUTH SCREEN
// ============================================================
function AuthScreen({ onAuth }) {
  const [mode,setMode]=useState("login");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [name,setName]=useState("");
  const [loading,setLoading]=useState(false);
  const [gLoading,setGLoading]=useState(false);
  const [error,setError]=useState("");
  const [pwVis,setPwVis]=useState(false);
  const [visible,setVisible]=useState(false);
  useEffect(()=>{setTimeout(()=>setVisible(true),80);},[]);

  const handleEmail=async()=>{
    if(!email||!password){setError("Please fill in all fields.");SFX.error();return;}
    setLoading(true);setError("");
    try{
      let u;
      if(mode==="signup"){
        const c=await createUserWithEmailAndPassword(auth,email,password);
        u=c.user;
        await updateProfile(u,{displayName:name||email.split("@")[0]});
        await setDoc(doc(db,"users",u.uid),{name:name||email.split("@")[0],email:u.email,uid:u.uid,status:"online",heartbeat:Date.now(),createdAt:serverTimestamp()});
      }else{
        const c=await signInWithEmailAndPassword(auth,email,password);
        u=c.user;
        await updateDoc(doc(db,"users",u.uid),{status:"online"});
      }
      SFX.confirm();haptic([10,20,10,20,30]);onAuth();
    }catch(e){
      SFX.error();haptic([30,10,30]);
      const msg=e.code==="auth/wrong-password"?"Wrong password":e.code==="auth/user-not-found"?"No account found":e.code==="auth/email-already-in-use"?"Email already registered":e.code==="auth/weak-password"?"Password must be at least 6 characters":e.message;
      setError(msg);
    }
    setLoading(false);
  };

  const handleGoogle=async()=>{
    setGLoading(true);setError("");
    try{
      const r=await signInWithPopup(auth,new GoogleAuthProvider());
      const u=r.user;
      await setDoc(doc(db,"users",u.uid),{name:u.displayName||"",email:u.email,uid:u.uid,photo:u.photoURL||null,status:"online",heartbeat:Date.now(),updatedAt:serverTimestamp()},{merge:true});
      SFX.confirm();haptic([10,20,10,20,30]);onAuth();
    }catch(e){
      if(e.code!=="auth/popup-closed-by-user"){SFX.error();setError("Google sign-in failed. Try again.");}
    }
    setGLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",background:`radial-gradient(ellipse at 50% 0%,rgba(201,168,76,0.08) 0%,transparent 60%),${C.black}`}}>
      <div style={{width:"100%",maxWidth:430,padding:"0 0 0",display:"flex",flexDirection:"column",alignItems:"center",gap:0}}>
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:48,opacity:visible?1:0,transform:visible?"translateY(0)":"translateY(-20px)",transition:"all 0.8s cubic-bezier(0.16,1,0.3,1)"}}>
          <div style={{position:"relative",marginBottom:16}}>
            <div style={{width:48,height:48,background:`linear-gradient(135deg,${C.goldDim},${C.gold})`,transform:"rotate(45deg)",animation:"glowPulse 3s infinite",boxShadow:`0 0 30px rgba(201,168,76,0.3)`}}/>
            <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:C.black,fontFamily:"Jost,sans-serif",fontWeight:700,letterSpacing:1}}>L</div>
          </div>
          <div style={{fontSize:42,fontWeight:300,letterSpacing:16,color:C.gold,fontFamily:"'Cormorant Garamond',Georgia,serif",animation:"glowPulse 4s 0.5s infinite"}}>LUCENT</div>
          <div style={{fontSize:9,letterSpacing:6,color:C.goldDim,fontFamily:"Jost,sans-serif",fontWeight:300,marginTop:10}}>YOUR CIRCLE · ALWAYS</div>
          <div style={{width:0,height:1,background:`linear-gradient(90deg,transparent,${C.gold}66,transparent)`,marginTop:20,animation:"goldLine 1.2s 0.4s ease both"}}/>
        </div>
        <div style={{width:"100%",maxWidth:430,background:`linear-gradient(180deg,rgba(15,15,15,0.98) 0%,${C.black} 100%)`,borderRadius:"28px 28px 0 0",border:`1px solid rgba(255,255,255,0.06)`,borderBottom:"none",padding:"28px 24px 52px",backdropFilter:"blur(40px)",boxShadow:"0 -20px 80px rgba(0,0,0,0.8), 0 -1px 0 rgba(201,168,76,0.08)",opacity:visible?1:0,transform:visible?"translateY(0)":"translateY(40px)",transition:"all 0.75s 0.15s cubic-bezier(0.16,1,0.3,1)"}}>
          <div style={{display:"flex",background:"rgba(255,255,255,0.03)",borderRadius:14,padding:4,marginBottom:26,gap:4,border:"1px solid rgba(255,255,255,0.05)"}}>
            {["login","signup"].map(m=>(
              <button key={m} onClick={()=>{SFX.tap();haptic([6]);setMode(m);setError("");}}
                style={{flex:1,padding:"11px",background:mode===m?`linear-gradient(135deg,${C.goldDim},${C.gold}BB)`:"none",border:"none",borderRadius:10,color:mode===m?C.black:C.textDim,fontSize:11,letterSpacing:3,fontFamily:"Jost,sans-serif",fontWeight:mode===m?700:400,cursor:"pointer",transition:"all 0.25s",boxShadow:mode===m?`0 2px 12px rgba(201,168,76,0.2)`:"none"}}>
                {m==="login"?"SIGN IN":"JOIN NOW"}
              </button>
            ))}
          </div>
          {mode==="signup"&&<InputField label="FULL NAME" value={name} onChange={setName} placeholder="Your full name"/>}
          <InputField label="EMAIL ADDRESS" value={email} onChange={setEmail} type="email" placeholder="your@email.com"/>
          <InputField label="PASSWORD" value={password} onChange={setPassword} type={pwVis?"text":"password"} placeholder="••••••••"
            hint={<button onClick={()=>{SFX.tap();setPwVis(!pwVis);}} style={{background:"none",border:"none",cursor:"pointer",color:C.textDim,fontSize:9,fontFamily:"Jost,sans-serif",letterSpacing:2,padding:0}}>{pwVis?"HIDE":"SHOW"}</button>}/>
          {error&&<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.error,fontFamily:"Jost,sans-serif",marginBottom:14,padding:"10px 14px",background:"rgba(229,115,115,0.06)",borderRadius:10,border:"1px solid rgba(229,115,115,0.15)",animation:"fadeIn 0.25s ease both"}}><span>⚠</span>{error}</div>}
          <GoldBtn onClick={handleEmail} disabled={loading} style={{marginTop:6}}>{mode==="login"?"ENTER LUCENT":"CREATE ACCOUNT"}</GoldBtn>
          <div style={{display:"flex",alignItems:"center",gap:14,margin:"20px 0"}}>
            <div style={{flex:1,height:1,background:"rgba(255,255,255,0.06)"}}/><div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:3}}>OR</div><div style={{flex:1,height:1,background:"rgba(255,255,255,0.06)"}}/>
          </div>
          <button onClick={handleGoogle} disabled={gLoading}
            style={{width:"100%",padding:"14px",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,color:C.text,fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:300,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:12,transition:"all 0.2s",backdropFilter:"blur(10px)"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(201,168,76,0.25)";e.currentTarget.style.background="rgba(201,168,76,0.04)";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.07)";e.currentTarget.style.background="rgba(255,255,255,0.03)";}}>
            {gLoading?<Spinner/>:<>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66 2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </>}
          </button>
          <div style={{textAlign:"center",marginTop:20,fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif",lineHeight:1.8,letterSpacing:0.5}}>Your circle stays private. Always.</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// NAV
// ============================================================
const NAV=[{id:"home",icon:"◈",label:"Home"},{id:"feed",icon:"◉",label:"Circle"},{id:"events",icon:"◇",label:"Events"},{id:"expenses",icon:"◎",label:"Ledger"},{id:"chat",icon:"◐",label:"Chat"}];
const NAV_MORE=[{id:"notes",icon:"◫",label:"Notes"},{id:"habits",icon:"◑",label:"Habits"},{id:"schedule",icon:"▣",label:"Schedule"},{id:"assignments",icon:"◧",label:"Tasks"},{id:"timer",icon:"◷",label:"Timer"}];

function BottomNav({ tab, setTab }) {
  return (
    <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:430,background:"rgba(5,5,5,0.95)",backdropFilter:isLowEnd?"none":"blur(30px)",WebkitBackdropFilter:isLowEnd?"none":"blur(30px)",borderTop:"1px solid rgba(255,255,255,0.05)",padding:"8px 0 24px",zIndex:100}}>
      <div style={{position:"absolute",top:-1,left:"10%",right:"10%",height:1,background:`linear-gradient(90deg,transparent,${C.gold}44,transparent)`}}/>
      <div style={{display:"flex",justifyContent:"space-around"}}>
        {NAV.map(n=>(
          <button key={n.id} onClick={()=>setTab(n.id)}
            style={{background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,padding:"8px 12px",position:"relative",minWidth:54}}>
            {tab===n.id&&<div style={{position:"absolute",top:2,left:"50%",transform:"translateX(-50%)",width:3,height:3,borderRadius:"50%",background:C.gold,animation:"popIn 0.3s ease both",boxShadow:`0 0 6px ${C.gold}`}}/>}
            <div style={{fontSize:20,color:tab===n.id?C.gold:C.textDim,transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",animation:tab===n.id?"navPop 0.3s ease both":undefined,filter:tab===n.id?`drop-shadow(0 0 6px rgba(201,168,76,0.5))`:"none"}}>{n.icon}</div>
            <div style={{fontSize:8,letterSpacing:1.5,fontFamily:"Jost,sans-serif",color:tab===n.id?C.gold:C.textDim,fontWeight:tab===n.id?600:300,transition:"all 0.2s"}}>{n.label.toUpperCase()}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function SettingsToggle({ on, onToggle }) {
  return (
    <div onClick={onToggle} style={{
      width:42, height:24, borderRadius:12, cursor:"pointer",
      background: on ? `linear-gradient(90deg,${C.goldDim},${C.gold})` : "rgba(255,255,255,0.08)",
      border: `1px solid ${on ? C.goldDim : "rgba(255,255,255,0.1)"}`,
      position:"relative", transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",
      boxShadow: on ? `0 0 10px rgba(201,168,76,0.3)` : "none",
      flexShrink:0,
    }}>
      <div style={{
        position:"absolute", top:3, left: on ? 20 : 3,
        width:16, height:16, borderRadius:"50%",
        background: on ? C.black : "rgba(255,255,255,0.4)",
        transition:"left 0.25s cubic-bezier(0.16,1,0.3,1)",
        boxShadow:"0 1px 4px rgba(0,0,0,0.4)",
      }}/>
    </div>
  );
}

function AppHeader({ tab, user, onSignOut }) {
  const titles={home:"LUCENT",feed:"Circle",events:"Events",expenses:"Ledger",notes:"Archives",habits:"Discipline",schedule:"Schedule",assignments:"Tasks",chat:"Chamber",timer:"Tempus"};
  const subtitles={home:"YOUR CIRCLE · ALWAYS",feed:"What's happening",events:"Plan together",expenses:"Split fairly",notes:"Shared knowledge",habits:"Build discipline",schedule:"Stay on track",assignments:"Track your work",chat:"Message the circle",timer:"Own your time"};
  const [menu, setMenu] = useState(false);
  const [page, setPage] = useState("main"); // main | profile | status | appearance | notifications
  const [editName, setEditName] = useState(user?.displayName||"");
  const [savingName, setSavingName] = useState(false);
  const [userStatus, setUserStatus] = useState("online");
  const [notifMessages, setNotifMessages] = useState(true);
  const [notifEvents, setNotifEvents] = useState(true);
  const [notifHabits, setNotifHabits] = useState(false);
  const [dimMode, setDimMode] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [perfMode, setPerfMode] = useState(()=>(typeof localStorage !== "undefined" && localStorage.getItem("lucent_perf_mode")==="1"));

  const openMenu = () => { setPage("main"); setMenu(true); SFX.tap(); };
  const closeMenu = () => setMenu(false);
  const goPage = (p) => { setPage(p); SFX.nav(); };

  const statusOptions = [
    { key:"online",  label:"Online",        color:C.online,  icon:"●" },
    { key:"away",    label:"Away",           color:C.gold,    icon:"◐" },
    { key:"dnd",     label:"Do Not Disturb", color:C.error,   icon:"◉" },
    { key:"offline", label:"Invisible",      color:C.textDim, icon:"○" },
  ];

  const saveName = async () => {
    if (!editName.trim() || editName.trim() === user?.displayName) return;
    setSavingName(true); SFX.confirm();
    try {
      await updateProfile(user, { displayName: editName.trim() });
      await updateDoc(doc(db,"users",user.uid), { name: editName.trim() });
      toast("Name updated","success");
    } catch(e) { toast("Failed to update name","error"); SFX.error(); }
    setSavingName(false);
  };

  const saveStatus = async (s) => {
    setUserStatus(s); SFX.tap();
    try { await updateDoc(doc(db,"users",user.uid), { status: s }); } catch(e){}
  };

  const Row = ({ icon, label, sub, onClick, danger }) => (
    <RippleBtn onClick={onClick} style={{
      width:"100%", display:"flex", alignItems:"center", gap:14,
      padding:"13px 18px", background:"none", border:"none",
      borderBottom:"1px solid rgba(255,255,255,0.04)", cursor:"pointer",
      transition:"background 0.15s",
    }}>
      <div style={{ width:32, height:32, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center",
        background: danger ? "rgba(229,115,115,0.08)" : "rgba(201,168,76,0.07)",
        border: `1px solid ${danger ? "rgba(229,115,115,0.15)" : "rgba(201,168,76,0.12)"}`,
        fontSize:15, color: danger ? C.error : C.gold, flexShrink:0,
      }}>{icon}</div>
      <div style={{flex:1, textAlign:"left"}}>
        <div style={{fontSize:13, color: danger ? C.error : C.text, fontFamily:"Jost,sans-serif", fontWeight:300, letterSpacing:0.3}}>{label}</div>
        {sub && <div style={{fontSize:10, color:C.textDim, fontFamily:"Jost,sans-serif", marginTop:2, letterSpacing:0.3}}>{sub}</div>}
      </div>
      {!danger && <div style={{color:C.textDim, fontSize:12}}>›</div>}
    </RippleBtn>
  );

  const SectionLabel2 = ({children}) => (
    <div style={{fontSize:8, letterSpacing:3, color:C.textDim, fontFamily:"Jost,sans-serif", padding:"14px 18px 6px"}}>{children}</div>
  );

  const BackBtn = ({label}) => (
    <RippleBtn onClick={()=>goPage("main")} style={{display:"flex",alignItems:"center",gap:8,padding:"12px 18px",background:"none",border:"none",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.04)",width:"100%"}}>
      <span style={{color:C.gold,fontSize:16}}>‹</span>
      <span style={{fontSize:11,letterSpacing:3,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{label}</span>
    </RippleBtn>
  );

  const renderPage = () => {
    if (page === "profile") return (
      <>
        <BackBtn label="PROFILE"/>
        <div style={{padding:"20px 18px"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,marginBottom:24}}>
            <div style={{position:"relative"}}>
              <Avatar name={user?.displayName||"U"} size={64} gold photo={user?.photoURL} ring/>
              <div style={{position:"absolute",bottom:2,right:2}}><StatusDot status={userStatus}/></div>
            </div>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:16,color:C.text,fontFamily:"'Cormorant Garamond',serif",fontWeight:300}}>{user?.displayName||"User"}</div>
              <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:3}}>{user?.email}</div>
            </div>
          </div>
          <div style={{fontSize:9,letterSpacing:3,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:10}}>DISPLAY NAME</div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <input value={editName} onChange={e=>setEditName(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&saveName()}
              placeholder="Your name"
              style={{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"11px 14px",color:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",transition:"border-color 0.2s"}}
              onFocus={e=>e.target.style.borderColor="rgba(201,168,76,0.3)"}
              onBlur={e=>e.target.style.borderColor="rgba(255,255,255,0.08)"}
            />
            <RippleBtn onClick={saveName} style={{
              padding:"11px 16px",borderRadius:12,fontSize:11,letterSpacing:1.5,fontFamily:"Jost,sans-serif",
              background:editName.trim()&&editName.trim()!==user?.displayName?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.04)",
              border:`1px solid ${editName.trim()&&editName.trim()!==user?.displayName?C.goldDim:"rgba(255,255,255,0.06)"}`,
              color:editName.trim()&&editName.trim()!==user?.displayName?C.black:C.textDim,
              cursor:"pointer",transition:"all 0.2s",
            }}>{savingName?"…":"SAVE"}</RippleBtn>
          </div>
          <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:10,letterSpacing:0.5,opacity:0.7}}>Changes will be visible to your circle.</div>
        </div>
      </>
    );

    if (page === "status") return (
      <>
        <BackBtn label="STATUS"/>
        <div style={{padding:"12px 0"}}>
          {statusOptions.map(s=>(
            <RippleBtn key={s.key} onClick={()=>saveStatus(s.key)} style={{
              width:"100%",display:"flex",alignItems:"center",gap:14,padding:"14px 18px",
              background:userStatus===s.key?"rgba(201,168,76,0.05)":"none",
              border:"none",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.04)",
              transition:"background 0.15s",
            }}>
              <div style={{width:10,height:10,borderRadius:"50%",background:s.color,boxShadow:`0 0 8px ${s.color}`,flexShrink:0}}/>
              <div style={{flex:1,textAlign:"left",fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>{s.label}</div>
              {userStatus===s.key&&<div style={{fontSize:14,color:C.gold}}>✓</div>}
            </RippleBtn>
          ))}
        </div>
      </>
    );

    if (page === "appearance") return (
      <>
        <BackBtn label="APPEARANCE"/>
        <div style={{padding:"8px 0"}}>
          {[
            {label:"Dim Mode", sub:"Reduce brightness of backgrounds", val:dimMode, set:setDimMode},
            {label:"Compact Mode", sub:"Tighter spacing throughout the app", val:compactMode, set:setCompactMode},
          ].map((item,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>{item.label}</div>
                <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2}}>{item.sub}</div>
              </div>
              <SettingsToggle on={item.val} onToggle={()=>{item.set(!item.val);SFX.tap();}}/>
            </div>
          ))}

          {/* Performance Mode toggle */}
          <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderBottom:"1px solid rgba(255,255,255,0.04)",background:perfMode?"rgba(201,168,76,0.03)":"none"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>Performance Mode</div>
                {perfMode&&<div style={{fontSize:8,letterSpacing:2,color:C.gold,fontFamily:"Jost,sans-serif",background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:6,padding:"2px 6px"}}>ON</div>}
              </div>
              <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2}}>Disable heavy effects for smoother experience</div>
              {perfMode&&<div style={{fontSize:9,color:C.gold,fontFamily:"Jost,sans-serif",marginTop:4,letterSpacing:0.5}}>⚡ Restart app to fully apply</div>}
            </div>
            <SettingsToggle on={perfMode} onToggle={()=>{
              const next=!perfMode;
              setPerfMode(next);
              localStorage.setItem("lucent_perf_mode", next?"1":"0");
              SFX.tap();
              setTimeout(()=>window.location.reload(), 600);
            }}/>
          </div>

          <div style={{padding:"16px 18px 8px"}}>
            <div style={{fontSize:9,letterSpacing:3,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:14}}>THEME ACCENT</div>
            <div style={{display:"flex",gap:10}}>
              {["#C9A84C","#7EB8A0","#8B7FD4","#D47F7F"].map((col,i)=>(
                <div key={i} onClick={()=>SFX.tap()} style={{width:32,height:32,borderRadius:"50%",background:col,cursor:"pointer",boxShadow:i===0?`0 0 0 2px ${C.gold},0 0 12px ${col}44`:"none",transition:"box-shadow 0.2s"}}/>
              ))}
            </div>
            <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:10,opacity:0.6}}>More accent colours coming soon.</div>
          </div>
        </div>
      </>
    );

    if (page === "notifications") return (
      <>
        <BackBtn label="NOTIFICATIONS"/>
        <div style={{padding:"8px 0"}}>
          {[
            {label:"Messages", sub:"New messages in Chamber", val:notifMessages, set:setNotifMessages},
            {label:"Events", sub:"Upcoming event reminders", val:notifEvents, set:setNotifEvents},
            {label:"Habits", sub:"Daily habit check-in reminders", val:notifHabits, set:setNotifHabits},
          ].map((item,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>{item.label}</div>
                <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2}}>{item.sub}</div>
              </div>
              <SettingsToggle on={item.val} onToggle={()=>{item.set(!item.val);SFX.tap();}}/>
            </div>
          ))}
        </div>
      </>
    );

    // main page
    const currentStatus = statusOptions.find(s=>s.key===userStatus);
    return (
      <>
        {/* Profile hero */}
        <div style={{padding:"18px 18px 14px",borderBottom:"1px solid rgba(255,255,255,0.06)",background:`linear-gradient(135deg,rgba(201,168,76,0.06),transparent)`}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{position:"relative"}}>
              <Avatar name={user?.displayName||"U"} size={44} gold photo={user?.photoURL} ring/>
              <div style={{position:"absolute",bottom:1,right:1}}><StatusDot status={userStatus}/></div>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:15,fontFamily:"Jost,sans-serif",fontWeight:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.displayName||"User"}</div>
              <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2,letterSpacing:0.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user?.email}</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginTop:12,cursor:"pointer"}} onClick={()=>goPage("status")}>
            <div style={{width:7,height:7,borderRadius:"50%",background:currentStatus?.color,boxShadow:`0 0 6px ${currentStatus?.color}`}}/>
            <div style={{fontSize:9,color:currentStatus?.color,fontFamily:"Jost,sans-serif",letterSpacing:2}}>{currentStatus?.label?.toUpperCase()}</div>
            <div style={{fontSize:10,color:C.textDim,marginLeft:2}}>›</div>
          </div>
        </div>

        {/* Settings sections */}
        <SectionLabel2>ACCOUNT</SectionLabel2>
        <Row icon="✦" label="Edit Profile" sub="Name, photo" onClick={()=>goPage("profile")}/>
        <Row icon="◐" label="Status" sub={currentStatus?.label} onClick={()=>goPage("status")}/>

        <SectionLabel2>PREFERENCES</SectionLabel2>
        {/* Performance Mode toggle — visible directly in main menu */}
        <div style={{display:"flex",alignItems:"center",gap:14,padding:"13px 18px",borderBottom:"1px solid rgba(255,255,255,0.04)",background:perfMode?"rgba(201,168,76,0.03)":"none"}}>
          <div style={{width:32,height:32,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(201,168,76,0.07)",border:"1px solid rgba(201,168,76,0.12)",fontSize:15,color:C.gold,flexShrink:0}}>⚡</div>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>Performance Mode</div>
              {perfMode&&<div style={{fontSize:8,letterSpacing:2,color:C.gold,fontFamily:"Jost,sans-serif",background:"rgba(201,168,76,0.1)",border:"1px solid rgba(201,168,76,0.2)",borderRadius:6,padding:"2px 6px"}}>ON</div>}
            </div>
            <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2}}>Disable heavy effects for smoother experience</div>
          </div>
          <SettingsToggle on={perfMode} onToggle={()=>{
            const next=!perfMode;
            setPerfMode(next);
            localStorage.setItem("lucent_perf_mode", next?"1":"0");
            SFX.tap();
            setTimeout(()=>window.location.reload(), 600);
          }}/>
        </div>
        <Row icon="◈" label="Appearance" sub="Theme, display density" onClick={()=>goPage("appearance")}/>
        <Row icon="◎" label="Notifications" sub={`${[notifMessages,notifEvents,notifHabits].filter(Boolean).length} active`} onClick={()=>goPage("notifications")}/>

        <SectionLabel2>APP</SectionLabel2>
        <Row icon="◇" label="Version" sub="Lucent v1.0 · Private build"/>
        <div style={{height:8}}/>
        <Row icon="↗" label="Sign Out" danger onClick={()=>{SFX.tap();onSignOut();closeMenu();}}/>
        <div style={{height:4}}/>
      </>
    );
  };

  return (
    <>
      <div style={{position:"sticky",top:0,zIndex:100,background:"rgba(5,5,5,0.95)",backdropFilter:isLowEnd?"none":"blur(30px)",WebkitBackdropFilter:isLowEnd?"none":"blur(30px)",borderBottom:"1px solid rgba(255,255,255,0.04)",padding:"14px 20px 12px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{fontSize:tab==="home"?22:17,fontWeight:300,letterSpacing:tab==="home"?8:4,fontFamily:"'Cormorant Garamond',Georgia,serif",color:C.text,lineHeight:1}}>{titles[tab]||"Lucent"}</div>
          {subtitles[tab]&&<div style={{fontSize:9,letterSpacing:3,color:tab==="home"?C.gold:C.textDim,fontFamily:"Jost,sans-serif",marginTop:4,fontWeight:300}}>{subtitles[tab]}</div>}
        </div>
        <RippleBtn onClick={openMenu} style={{borderRadius:"50%",padding:2}}>
          <div style={{position:"relative"}}>
            <Avatar name={user?.displayName||user?.email||"U"} size={36} gold photo={user?.photoURL} ring/>
            <div style={{position:"absolute",bottom:1,right:1}}><StatusDot status={userStatus}/></div>
          </div>
        </RippleBtn>
      </div>

      {menu&&(
        <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(0,0,0,0.4)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)"}} onClick={closeMenu}>
          <div style={{position:"absolute",top:66,right:16,width:280,maxHeight:"80vh",overflowY:"auto",animation:"scaleIn 0.22s cubic-bezier(0.16,1,0.3,1) both",transformOrigin:"top right"}} onClick={e=>e.stopPropagation()} className="no-scroll">
            <Card style={{padding:0,overflow:"hidden",border:"1px solid rgba(201,168,76,0.12)"}}>
              {renderPage()}
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// TAB COMPONENTS
// ============================================================
function HomeTab({ user, setTab }) {
  const [members, setMembers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [visible, setVisible] = useState(false);
  const [time, setTime] = useState(new Date());

  useEffect(()=>{ setTimeout(()=>setVisible(true),80); },[]);
  useEffect(()=>{ return onSnapshot(collection(db,"users"),snap=>setMembers(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);
  useEffect(()=>{ const q=query(collection(db,"events"),orderBy("createdAt","desc")); return onSnapshot(q,snap=>setUpcomingEvents(snap.docs.slice(0,2).map(d=>({id:d.id,...d.data()})))); },[]);
  useEffect(()=>{ const t=setInterval(()=>setTime(new Date()),30000); return ()=>clearInterval(t); },[]);

  const hour=time.getHours();
  const greeting=hour<5?"Still up, ":hour<12?"Good morning, ":hour<17?"Good afternoon, ":hour<21?"Good evening, ":"Good night, ";
  const displayName=user?.displayName||user?.email?.split("@")[0]||"Friend";
  const firstName=displayName.split(" ")[0];

  const timeStr = time.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
  const dateStr = time.toLocaleDateString([],{weekday:"long",month:"long",day:"numeric"});

  return (
    <div style={{padding:"0 20px 110px",opacity:visible?1:0,transition:"opacity 0.5s ease"}}>

      {/* Hero greeting card */}
      <div style={{marginBottom:24,animation:"fadeUp 0.5s ease both"}}>
        <Card gold glow style={{padding:"22px 22px 20px",position:"relative",overflow:"hidden"}}>
          {/* Background decorative element */}
          <div style={{position:"absolute",top:-20,right:-20,width:120,height:120,background:`radial-gradient(circle,rgba(201,168,76,0.12),transparent 70%)`,borderRadius:"50%",pointerEvents:"none"}}/>
          <div style={{position:"absolute",bottom:-30,right:20,fontSize:80,opacity:0.04,fontFamily:"'Cormorant Garamond',serif",color:C.gold,lineHeight:1,pointerEvents:"none",userSelect:"none"}}>L</div>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:10,letterSpacing:3,color:C.gold,fontFamily:"Jost,sans-serif",fontWeight:300,marginBottom:8,animation:isLowEnd?undefined:"glowPulse 4s infinite"}}>{greeting.toUpperCase()}</div>
              <div style={{fontSize:32,fontWeight:300,lineHeight:1.1,fontFamily:"'Cormorant Garamond',Georgia,serif",color:C.text}}>{firstName}</div>
              <div style={{fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:6,letterSpacing:0.5}}>{dateStr}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:28,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif",color:C.gold,letterSpacing:2,animation:"glowPulse 5s infinite"}}>{timeStr}</div>
              <div style={{display:"flex",alignItems:"center",gap:6,justifyContent:"flex-end",marginTop:6}}>
                <StatusDot status="online"/>
                <div style={{fontSize:9,color:C.online,fontFamily:"Jost,sans-serif",letterSpacing:2}}>ONLINE</div>
              </div>
            </div>
          </div>

          <GoldDivider margin="16px 0 14px"/>

          {/* Mini stats row */}
          <div style={{display:"flex",gap:0}}>
            {[
              {v:members.length,l:"Members"},
              {v:upcomingEvents.length,l:"Events"},
            ].map((s,i)=>(
              <div key={i} style={{flex:1,textAlign:"center",borderRight:i<1?`1px solid rgba(201,168,76,0.15)`:undefined,padding:"0 16px"}}>
                <div style={{fontSize:22,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:C.gold}}>{s.v}</div>
                <div style={{fontSize:9,letterSpacing:2,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:2}}>{s.l.toUpperCase()}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Circle members */}
      <div style={{marginBottom:24}}>
        <SectionLabel action="See All" onAction={()=>setTab("chat")}>THE CIRCLE</SectionLabel>
        {members.length===0 ? (
          <div style={{fontSize:12,color:C.textDim,fontFamily:"Jost,sans-serif",padding:"12px 0"}}>No members yet.</div>
        ) : (
          <div className="no-scroll" style={{display:"flex",gap:12,overflowX:"auto",paddingBottom:4,paddingTop:2}}>
            {members.map((m,i)=>(
              <RippleBtn key={m.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:7,flexShrink:0,padding:"8px 6px",borderRadius:16,animation:`cardReveal 0.4s ${i*0.06}s both`}} onClick={()=>setTab("chat")}>
                <div style={{position:"relative"}}>
                  <Avatar name={m.name||"?"} size={50} photo={m.photo}/>
                  <div style={{position:"absolute",bottom:2,right:2}}>
                    <StatusDot status={m.id===user?.uid?"online":m.status||"offline"}/>
                  </div>
                </div>
                <div style={{fontSize:10,color:C.textMid,fontFamily:"Jost,sans-serif",whiteSpace:"nowrap",letterSpacing:0.5}}>{(m.name||"?").split(" ")[0]}</div>
              </RippleBtn>
            ))}
          </div>
        )}
      </div>

      {/* Quick actions grid — enhanced */}
      <div style={{marginBottom:24}}>
        <SectionLabel>QUICK ACCESS</SectionLabel>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {label:"Events",tab:"events",icon:"◇",sub:"Plan together"},
            {label:"Ledger",tab:"expenses",icon:"◎",sub:"Split costs"},
            {label:"Archives",tab:"notes",icon:"◫",sub:"Shared notes"},
            {label:"Discipline",tab:"habits",icon:"◑",sub:"Build streaks"},
          ].map((s,i)=>(
            <Card key={s.tab} onClick={()=>setTab(s.tab)} style={{padding:"18px 16px",animation:isLowEnd?undefined:`cardReveal 0.4s ${0.15+i*0.07}s both`}}>
              <div style={{fontSize:28,color:C.gold,marginBottom:10,animation:"glowPulse 3s infinite",filter:`drop-shadow(0 0 8px rgba(201,168,76,0.3))`}}>{s.icon}</div>
              <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",letterSpacing:1,fontWeight:500,marginBottom:3}}>{s.label}</div>
              <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:0.5}}>{s.sub}</div>
            </Card>
          ))}
        </div>
      </div>

      {/* Upcoming events */}
      {upcomingEvents.length>0&&(
        <div style={{marginBottom:24,animation:"cardReveal 0.4s 0.35s both"}}>
          <SectionLabel action="All Events" onAction={()=>setTab("events")}>UPCOMING</SectionLabel>
          {upcomingEvents.map((ev,i)=>(
            <Card key={ev.id} gold onClick={()=>setTab("events")} style={{marginBottom:10,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:0,right:0,width:60,height:60,background:`radial-gradient(circle at top right,rgba(201,168,76,0.15),transparent 70%)`,pointerEvents:"none"}}/>
              <div style={{display:"flex",gap:14,alignItems:"center"}}>
                <div style={{width:44,height:44,borderRadius:12,background:`linear-gradient(135deg,${C.goldDim}22,${C.gold}11)`,border:`1px solid rgba(201,168,76,0.2)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:C.gold,flexShrink:0}}>◇</div>
                <div style={{flex:1}}>
                  {ev.date&&<div style={{fontSize:9,color:C.gold,letterSpacing:2.5,fontFamily:"Jost,sans-serif",marginBottom:4}}>{ev.date}{ev.time?` · ${ev.time}`:""}</div>}
                  <div style={{fontSize:17,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif"}}>{ev.title}</div>
                  {ev.location&&<div style={{fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:3}}>📍 {ev.location}</div>}
                </div>
                <div style={{fontSize:16,color:C.textDim}}>›</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* More tools */}
      <div style={{animation:"cardReveal 0.4s 0.45s both"}}>
        <SectionLabel>MORE TOOLS</SectionLabel>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {NAV_MORE.map((n,i)=>(
            <RippleBtn key={n.id} onClick={()=>setTab(n.id)} style={{
              background:"rgba(255,255,255,0.02)",
              border:"1px solid rgba(255,255,255,0.05)",
              borderRadius:16,padding:"16px",
              display:"flex",alignItems:"center",gap:12,
              transition:"all 0.2s",
              animation:`cardReveal 0.4s ${0.5+i*0.07}s both`,
            }}>
              <div style={{width:36,height:36,borderRadius:10,background:`linear-gradient(135deg,rgba(201,168,76,0.08),rgba(201,168,76,0.03))`,border:"1px solid rgba(201,168,76,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:C.gold}}>{n.icon}</div>
              <div>
                <div style={{fontSize:12,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:400}}>{n.label}</div>
                <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1,marginTop:2}}>TAP TO OPEN</div>
              </div>
            </RippleBtn>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FEED TAB — ELEVATED
// ============================================================
function FeedTab({ user }) {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [composing, setComposing] = useState(false);

  useEffect(()=>{ const q=query(collection(db,"posts"),orderBy("createdAt","desc")); return onSnapshot(q,snap=>setPosts(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);

  const post = async () => {
    if(!text.trim()) return;
    setPosting(true);
    try {
      await addDoc(collection(db,"posts"),{text:text.trim(),authorId:user.uid,authorName:user.displayName||user.email,authorPhoto:user.photoURL||null,likes:[],createdAt:serverTimestamp()});
      SFX.confirm(); haptic([10,20,10]);
      toast("Posted to the circle");
      // Notify everyone in the circle
      const name = user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "Someone";
      const preview = text.trim().length > 60 ? text.trim().slice(0, 60) + "..." : text.trim();
      sendInstantNotification(`✨ ${name} posted in the circle`, preview);
      setText(""); setComposing(false);
    } catch(e){ toast("Failed to post. Try again.","error"); }
    setPosting(false);
  };

  const deletePost = async (p) => {
    if(p.authorId!==user.uid) return;
    try { await deleteDoc(doc(db,"posts",p.id)); toast("Post deleted"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };

  const toggleLike = async (p) => {
    SFX.like(); haptic([6]);
    const likes=p.likes||[];
    const alreadyLiked = likes.includes(user.uid);
    try {
      await updateDoc(doc(db,"posts",p.id),{likes:alreadyLiked?likes.filter(id=>id!==user.uid):[...likes,user.uid]});
      // Only notify when liking (not unliking), and not your own post
      if(!alreadyLiked && p.authorId !== user.uid){
        const name = user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "Someone";
        const preview = p.text?.length > 40 ? p.text.slice(0,40)+"..." : p.text;
        sendInstantNotification(`❤️ ${name} liked your post`, `"${preview}"`);
      }
    } catch(e){}
  };


  const timeAgo = ts => {
    if(!ts?.seconds) return "just now";
    const diff=Date.now()-ts.seconds*1000;
    if(diff<60000) return "just now";
    if(diff<3600000) return `${Math.floor(diff/60000)}m ago`;
    if(diff<86400000) return `${Math.floor(diff/3600000)}h ago`;
    return `${Math.floor(diff/86400000)}d ago`;
  };

  return (
    <div style={{padding:"0 20px 110px"}}>
      {/* Compose area */}
      <Card style={{marginBottom:20,padding:composing?"18px":"14px 18px",transition:"padding 0.3s ease"}}>
        {composing ? (
          <>
            <div style={{display:"flex",gap:12,marginBottom:14,alignItems:"flex-start"}}>
              <Avatar name={user?.displayName||"?"} size={34} photo={user?.photoURL}/>
              <div style={{flex:1}}>
                <div style={{fontSize:11,color:C.gold,fontFamily:"Jost,sans-serif",marginBottom:8,letterSpacing:1}}>{user?.displayName?.split(" ")[0]||"You"}</div>
                <textarea value={text} onChange={e=>setText(e.target.value)}
                  placeholder="Share something with the circle..."
                  autoFocus
                  style={{width:"100%",background:"none",border:"none",outline:"none",color:C.text,fontSize:15,fontFamily:"'Cormorant Garamond',Georgia,serif",resize:"none",minHeight:80,lineHeight:1.7,letterSpacing:0.3}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.05)"}}>
              <GoldBtn onClick={()=>{setComposing(false);setText("");}} outline small style={{width:"auto",padding:"9px 18px"}}>CANCEL</GoldBtn>
              <GoldBtn onClick={post} disabled={posting} small style={{width:"auto",padding:"9px 22px"}}>{posting?<LoadingDots/>:"POST"}</GoldBtn>
            </div>
          </>
        ) : (
          <RippleBtn onClick={()=>setComposing(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:12}}>
            <Avatar name={user?.displayName||"?"} size={34} photo={user?.photoURL}/>
            <div style={{color:C.textDim,fontSize:14,fontFamily:"'Cormorant Garamond',Georgia,serif",flex:1,textAlign:"left"}}>
              What's on your mind, {firstName(user?.displayName||"friend")}?
            </div>
          </RippleBtn>
        )}
      </Card>

      {posts.length===0&&<EmptyState icon="◉" text="Nothing posted yet." sub="Be the first to share something."/>}

      {posts.map((p,i)=>{
        const liked=(p.likes||[]).includes(user.uid);
        const likeCount=(p.likes||[]).length;
        return (
          <Card key={p.id} style={{marginBottom:14,animation:`cardReveal 0.35s ${i*0.06}s both`}}>
            {/* Author row */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
              <Avatar name={p.authorName||"?"} size={40} photo={p.authorPhoto}/>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:500,color:C.text,letterSpacing:0.3}}>{p.authorName}</div>
                <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:0.5,marginTop:2}}>{timeAgo(p.createdAt)}</div>
              </div>
              <div style={{width:6,height:6,borderRadius:"50%",background:C.goldDim,opacity:0.4}}/>
            </div>

            {/* Content */}
            <div style={{fontSize:17,color:C.textMid,lineHeight:1.7,fontFamily:"'Cormorant Garamond',Georgia,serif",marginBottom:16,letterSpacing:0.3,paddingLeft:52}}>{p.text}</div>

            {/* Action bar */}
            <div style={{display:"flex",alignItems:"center",gap:6,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.04)",paddingLeft:52}}>
              <RippleBtn onClick={()=>toggleLike(p)} style={{display:"flex",alignItems:"center",gap:7,padding:"6px 12px",borderRadius:20,background:liked?"rgba(201,168,76,0.08)":"transparent",border:`1px solid ${liked?"rgba(201,168,76,0.2)":"transparent"}`,transition:"all 0.2s"}}>
                <span style={{
                  color:liked?C.gold:C.textDim,fontSize:15,
                  transition:"all 0.2s",
                  display:"inline-block",
                  animation:liked?"heartBeat 0.4s ease":"none",
                  filter:liked?`drop-shadow(0 0 4px ${C.gold})`:"none",
                }}>{liked?"♦":"◇"}</span>
                <span style={{fontSize:12,color:liked?C.gold:C.textDim,fontFamily:"Jost,sans-serif",transition:"color 0.2s",fontWeight:liked?500:300}}>
                  {likeCount>0?likeCount:"Like"}
                </span>
              </RippleBtn>
              {p.authorId===user.uid&&(
                <button onClick={()=>deletePost(p)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:10,fontFamily:"Jost,sans-serif",opacity:0.5,letterSpacing:1,padding:"4px 0"}}>✕ DELETE</button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function firstName(name) { return name.split(" ")[0]; }

// ============================================================
// EVENTS TAB — ELEVATED
// ============================================================
const fmtDate = (d) => {
  if (!d) return "";
  // YYYY-MM-DD → "Mon Jun 1"
  const parts = d.split("-");
  if (parts.length === 3) {
    const dt = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
    return dt.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
  }
  return d;
};
const fmtTime = (t) => {
  if (!t) return "";
  // HH:MM → "2:30 PM"
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
};

function EventsTab({ user }) {
  const [events, setEvents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({title:"",date:"",time:"",location:""});
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(()=>{ const q=query(collection(db,"events"),orderBy("createdAt","desc")); return onSnapshot(q,snap=>setEvents(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);

  const create = async () => {
    if(!form.title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"events"),{...form,rsvps:[user.uid],createdBy:user.uid,createdAt:serverTimestamp()});
      SFX.confirm(); haptic([10,20,10]);
      toast("Event created!");
      setForm({title:"",date:"",time:"",location:""}); setCreating(false);
    } catch(e){ toast("Failed to create event","error"); }
    setSaving(false);
  };

  const deleteEvent = async (ev) => {
    if(ev.createdBy!==user.uid) return;
    try { await deleteDoc(doc(db,"events",ev.id)); toast("Event removed"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };

  const toggleRsvp = async (ev) => {
    SFX.tap(); haptic([8]);
    const rsvps=ev.rsvps||[];
    const going=rsvps.includes(user.uid);
    try {
      await updateDoc(doc(db,"events",ev.id),{rsvps:going?rsvps.filter(id=>id!==user.uid):[...rsvps,user.uid]});
      if(!going) toast("You're going! 🎉");
    } catch(e){ toast("Failed to update RSVP","error"); }
  };

  const displayed = events.filter(e => {
    if (filter === "archived") return e.archived === true;
    if (filter === "going")    return !e.archived && (e.rsvps||[]).includes(user.uid);
    return !e.archived;
  });

  return (
    <div style={{padding:"0 20px 110px"}}>
      {/* Filter pills */}
      <div className="no-scroll" style={{display:"flex",gap:8,marginBottom:18,overflowX:"auto",paddingBottom:2}}>
        {["all","going","archived"].map(f=>(
          <Pill key={f} active={filter===f} onClick={()=>setFilter(f)}>{f==="all"?"ALL EVENTS":f==="going"?"GOING":"ARCHIVED"}</Pill>
        ))}
      </div>

      {/* Create button */}
      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{
        width:"100%",
        border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,
        borderRadius:16,padding:"14px 16px",marginBottom:18,
        display:"flex",alignItems:"center",justifyContent:"center",gap:10,
        background:creating?"rgba(201,168,76,0.04)":"transparent",
        transition:"all 0.25s",
      }}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"◇"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"CREATE EVENT"}</span>
      </RippleBtn>

      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="EVENT NAME" value={form.title} onChange={v=>setForm(f=>({...f,title:v}))} placeholder="What's the occasion?"/>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>DATE</div>
            <input type="date" value={form.date}
              onChange={e=>{setForm(f=>({...f,date:e.target.value}));SFX.tap();}}
              style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"12px 14px",color:form.date?C.gold:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",colorScheme:"dark",boxSizing:"border-box",transition:"border-color 0.2s, color 0.2s"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>TIME</div>
            <input type="time" value={form.time}
              onChange={e=>{setForm(f=>({...f,time:e.target.value}));SFX.tap();}}
              style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"12px 14px",color:form.time?C.gold:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",colorScheme:"dark",boxSizing:"border-box",transition:"border-color 0.2s, color 0.2s"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
          </div>
          <InputField label="LOCATION" value={form.location} onChange={v=>setForm(f=>({...f,location:v}))} placeholder="Where?"/>
          <GoldBtn onClick={create} disabled={saving}>CREATE EVENT</GoldBtn>
        </Card>
      )}

      {displayed.length===0&&!creating&&<EmptyState icon="◇" text="No events yet." sub="Create one and invite your circle."/>}

      {displayed.map((ev,i)=>{
        const going=(ev.rsvps||[]).includes(user.uid);
        const count=(ev.rsvps||[]).length;
        return (
          <Card key={ev.id} gold={going} style={{marginBottom:14,animation:`cardReveal 0.35s ${i*0.08}s both`}}>
            {/* Date badge */}
            {ev.date&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:`linear-gradient(135deg,rgba(201,168,76,0.1),rgba(201,168,76,0.05))`,border:"1px solid rgba(201,168,76,0.2)",borderRadius:8,padding:"4px 10px",marginBottom:12}}>
                <span style={{fontSize:8,color:C.gold,letterSpacing:2.5,fontFamily:"Jost,sans-serif"}}>{fmtDate(ev.date)}{ev.time?` · ${fmtTime(ev.time)}`:""}</span>
              </div>
            )}
            <div style={{fontSize:20,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif",marginBottom:8,lineHeight:1.2}}>{ev.title}</div>
            {ev.location&&<div style={{fontSize:12,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:14,display:"flex",alignItems:"center",gap:6}}><span>📍</span>{ev.location}</div>}

            {/* Footer */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:"1px solid rgba(255,255,255,0.05)",paddingTop:12}}>
              {/* Attending avatars placeholder */}
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{display:"flex"}}>
                  {[...Array(Math.min(3,count))].map((_,j)=>(
                    <div key={j} style={{width:22,height:22,borderRadius:"50%",background:`linear-gradient(135deg,${C.s4},${C.s5})`,border:`1.5px solid ${C.surface}`,marginLeft:j>0?-7:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:C.textDim,fontFamily:"Jost,sans-serif"}}>
                      {j+1}
                    </div>
                  ))}
                </div>
                <div style={{fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{count} attending</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                {ev.createdBy===user.uid&&(
                  <button onClick={()=>deleteEvent(ev)} style={{background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:10,fontFamily:"Jost,sans-serif",opacity:0.55,padding:"4px",letterSpacing:1}}>✕</button>
                )}
                <RippleBtn onClick={()=>toggleRsvp(ev)} style={{
                  background:going?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.04)",
                  border:`1px solid ${going?C.goldDim:"rgba(255,255,255,0.08)"}`,
                  borderRadius:10,padding:"7px 16px",
                  color:going?C.black:C.textDim,
                  fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:going?600:400,
                  transition:"all 0.25s",
                  boxShadow:going?`0 2px 12px rgba(201,168,76,0.2)`:"none",
                }}>
                  {going?"✓ GOING":"RSVP"}
                </RippleBtn>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
// EXPENSES TAB — ELEVATED
// ============================================================
function ExpensesTab({ user }) {
  const [expenses, setExpenses] = useState([]);
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({title:"",amount:"",split:"2"});
  const [splitMode, setSplitMode] = useState("circle"); // "circle" | "custom"
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ const q=query(collection(db,"expenses"),orderBy("createdAt","desc")); return onSnapshot(q,snap=>setExpenses(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);

  // 🤖 Fetch all circle members for auto-split
  useEffect(()=>{ return onSnapshot(collection(db,"users"),snap=>setMembers(snap.docs.map(d=>({uid:d.id,...d.data()})))); },[]);

  const create = async () => {
    if(!form.title.trim()||!form.amount) return;
    const amount = parseFloat(form.amount);
    if(isNaN(amount) || amount <= 0){ toast("Enter a valid amount","error"); return; }
    setSaving(true);
    try {
      let splitCount, splits;

      if (splitMode === "circle" && members.length > 0) {
        // 🤖 Auto-split: divide evenly among all current members
        splitCount = members.length;
        const share = parseFloat((amount / splitCount).toFixed(2));
        splits = {};
        members.forEach(m => { splits[m.uid] = share; });
      } else {
        // Manual split
        splitCount = parseInt(form.split) || 2;
        splits = null;
      }

      await addDoc(collection(db,"expenses"),{
        title: form.title.trim(),
        amount,
        split: splitCount,
        splits: splits || null,
        paidBy: user.uid,
        paidByName: user.displayName||user.email,
        settled: [],
        createdAt: serverTimestamp(),
      });
      SFX.confirm(); haptic([10,20,10]);
      toast(`Expense split ${splitMode==="circle"?"across circle ("+splitCount+" people)":"evenly"} ✦`);
      setForm({title:"",amount:"",split:"2"}); setCreating(false);
    } catch(e){ toast("Failed to add expense","error"); }
    setSaving(false);
  };

  const settle = async (ex) => {
    SFX.confirm(); haptic([10,30,10]);
    const settled=ex.settled||[];
    try {
      await updateDoc(doc(db,"expenses",ex.id),{settled:settled.includes(user.uid)?settled.filter(id=>id!==user.uid):[...settled,user.uid]});
      toast("Marked as settled ✓");
    } catch(e){ toast("Failed to update","error"); }
  };

  const deleteExpense = async (ex) => {
    if(ex.paidBy!==user.uid) return;
    try { await deleteDoc(doc(db,"expenses",ex.id)); toast("Expense removed"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };

  // My share: use splits map if available, else fall back to equal division
  const getMyShare = (ex) => {
    if (ex.splits && ex.splits[user.uid] != null) return ex.splits[user.uid];
    return ex.split > 0 ? ex.amount / ex.split : ex.amount;
  };

  const myUnsettled=expenses.filter(e=>e.paidBy!==user.uid&&!(e.settled||[]).includes(user.uid));
  const totalOwed=myUnsettled.reduce((s,e)=>s+getMyShare(e),0);
  const totalYouPaid=expenses.filter(e=>e.paidBy===user.uid).reduce((s,e)=>s+e.amount,0);

  // Preview share for the creation form
  const previewAmount = parseFloat(form.amount)||0;
  const previewCount  = splitMode==="circle" ? (members.length||1) : (parseInt(form.split)||2);
  const previewShare  = previewAmount > 0 ? (previewAmount / previewCount).toFixed(2) : null;

  return (
    <div style={{padding:"0 20px 110px"}}>
      {/* Balance hero */}
      <Card gold glow style={{marginBottom:20,animation:"cardReveal 0.4s ease both",padding:"22px 22px 20px",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-30,right:-30,width:100,height:100,background:`radial-gradient(circle,rgba(201,168,76,0.15),transparent 70%)`}}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.goldDim,fontFamily:"Jost,sans-serif",marginBottom:10}}>YOU OWE</div>
            <div style={{fontSize:48,fontWeight:300,color:totalOwed>0?C.gold:C.online,fontFamily:"'Cormorant Garamond',Georgia,serif",letterSpacing:-1,lineHeight:1,animation:"glowPulse 4s infinite"}}>${totalOwed.toFixed(2)}</div>
            <div style={{fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:6}}>{myUnsettled.length} pending · {expenses.length} total</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:9,letterSpacing:2,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:6}}>YOU PAID</div>
            <div style={{fontSize:22,fontWeight:300,color:C.textMid,fontFamily:"'Cormorant Garamond',serif"}}>${totalYouPaid.toFixed(2)}</div>
          </div>
        </div>
        {totalOwed===0&&myUnsettled.length===0&&expenses.length>0&&(
          <div style={{marginTop:14,display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"rgba(76,175,80,0.08)",borderRadius:10,border:"1px solid rgba(76,175,80,0.15)"}}>
            <span style={{color:C.online,fontSize:14}}>✓</span>
            <span style={{fontSize:11,color:C.online,fontFamily:"Jost,sans-serif",letterSpacing:1}}>ALL SETTLED</span>
          </div>
        )}
      </Card>

      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{width:"100%",border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,borderRadius:16,padding:"14px 16px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:creating?"rgba(201,168,76,0.04)":"transparent",transition:"all 0.25s"}}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"◎"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"ADD EXPENSE"}</span>
      </RippleBtn>

      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="WHAT FOR" value={form.title} onChange={v=>setForm(f=>({...f,title:v}))} placeholder="e.g. Group dinner"/>
          <InputField label="TOTAL ($)" value={form.amount} onChange={v=>setForm(f=>({...f,amount:v}))} type="number" placeholder="0.00"/>

          {/* 🤖 Split mode toggle */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:10}}>SPLIT MODE</div>
            <div style={{display:"flex",gap:8}}>
              {[["circle","◉ CIRCLE","Split among all members"],["custom","◎ CUSTOM","Enter number manually"]].map(([val,label,hint])=>(
                <button key={val} onClick={()=>{setSplitMode(val);SFX.tap();}} style={{
                  flex:1,padding:"10px 8px",borderRadius:12,
                  background:splitMode===val?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.03)",
                  border:`1px solid ${splitMode===val?C.gold:"rgba(255,255,255,0.07)"}`,
                  color:splitMode===val?C.black:C.textDim,
                  fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:1.5,cursor:"pointer",
                  transition:"all 0.2s",fontWeight:splitMode===val?600:300,
                }}>
                  <div>{label}</div>
                  <div style={{fontSize:8,marginTop:3,opacity:0.7,letterSpacing:0.5}}>{hint}</div>
                </button>
              ))}
            </div>
          </div>

          {splitMode==="custom"&&(
            <InputField label="WAYS" value={form.split} onChange={v=>setForm(f=>({...f,split:v}))} type="number" placeholder="2"/>
          )}

          {previewShare&&(
            <div style={{fontSize:11,color:C.gold,fontFamily:"Jost,sans-serif",marginBottom:14,letterSpacing:1,textAlign:"center",padding:"10px",background:"rgba(201,168,76,0.06)",borderRadius:10,border:"1px solid rgba(201,168,76,0.12)"}}>
              {splitMode==="circle"
                ? <>Each of <strong>{previewCount}</strong> members pays <strong>${previewShare}</strong></>
                : <>Each person pays: <strong>${previewShare}</strong></>
              }
            </div>
          )}
          <GoldBtn onClick={create} disabled={saving}>ADD EXPENSE</GoldBtn>
        </Card>
      )}

      {expenses.length===0&&!creating&&<EmptyState icon="◎" text="No expenses yet." sub="Add one to start splitting costs."/>}

      {expenses.map((ex,i)=>{
        const isMyDebt=ex.paidBy!==user.uid;
        const settled=(ex.settled||[]).includes(user.uid);
        const share=getMyShare(ex).toFixed(2);
        const autoSplit=!!ex.splits;
        return (
          <Card key={ex.id} style={{marginBottom:12,opacity:settled?0.5:1,animation:`cardReveal 0.35s ${i*0.07}s both`,transition:"opacity 0.4s ease"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:18,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif",marginBottom:5}}>{ex.title}</div>
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:0.5}}>
                    Paid by <span style={{color:ex.paidBy===user.uid?C.gold:C.textMid}}>{ex.paidBy===user.uid?"you":ex.paidByName}</span>
                  </div>
                  <div style={{width:3,height:3,borderRadius:"50%",background:C.s5}}/>
                  <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{ex.split} ways</div>
                  {autoSplit&&<div style={{fontSize:8,color:C.gold,fontFamily:"Jost,sans-serif",letterSpacing:1.5,background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.15)",borderRadius:5,padding:"1px 6px"}}>AUTO</div>}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:26,color:isMyDebt&&!settled?C.gold:C.textDim,fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:300,lineHeight:1}}>${share}</div>
                <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:3,letterSpacing:1}}>YOUR SHARE</div>
                <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:1}}>of ${ex.amount.toFixed(2)}</div>
              </div>
            </div>
            {settled ? (
              <div style={{marginTop:12,display:"flex",alignItems:"center",gap:7,color:C.online,fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:1.5}}>
                <span>✓</span> SETTLED
              </div>
            ) : isMyDebt ? (
              <RippleBtn onClick={()=>settle(ex)} style={{marginTop:14,width:"100%",border:`1px solid rgba(201,168,76,0.25)`,borderRadius:12,padding:"10px",color:C.gold,fontSize:11,fontFamily:"Jost,sans-serif",letterSpacing:2,textAlign:"center",background:"rgba(201,168,76,0.04)",transition:"all 0.2s"}}>
                MARK SETTLED
              </RippleBtn>
            ) : null}
            {ex.paidBy===user.uid&&(
              <button onClick={()=>deleteExpense(ex)} style={{marginTop:10,background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:10,fontFamily:"Jost,sans-serif",opacity:0.55,letterSpacing:1,display:"block"}}>✕ REMOVE</button>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
// NOTES TAB — ELEVATED
// ============================================================
function NotesTab({ user }) {
  const [notes, setNotes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({title:"",subject:"",content:""});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("active");

  useEffect(()=>{ const q=query(collection(db,"notes"),orderBy("createdAt","desc")); return onSnapshot(q,snap=>setNotes(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);

  const displayedNotes = notes.filter(n => filter === "archived" ? n.archived === true : !n.archived);

  const create = async () => {
    if(!form.title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"notes"),{...form,authorId:user.uid,authorName:user.displayName||user.email,createdAt:serverTimestamp()});
      SFX.confirm(); haptic([10,20,10]);
      toast("Note saved to archives");
      setForm({title:"",subject:"",content:""}); setCreating(false);
    } catch(e){ toast("Failed to save note","error"); }
    setSaving(false);
  };

  const deleteNote = async (n) => {
    if(n.authorId!==user.uid) return;
    try { await deleteDoc(doc(db,"notes",n.id)); toast("Note deleted"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };


  const subjectColors = {
    "default":C.goldDim,
    "MATH":"#7BA7BC","ECON":"#8BA888","CS":"#C9A84C","PHYSICS":"#B07BC9","BIO":"#7BB07A",
  };
  const getSubjectColor = (sub) => sub ? (subjectColors[sub.toUpperCase()] || C.goldDim) : C.goldDim;

  return (
    <div style={{padding:"0 20px 110px"}}>
      {/* Filter pills */}
      <div className="no-scroll" style={{display:"flex",gap:8,marginBottom:18,overflowX:"auto",paddingBottom:2}}>
        {["active","archived"].map(f=>(
          <Pill key={f} active={filter===f} onClick={()=>setFilter(f)}>{f==="active"?"ACTIVE":"ARCHIVED"}</Pill>
        ))}
      </div>

      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{width:"100%",border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,borderRadius:16,padding:"14px 16px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:creating?"rgba(201,168,76,0.04)":"transparent",transition:"all 0.25s"}}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"◫"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"NEW ARCHIVE"}</span>
      </RippleBtn>

      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="TITLE" value={form.title} onChange={v=>setForm(f=>({...f,title:v}))} placeholder="Note title"/>
          <InputField label="SUBJECT / TAG" value={form.subject} onChange={v=>setForm(f=>({...f,subject:v}))} placeholder="e.g. ECON 301"/>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>CONTENT</div>
            <textarea value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))}
              placeholder="Write your notes..."
              style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"14px 16px",color:C.text,fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:300,resize:"none",minHeight:120,outline:"none",lineHeight:1.7,transition:"border-color 0.2s"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
          </div>
          <GoldBtn onClick={create} disabled={saving}>SAVE TO ARCHIVES</GoldBtn>
        </Card>
      )}

      {displayedNotes.length===0&&!creating&&(
        <EmptyState icon="◫" text={filter==="archived"?"No archived notes.":"Archives empty."} sub={filter==="archived"?"Notes older than 30 days appear here.":"Save your first note."}/>
      )}

      {displayedNotes.map((n,i)=>{
        const isOpen=expanded===n.id;
        const subColor=getSubjectColor(n.subject);
        return (
          <Card key={n.id} style={{marginBottom:12,animation:`cardReveal 0.35s ${i*0.08}s both`,cursor:"pointer"}} onClick={()=>{setExpanded(isOpen?null:n.id);SFX.tap();}}>
            <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
              {/* Subject color bar */}
              <div style={{width:3,borderRadius:3,alignSelf:"stretch",background:`linear-gradient(180deg,${subColor},${subColor}44)`,flexShrink:0,minHeight:40}}/>
              <div style={{flex:1}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div>
                    {n.subject&&(
                      <div style={{display:"inline-flex",alignItems:"center",background:`${subColor}18`,border:`1px solid ${subColor}40`,borderRadius:6,padding:"3px 8px",marginBottom:7}}>
                        <span style={{fontSize:9,color:subColor,letterSpacing:2,fontFamily:"Jost,sans-serif"}}>{n.subject.toUpperCase()}</span>
                      </div>
                    )}
                    <div style={{fontSize:17,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif",lineHeight:1.3}}>{n.title}</div>
                  </div>
                  <div style={{fontSize:14,color:C.textDim,marginLeft:8,transition:"transform 0.2s",transform:isOpen?"rotate(90deg)":"none"}}>›</div>
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                  <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:0.5}}>by {n.authorName}</div>
                  {n.authorId===user.uid&&<button onClick={()=>deleteNote(n)} style={{background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:10,fontFamily:"Jost,sans-serif",opacity:0.55,letterSpacing:1}}>✕</button>}
                </div>
                {isOpen&&n.content&&(
                  <div style={{fontSize:13,color:C.textMid,fontFamily:"Jost,sans-serif",marginTop:12,lineHeight:1.7,fontWeight:300,borderTop:"1px solid rgba(255,255,255,0.05)",paddingTop:12,animation:"fadeIn 0.3s ease both"}}>
                    {n.content}
                  </div>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================
// HABITS TAB — ELEVATED
// ============================================================
function HabitsTab({ user }) {
  const [habits, setHabits] = useState([]);
  const [creating, setCreating] = useState(false);
  const [habitName, setHabitName] = useState("");
  const [habitIcon, setHabitIcon] = useState("◑");
  const [habitFreq, setHabitFreq] = useState("daily");
  const [saving, setSaving] = useState(false);
  // Always compute today fresh (avoids stale date if app stays open past midnight)
  // Always compute today fresh — uses local date, not UTC (fixes midnight bug for UTC+8)
  const getToday = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };

  useEffect(()=>{ const q=query(collection(db,"habits"),where("userId","==",user.uid)); return onSnapshot(q,snap=>setHabits(snap.docs.map(d=>({id:d.id,...d.data()})))); },[user.uid]);

  const create = async () => {
    if(!habitName.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"habits"),{name:habitName.trim(),icon:habitIcon||"◑",frequency:habitFreq,userId:user.uid,completedDays:[],createdAt:serverTimestamp()});
      SFX.confirm(); haptic([10,20,10]);
      setHabitName(""); setCreating(false);
    } catch(e){ toast("Failed to add habit","error"); }
    setSaving(false);
  };

  const toggle = async (h) => {
    const days=h.completedDays||[];
    const today=getToday();
    const isDone=days.includes(today);
    if(!isDone){ SFX.confirm(); haptic([15,10,15]); toast("Habit completed! 🔥");}
    else { SFX.tap(); haptic([5]); }
    try {
      await updateDoc(doc(db,"habits",h.id),{completedDays:isDone?days.filter(d=>d!==today):[...days,today]});
    } catch(e){ toast("Failed to update habit","error"); }
  };

  const streak = (h) => {
    const days=(h.completedDays||[]).sort();
    let s=0; const d=new Date();
    while(true){ const ds=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; if(days.includes(ds)){s++;d.setDate(d.getDate()-1);}else break; }
    return s;
  };

  const deleteHabit = async (h) => {
    if(h.userId !== user.uid) return; // safety: only owner can delete
    try { await deleteDoc(doc(db,"habits",h.id)); toast("Habit removed"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };

  const doneCount=habits.filter(h=>(h.completedDays||[]).includes(getToday())).length;
  const totalCount=habits.length;
  const pct=totalCount>0?Math.round((doneCount/totalCount)*100):0;

  const ICONS=["◑","◈","◉","◇","◎","◫","▣","◷"];

  return (
    <div style={{padding:"0 20px 110px"}}>
      {totalCount>0&&(
        <Card gold glow style={{marginBottom:20,animation:"cardReveal 0.4s ease both",padding:"20px 22px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div>
              <div style={{fontSize:9,letterSpacing:3.5,color:C.goldDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>TODAY'S SCORE</div>
              <div style={{fontSize:52,fontWeight:300,color:pct===100?C.online:C.gold,fontFamily:"'Cormorant Garamond',Georgia,serif",animation:"glowPulse 3s infinite",lineHeight:1}}>{doneCount}<span style={{fontSize:28,color:C.goldDim}}>/{totalCount}</span></div>
            </div>
            {/* Circular progress */}
            <div style={{position:"relative",width:64,height:64}}>
              <svg width="64" height="64" style={{transform:"rotate(-90deg)"}}>
                <circle cx="32" cy="32" r="26" fill="none" stroke={C.s3} strokeWidth="4"/>
                <circle cx="32" cy="32" r="26" fill="none" stroke={pct===100?C.online:C.gold} strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={`${2*Math.PI*26}`}
                  strokeDashoffset={`${2*Math.PI*26*(1-pct/100)}`}
                  style={{transition:"stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)",filter:`drop-shadow(0 0 4px ${pct===100?C.online:C.gold}88)`}}/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:600,fontFamily:"Jost,sans-serif",color:pct===100?C.online:C.gold}}>{pct}%</div>
            </div>
          </div>
          {/* Week view */}
          <div style={{display:"flex",gap:4}}>
            {[6,5,4,3,2,1,0].map(daysAgo=>{
              const d=new Date(); d.setDate(d.getDate()-daysAgo);
              const ds=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
              const isToday=daysAgo===0;
              const doneHabits=habits.filter(h=>(h.completedDays||[]).includes(ds)).length;
              const dayPct=totalCount>0?doneHabits/totalCount:0;
              return (
                <div key={daysAgo} style={{flex:1,textAlign:"center"}}>
                  <div style={{fontSize:8,color:isToday?C.gold:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:5,letterSpacing:0.5}}>
                    {["S","M","T","W","T","F","S"][d.getDay()]}
                  </div>
                  <div style={{height:24,borderRadius:4,background:dayPct>0?`rgba(201,168,76,${0.2+0.8*dayPct})`:C.s3,border:isToday?`1px solid rgba(201,168,76,0.4)`:"none",transition:"background 0.3s"}}/>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{width:"100%",border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,borderRadius:16,padding:"14px 16px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:creating?"rgba(201,168,76,0.04)":"transparent",transition:"all 0.25s"}}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"◑"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"ADD HABIT"}</span>
      </RippleBtn>

      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="HABIT NAME" value={habitName} onChange={setHabitName} placeholder="e.g. Study 2 hours"/>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:10}}>CHOOSE ICON</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {ICONS.map(ic=>(
                <button key={ic} onClick={()=>{setHabitIcon(ic);SFX.tap();}} style={{width:38,height:38,borderRadius:10,background:habitIcon===ic?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.03)",border:`1px solid ${habitIcon===ic?C.gold:"rgba(255,255,255,0.07)"}`,cursor:"pointer",fontSize:16,color:habitIcon===ic?C.black:C.textDim,transition:"all 0.2s"}}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:10}}>FREQUENCY</div>
            <div style={{display:"flex",gap:8}}>
              {["daily","weekly"].map(f=>(
                <button key={f} onClick={()=>{setHabitFreq(f);SFX.tap();}} style={{
                  flex:1,padding:"9px 0",borderRadius:12,
                  background:habitFreq===f?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.03)",
                  border:`1px solid ${habitFreq===f?C.gold:"rgba(255,255,255,0.07)"}`,
                  color:habitFreq===f?C.black:C.textDim,
                  fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:2,cursor:"pointer",
                  transition:"all 0.2s",fontWeight:500,
                }}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <GoldBtn onClick={create} disabled={saving}>ADD HABIT</GoldBtn>
        </Card>
      )}

      {habits.length===0&&!creating&&<EmptyState icon="◑" text="No habits yet." sub="Build discipline, one day at a time."/>}

      {habits.map((h,i)=>{
        const done=(h.completedDays||[]).includes(getToday());
        const s=streak(h);
        const totalDays=(h.completedDays||[]).length;
        return (
          <div key={h.id} style={{marginBottom:10,animation:`cardReveal 0.35s ${i*0.07}s both`}}>
            <Card gold={done} style={{display:"flex",alignItems:"center",gap:14,transition:"all 0.3s",boxShadow:done?`0 4px 28px rgba(201,168,76,0.12),0 1px 0 rgba(255,255,255,0.04) inset`:"none"}}>
              {/* Check circle */}
              <button onClick={()=>toggle(h)} style={{
                width:44,height:44,borderRadius:"50%",
                background:done?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"none",
                border:`2px solid ${done?C.gold:"rgba(255,255,255,0.1)"}`,
                cursor:"pointer",
                color:done?C.black:C.textDim,
                fontSize:16,
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
                flexShrink:0,
                boxShadow:done?`0 2px 14px rgba(201,168,76,0.3)`:"none",
              }}>
                {done?<span style={{animation:"popIn 0.3s ease both",display:"inline-block"}}>✓</span>:<span style={{fontSize:18}}>{h.icon||"◑"}</span>}
              </button>

              <div style={{flex:1}}>
                <div style={{fontSize:16,fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:300,color:done?C.textMid:C.text,textDecoration:done?"line-through":"none",textDecorationColor:C.goldDim}}>{h.name}</div>
                <div style={{display:"flex",gap:10,marginTop:4}}>
                  {s>0&&(
                    <div style={{fontSize:9,color:C.gold,fontFamily:"Jost,sans-serif",letterSpacing:1.5,display:"flex",alignItems:"center",gap:4}}>
                      <span>🔥</span>{s} DAY STREAK
                    </div>
                  )}
                  {totalDays>0&&<div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1}}>{totalDays} total</div>}
                </div>
              </div>

              {/* Actions */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,flexShrink:0}}>
                {done&&(
                  <div style={{width:24,height:24,borderRadius:"50%",background:"rgba(76,175,80,0.15)",border:"1px solid rgba(76,175,80,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:C.online}}>✓</div>
                )}
                <button onClick={()=>deleteHabit(h)} style={{background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:11,opacity:0.4,padding:"2px",lineHeight:1}}>✕</button>
              </div>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// SCHEDULE TAB — ELEVATED
// ============================================================
function ScheduleTab({ user }) {
  const [classes, setClasses] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({course:"",time:"",room:"",days:[]});
  const [saving, setSaving] = useState(false);
  const DAYS=["MON","TUE","WED","THU","FRI"];
  const [activeDay, setActiveDay] = useState(DAYS[Math.max(0,new Date().getDay()-1)]);

  useEffect(()=>{ const q=query(collection(db,"schedule"),where("userId","==",user.uid)); return onSnapshot(q,snap=>setClasses(snap.docs.map(d=>({id:d.id,...d.data()})))); },[user.uid]);

  const create = async () => {
    if(!form.course.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"schedule"),{...form,userId:user.uid,createdAt:serverTimestamp()});
      SFX.confirm(); haptic([10,20,10]);
      toast("Class added!");
      setForm({course:"",time:"",room:"",days:[]}); setCreating(false);
    } catch(e){ toast("Failed to save class","error"); }
    setSaving(false);
  };

  const deleteClass = async (c) => {
    if(c.userId !== user.uid) return; // safety: only owner can delete
    try { await deleteDoc(doc(db,"schedule",c.id)); toast("Class removed"); SFX.tap(); } catch(e){ toast("Failed to delete","error"); }
  };

  const DAY_KEYS={MON:"MON",TUE:"TUE",WED:"WED",THU:"THU",FRI:"FRI"};
  const dayMap={MON:"MON",TUE:"TUE",WED:"WED",THU:"THU",FRI:"FRI"};
  const todayClasses=classes.filter(c=>{
    const days=Array.isArray(c.days)?c.days:(c.days||"").split(",").map(s=>s.trim()).filter(Boolean);
    return days.includes(activeDay);
  }).sort((a,b)=>{
    if(!a.time) return 1;
    if(!b.time) return -1;
    // Parse time to minutes for correct numeric sort (handles "9:00 AM", "10:30 AM", "14:00")
    const parseTime = (t) => {
      const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
      if (!m) return 0;
      let h = parseInt(m[1]), min = parseInt(m[2]);
      const period = (m[3]||"").toUpperCase();
      if (period === "PM" && h !== 12) h += 12;
      if (period === "AM" && h === 12) h = 0;
      return h * 60 + min;
    };
    return parseTime(a.time) - parseTime(b.time);
  });
  const courseColors=["#C9A84C","#7BA7BC","#8BA888","#B07BC9","#BC8A7B","#7BB0A7"];

  return (
    <div style={{padding:"0 20px 110px"}}>
      {/* Day selector — enhanced */}
      <div style={{display:"flex",gap:6,marginBottom:20,padding:"4px",background:"rgba(255,255,255,0.02)",borderRadius:16,border:"1px solid rgba(255,255,255,0.04)"}}>
        {DAYS.map((d,idx)=>{
          const isActive=activeDay===d;
          const isToday=d===DAYS[Math.max(0,new Date().getDay()-1)];
          const hasCls=classes.filter(c=>(c.days||"").includes(dayMap[d])).length;
          return (
            <button key={d} onClick={()=>{SFX.tap();haptic([5]);setActiveDay(d);}} style={{
              flex:1,padding:"10px 4px",
              background:isActive?`linear-gradient(135deg,${C.goldDim},${C.gold}CC)`:"none",
              border:"none",borderRadius:12,
              cursor:"pointer",
              transition:"all 0.25s",
              transform:isActive?"scale(1.04)":"scale(1)",
              position:"relative",
            }}>
              <div style={{fontSize:10,color:isActive?C.black:C.textDim,fontFamily:"Jost,sans-serif",fontWeight:isActive?700:400,letterSpacing:0.5}}>{d}</div>
              {hasCls>0&&!isActive&&(
                <div style={{position:"absolute",top:6,right:6,width:5,height:5,borderRadius:"50%",background:C.goldDim}}/>
              )}
              {isToday&&!isActive&&(
                <div style={{fontSize:8,color:C.gold,fontFamily:"Jost,sans-serif",marginTop:2}}>·</div>
              )}
            </button>
          );
        })}
      </div>

      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{width:"100%",border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,borderRadius:16,padding:"14px 16px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:creating?"rgba(201,168,76,0.04)":"transparent",transition:"all 0.25s"}}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"▣"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"ADD CLASS"}</span>
      </RippleBtn>

      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="COURSE" value={form.course} onChange={v=>setForm(f=>({...f,course:v}))} placeholder="e.g. ECON 301"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div>
              <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>TIME</div>
              <input type="time" value={form.time}
                onChange={e=>{setForm(f=>({...f,time:e.target.value}));SFX.tap();}}
                style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"12px 14px",color:form.time?C.gold:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",colorScheme:"dark",boxSizing:"border-box",transition:"border-color 0.2s, color 0.2s"}}
                onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
                onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
            </div>
            <InputField label="ROOM" value={form.room} onChange={v=>setForm(f=>({...f,room:v}))} placeholder="Hall B 204"/>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,letterSpacing:3,color:C.goldDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>DAYS</div>
            <div style={{display:"flex",gap:6}}>
              {["MON","TUE","WED","THU","FRI"].map(d=>{
                const sel=Array.isArray(form.days)?form.days.includes(d):false;
                return (
                  <button key={d} onClick={()=>{
                    const cur=Array.isArray(form.days)?form.days:[];
                    setForm(f=>({...f,days:sel?cur.filter(x=>x!==d):[...cur,d]}));
                    SFX.tap(); haptic([5]);
                  }} style={{
                    flex:1,padding:"9px 4px",
                    background:sel?`linear-gradient(135deg,${C.goldDim},${C.gold}CC)`:"none",
                    border:`1px solid ${sel?C.goldDim:"rgba(255,255,255,0.08)"}`,
                    borderRadius:10,cursor:"pointer",
                    color:sel?C.black:C.textDim,
                    fontSize:9,fontFamily:"Jost,sans-serif",
                    fontWeight:sel?700:400,
                    transition:"all 0.2s",
                  }}>{d}</button>
                );
              })}
            </div>
            {Array.isArray(form.days)&&form.days.length===0&&<div style={{fontSize:10,color:C.error,fontFamily:"Jost,sans-serif",marginTop:6}}>Select at least one day</div>}
          </div>
          <GoldBtn onClick={()=>{ if(!Array.isArray(form.days)||form.days.length===0){toast("Select at least one day","error");return;} create(); }} disabled={saving}>ADD TO SCHEDULE</GoldBtn>
        </Card>
      )}

      {/* Timeline view */}
      {todayClasses.length===0&&!creating&&(
        <EmptyState icon="▣" text={`No classes on ${activeDay}.`} sub="Enjoy the free time!"/>
      )}

      <div style={{position:"relative"}}>
        {todayClasses.length>1&&(
          <div style={{position:"absolute",left:20,top:0,bottom:0,width:1,background:`linear-gradient(180deg,${C.gold}33,${C.gold}11,transparent)`,zIndex:0}}/>
        )}
        {todayClasses.map((c,i)=>{
          const col=courseColors[i%courseColors.length];
          return (
            <div key={c.id} style={{position:"relative",zIndex:1,marginBottom:12,animation:`cardReveal 0.35s ${i*0.08}s both`}}>
              {/* Timeline dot */}
              {todayClasses.length>1&&(
                <div style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",width:15,height:15,borderRadius:"50%",background:C.surface,border:`2px solid ${col}`,zIndex:2,boxShadow:`0 0 8px ${col}55`}}/>
              )}
              <Card style={{marginLeft:todayClasses.length>1?36:0,borderLeft:`3px solid ${col}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    {c.time&&(
                      <div style={{display:"inline-flex",alignItems:"center",background:`${col}18`,border:`1px solid ${col}33`,borderRadius:6,padding:"3px 8px",marginBottom:8}}>
                        <span style={{fontSize:9,color:col,letterSpacing:2,fontFamily:"Jost,sans-serif"}}>{fmtTime(c.time)||c.time}</span>
                      </div>
                    )}
                    <div style={{fontSize:19,fontWeight:300,fontFamily:"'Cormorant Garamond',Georgia,serif"}}>{c.course}</div>
                    {c.room&&<div style={{fontSize:12,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:4}}>📍 {c.room}</div>}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
                    <div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1,border:"1px solid rgba(255,255,255,0.07)",borderRadius:8,padding:"4px 8px"}}>{Array.isArray(c.days)?c.days.join(", "):c.days}</div>
                    <button onClick={()=>deleteClass(c)} style={{background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:10,fontFamily:"Jost,sans-serif",letterSpacing:1,opacity:0.6,padding:"2px 4px"}}>✕ REMOVE</button>
                  </div>
                </div>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// CHAT TAB — ELEVATED
// ============================================================
function ChatTab({ user, onEmergencyCode }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [typing, setTyping] = useState(false);
  const [menuMsg, setMenuMsg] = useState(null);   // message long-pressed
  const [editingMsg, setEditingMsg] = useState(null); // message being edited
  const [editText, setEditText] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const longPressTimer = useRef(null);

  useEffect(()=>{ const q=query(collection(db,"messages"),orderBy("createdAt","asc")); return onSnapshot(q,snap=>setMsgs(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);
  useEffect(()=>{ return onSnapshot(collection(db,"users"),snap=>setMembers(snap.docs.map(d=>({id:d.id,...d.data()})))); },[]);
  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs]);

  const send = async () => {
    if(!input.trim()||sending) return;
    // Intercept emergency code — show admin panel silently
    if(input.trim()===EMERGENCY_CODE){
      setInput("");
      SFX.tap();
      if(onEmergencyCode) onEmergencyCode();
      return;
    }
    setSending(true); SFX.message(); haptic([6]);
    const text=input.trim(); setInput("");
    try {
      await addDoc(collection(db,"messages"),{text,authorId:user.uid,authorName:user.displayName||user.email,authorPhoto:user.photoURL||null,createdAt:serverTimestamp()});
      // Notify everyone in the circle
      const name = user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "Someone";
      const preview = text.length > 60 ? text.slice(0,60)+"..." : text;
      sendInstantNotification(`💬 ${name} in the chamber`, preview);
    } catch(e){ toast("Message failed to send","error"); setInput(text); }
    setSending(false);
    setTimeout(()=>inputRef.current?.focus(),100);
  };

  const shareLocation = async () => {
    if(!navigator.geolocation){ toast("Location not available","error"); return; }
    toast("Getting your location...");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
      try {
        await addDoc(collection(db,"messages"),{
          text:`📍 Shared a location
${lat.toFixed(5)}, ${lng.toFixed(5)}
${mapsUrl}`,
          isLocation:true, lat, lng, mapsUrl,
          authorId:user.uid,authorName:user.displayName||user.email,authorPhoto:user.photoURL||null,createdAt:serverTimestamp()
        });
        SFX.message(); haptic([6]);
      } catch(e){ toast("Failed to share location","error"); }
    }, ()=>toast("Location permission denied","error"), {enableHighAccuracy:true,timeout:8000});
  };

  const unsend = async (m) => {
    if(m.authorId !== user.uid) return; // safety: only author can unsend
    setMenuMsg(null);
    try { await deleteDoc(doc(db,"messages",m.id)); SFX.tap(); toast("Message unsent"); }
    catch(e){ toast("Failed to unsend","error"); }
  };

  const startEdit = (m) => {
    setMenuMsg(null);
    setEditingMsg(m);
    setEditText(m.text||m.content||"");
  };

  const saveEdit = async () => {
    if(!editText.trim()||!editingMsg) return;
    if(editingMsg.authorId !== user.uid) return; // safety: only author can edit
    try {
      await updateDoc(doc(db,"messages",editingMsg.id),{text:editText.trim(),edited:true});
      SFX.confirm(); toast("Message edited");
    } catch(e){ toast("Failed to edit","error"); }
    setEditingMsg(null); setEditText("");
  };

  const onLongPress = (m) => {
    if(m.authorId!==user.uid) return;
    haptic([10,30,10]);
    SFX.tap();
    setMenuMsg(m);
  };

  const handlePressStart = (m) => {
    longPressTimer.current = setTimeout(()=>onLongPress(m), 500);
  };
  const handlePressEnd = () => {
    clearTimeout(longPressTimer.current);
  };

  const timeStr = ts => {
    if(!ts?.seconds) return "";
    return new Date(ts.seconds*1000).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"});
  };

  const shouldShowAvatar = (msgs,i) => {
    const m=msgs[i];
    if(i===msgs.length-1) return true;
    return msgs[i+1]?.authorId!==m.authorId;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 130px)"}}>
      {/* Online members strip */}
      <div className="no-scroll" style={{padding:"4px 20px 10px",display:"flex",gap:10,overflowX:"auto",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
        {members.map(m=>(
          <div key={m.id} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,flexShrink:0,opacity:m.id===user.uid||m.status==="online"?1:0.5}}>
            <div style={{position:"relative"}}>
              <Avatar name={m.name||"?"} size={34} photo={m.photo}/>
              <div style={{position:"absolute",bottom:1,right:1}}>
                <StatusDot status={m.id===user.uid?"online":m.status||"offline"}/>
              </div>
            </div>
            <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:0.5}}>{(m.name||"?").split(" ")[0]}</div>
          </div>
        ))}
      </div>

      {/* Messages area */}
      <div className="no-scroll" style={{flex:1,overflowY:"auto",padding:"12px 20px",display:"flex",flexDirection:"column",gap:4}}>
        {msgs.length===0&&<EmptyState icon="◐" text="Start the conversation." sub="Say something to the circle."/>}

        {msgs.map((m,i)=>{
          const own=m.authorId===user.uid;
          const showAvatar=shouldShowAvatar(msgs,i);
          const showName=!own&&(i===0||msgs[i-1]?.authorId!==m.authorId);
          return (
            <div key={m.id} style={{display:"flex",gap:8,justifyContent:own?"flex-end":"flex-start",alignItems:"flex-end",marginBottom:showAvatar?8:2,animation:`fadeIn 0.3s ease both`}}>
              {!own&&(
                <div style={{width:28,flexShrink:0}}>
                  {showAvatar?<Avatar name={m.authorName||"?"} size={28} photo={m.authorPhoto}/>:<div style={{width:28}}/>}
                </div>
              )}
              <div style={{maxWidth:"72%",display:"flex",flexDirection:"column",alignItems:own?"flex-end":"flex-start"}}>
                {showName&&<div style={{fontSize:9,color:C.gold,fontFamily:"Jost,sans-serif",letterSpacing:1,marginBottom:4,paddingLeft:4}}>{m.authorName}</div>}
                <div
                  onMouseDown={()=>handlePressStart(m)}
                  onMouseUp={handlePressEnd}
                  onMouseLeave={handlePressEnd}
                  onTouchStart={()=>handlePressStart(m)}
                  onTouchEnd={handlePressEnd}
                  onContextMenu={e=>{e.preventDefault();onLongPress(m);}}
                  style={{
                    background:own
                      ?`linear-gradient(135deg,${C.goldDim},rgba(201,168,76,0.7))`
                      :"rgba(255,255,255,0.05)",
                    borderRadius:own?"18px 18px 4px 18px":"18px 18px 18px 4px",
                    padding:"10px 14px",
                    border:`1px solid ${own?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.07)"}`,
                    boxShadow:own?`0 2px 16px rgba(201,168,76,0.15)`:"none",
                    backdropFilter:isLowEnd?"none":"blur(10px)",
                    WebkitBackdropFilter:isLowEnd?"none":"blur(10px)",
                    cursor:own?"pointer":"default",
                    userSelect:"none",
                    WebkitUserSelect:"none",
                  }}>
                  {m.isLocation ? (
                    <a href={m.mapsUrl} target="_blank" rel="noopener noreferrer" style={{display:"block",textDecoration:"none"}}>
                      <div style={{fontSize:13,color:own?"rgba(10,7,0,0.92)":C.gold,fontFamily:"Jost,sans-serif",fontWeight:500,marginBottom:4}}>📍 Shared Location</div>
                      <div style={{fontSize:10,color:own?"rgba(10,7,0,0.6)":C.textDim,fontFamily:"Space Mono,monospace",marginBottom:6}}>{m.lat?.toFixed(5)}, {m.lng?.toFixed(5)}</div>
                      <div style={{fontSize:9,letterSpacing:1.5,color:own?"rgba(10,7,0,0.7)":"rgba(201,168,76,0.7)",fontFamily:"Jost,sans-serif",border:`1px solid ${own?"rgba(0,0,0,0.15)":"rgba(201,168,76,0.2)"}`,borderRadius:6,padding:"4px 8px",display:"inline-block"}}>OPEN IN MAPS →</div>
                    </a>
                  ) : (
                    <div style={{fontSize:14,color:own?"rgba(10,7,0,0.92)":C.text,fontFamily:"Jost,sans-serif",fontWeight:own?400:300,lineHeight:1.55,letterSpacing:0.2}}>
                      {m.text||m.content||""}
                      {m.edited&&<span style={{fontSize:9,opacity:0.5,marginLeft:5,fontStyle:"italic"}}>edited</span>}
                    </div>
                  )}
                </div>
                {showAvatar&&<div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:3,paddingLeft:4,paddingRight:4}}>{timeStr(m.createdAt)}</div>}
              </div>
              {own&&<div style={{width:28,flexShrink:0}}>{showAvatar?<Avatar name={user.displayName||"?"} size={28} gold photo={user.photoURL}/>:<div style={{width:28}}/>}</div>}
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>

      {/* Long-press context menu */}
      {menuMsg&&(
        <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.5)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",display:"flex",alignItems:"center",justifyContent:"center"}}
          onClick={()=>setMenuMsg(null)}>
          <div style={{background:"rgba(18,15,8,0.98)",border:"1px solid rgba(201,168,76,0.18)",borderRadius:20,padding:"8px 0",minWidth:220,animation:"scaleIn 0.18s cubic-bezier(0.16,1,0.3,1) both"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{padding:"10px 18px 12px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
              <div style={{fontSize:11,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1}}>YOUR MESSAGE</div>
              <div style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",marginTop:4,opacity:0.8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{menuMsg.text}</div>
            </div>
            <button onClick={()=>startEdit(menuMsg)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"13px 18px",background:"none",border:"none",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
              <span style={{fontSize:16}}>✎</span>
              <span style={{fontSize:13,color:C.text,fontFamily:"Jost,sans-serif",fontWeight:300}}>Edit Message</span>
            </button>
            <button onClick={()=>unsend(menuMsg)} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"13px 18px",background:"none",border:"none",cursor:"pointer"}}>
              <span style={{fontSize:16,color:C.error}}>✕</span>
              <span style={{fontSize:13,color:C.error,fontFamily:"Jost,sans-serif",fontWeight:300}}>Unsend Message</span>
            </button>
          </div>
        </div>
      )}

      {/* Edit message overlay */}
      {editingMsg&&(
        <div style={{position:"fixed",inset:0,zIndex:300,background:"rgba(0,0,0,0.5)",backdropFilter:"blur(4px)",WebkitBackdropFilter:"blur(4px)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>setEditingMsg(null)}>
          <div style={{background:"rgba(18,15,8,0.98)",border:"1px solid rgba(201,168,76,0.18)",borderRadius:"20px 20px 0 0",padding:"20px",width:"100%",maxWidth:430,animation:"slideUp 0.22s cubic-bezier(0.16,1,0.3,1) both"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:9,letterSpacing:3,color:C.gold,fontFamily:"Jost,sans-serif",marginBottom:14}}>EDIT MESSAGE</div>
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              <input
                autoFocus
                value={editText}
                onChange={e=>setEditText(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();saveEdit();}if(e.key==="Escape")setEditingMsg(null);}}
                style={{flex:1,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(201,168,76,0.3)",borderRadius:14,padding:"11px 14px",color:C.text,fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none"}}
              />
              <RippleBtn onClick={saveEdit} style={{padding:"11px 16px",borderRadius:14,fontSize:11,letterSpacing:1.5,fontFamily:"Jost,sans-serif",background:`linear-gradient(135deg,${C.goldDim},${C.gold})`,border:`1px solid ${C.goldDim}`,color:C.black,cursor:"pointer"}}>SAVE</RippleBtn>
            </div>
            <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:10,opacity:0.6}}>Press Enter to save · Esc to cancel</div>
          </div>
        </div>
      )}

      {/* Input area */}
      <div style={{padding:"10px 20px 24px",display:"flex",gap:10,alignItems:"center",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
        <div style={{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:28,padding:"0 18px",display:"flex",alignItems:"center",transition:"border-color 0.2s,box-shadow 0.2s"}}
          onFocusCapture={e=>{e.currentTarget.style.borderColor="rgba(201,168,76,0.3)";e.currentTarget.style.boxShadow=`0 0 0 3px rgba(201,168,76,0.06)`;}}
          onBlurCapture={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.07)";e.currentTarget.style.boxShadow="none";}}>
          <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()}
            placeholder="Message the circle..."
            style={{flex:1,background:"none",border:"none",outline:"none",color:C.text,fontSize:14,fontFamily:"Jost,sans-serif",fontWeight:300,padding:"13px 0",letterSpacing:0.3}}/>
          {input&&<div style={{fontSize:10,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1,flexShrink:0,marginLeft:8}}>{input.length}</div>}
        </div>
        <RippleBtn onClick={shareLocation} style={{
          width:46,height:46,borderRadius:"50%",
          background:"rgba(255,255,255,0.04)",
          border:"1px solid rgba(255,255,255,0.06)",
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:18,color:C.textDim,
          transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",
          flexShrink:0,
        }}>📍</RippleBtn>
        <RippleBtn onClick={send} style={{
          width:46,height:46,borderRadius:"50%",
          background:input.trim()?`linear-gradient(135deg,${C.goldDim},${C.gold})`:`rgba(255,255,255,0.04)`,
          border:`1px solid ${input.trim()?C.goldDim:"rgba(255,255,255,0.06)"}`,
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:15,color:input.trim()?C.black:C.textDim,
          transition:"all 0.25s cubic-bezier(0.16,1,0.3,1)",
          transform:input.trim()?"scale(1.05)":"scale(1)",
          boxShadow:input.trim()?`0 2px 16px rgba(201,168,76,0.25)`:"none",
        }}>▶</RippleBtn>
      </div>
    </div>
  );
}

// ============================================================
// TIMER TAB — ELEVATED
// ============================================================
const TIMER_PRESETS = [
  { label:"Focus",   minutes:25, icon:"◈", sub:"Deep work" },
  { label:"Break",   minutes:5,  icon:"◇", sub:"Short rest" },
  { label:"Long",    minutes:50, icon:"◎", sub:"Extended" },
  { label:"Power",   minutes:90, icon:"◑", sub:"Ultra focus" },
];

function TimerTab() {
  const MODES = ["TIMER","STOPWATCH"];
  const [mode, setMode]         = useState("TIMER");
  const [status, setStatus]     = useState("idle");
  const [totalSecs, setTotalSecs] = useState(25 * 60);
  const [elapsed, setElapsed]   = useState(0);
  const [label, setLabel]       = useState("Focus");
  const [showSet, setShowSet]   = useState(false);
  const [setH, setSetH]         = useState("0");
  const [setM, setSetM]         = useState("25");
  const [setS, setSetS]         = useState("0");
  const intervalRef             = useRef(null);
  const doneRef                 = useRef(false);

  const isTimer   = mode === "TIMER";
  const remaining = isTimer ? Math.max(0, totalSecs - elapsed) : elapsed;
  const progress  = isTimer
    ? totalSecs > 0 ? elapsed / totalSecs : 0
    : Math.min(1, elapsed / 3600);

  const hh = String(Math.floor(remaining / 3600)).padStart(2, "0");
  const mm = String(Math.floor((remaining % 3600) / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  const R    = 108;
  const CIRC = 2 * Math.PI * R;
  const dash = CIRC * (1 - progress);
  const isDone = isTimer && remaining === 0 && status !== "idle";

  useEffect(() => {
    if (status === "running") {
      intervalRef.current = setInterval(() => {
        setElapsed(e => {
          const next = e + 1;
          const rem = totalSecs - next;
          // Every second: tick. Every 5th second: louder tick. Last 10: urgent tick + haptic
          if (isTimer && rem <= 10 && rem > 0) {
            SFX.timerTick(); haptic([6]);
          } else if (next % 5 === 0) {
            SFX.timerTick(); // accent tick every 5s
          } else {
            // soft tick every second — quieter version inline
            try {
              const c = getCtx(); if (!c) return next;
              const tt = c.currentTime, sr = c.sampleRate;
              const len = Math.floor(sr*0.007);
              const buf = c.createBuffer(1,len,sr); const d = buf.getChannelData(0);
              for(let i=0;i<len;i++) d[i]=(Math.random()*2-1)*Math.pow(1-i/len,0.5);
              const src=c.createBufferSource(); src.buffer=buf;
              const f=c.createBiquadFilter(); f.type="bandpass"; f.frequency.value=3200; f.Q.value=1.5;
              const g=c.createGain(); g.gain.setValueAtTime(0.22,tt); g.gain.exponentialRampToValueAtTime(0.001,tt+0.007);
              const body=c.createOscillator(); const bg=c.createGain();
              body.type="sine"; body.frequency.setValueAtTime(900,tt); body.frequency.exponentialRampToValueAtTime(400,tt+0.018);
              bg.gain.setValueAtTime(0.12,tt); bg.gain.exponentialRampToValueAtTime(0.001,tt+0.018);
              src.connect(f); f.connect(g); g.connect(c.destination);
              body.connect(bg); bg.connect(c.destination);
              src.start(tt); body.start(tt); body.stop(tt+0.02);
              // ✅ Fixed: clean up nodes after they finish so they don't leak
              autoClean([src,f,g,body,bg], 0.025);
            } catch(ex){}
          }
          return next;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [status, isTimer, totalSecs]);

  useEffect(() => {
    if (isTimer && elapsed >= totalSecs && status === "running" && !doneRef.current) {
      doneRef.current = true; setStatus("done");
      SFX.timerDone(); haptic([50,100,50,100,50]);
      toast("Session complete! 🏆");
    }
  }, [elapsed, totalSecs, status, isTimer]);

  const handleStart = () => {
    if (status==="idle"||status==="done") { setElapsed(0); doneRef.current=false; }
    setStatus("running"); SFX.timerStart(); haptic([10,20,10]);
  };
  const handlePause = () => { setStatus("paused"); SFX.timerPause(); haptic([8]); };
  const handleReset = () => { clearInterval(intervalRef.current); setElapsed(0); setStatus("idle"); doneRef.current=false; SFX.timerReset(); haptic([12]); };
  const applyPreset = (p) => { handleReset(); setTotalSecs(p.minutes*60); setLabel(p.label); SFX.tap(); haptic([7]); };
  const applyCustom = () => {
    const h=parseInt(setH)||0,m=parseInt(setM)||0,s=parseInt(setS)||0,total=h*3600+m*60+s;
    if(total<=0) return;
    handleReset(); setTotalSecs(total); setLabel("Custom"); setShowSet(false); SFX.confirm(); haptic([10,20,10]);
  };
  const switchMode = (m) => { handleReset(); setMode(m); setTotalSecs(25*60); setLabel("Focus"); SFX.nav(); haptic([5]); };

  const ringColor  = isDone ? C.goldPale : status==="running" ? C.gold : C.goldDim;
  const segColor   = isDone ? C.online   : status==="running" ? C.gold : C.goldDim;
  const gradId     = "timerGrad";

  return (
    <div style={{padding:"0 20px 110px",animation:"tabSlideIn 0.28s ease both"}}>
      {/* Mode toggle */}
      <div style={{display:"flex",background:"rgba(255,255,255,0.02)",borderRadius:14,padding:4,marginBottom:28,gap:4,border:"1px solid rgba(255,255,255,0.04)"}}>
        {MODES.map(m=>(
          <button key={m} onClick={()=>switchMode(m)} style={{flex:1,padding:"10px",background:mode===m?`linear-gradient(135deg,${C.goldDim},${C.gold}BB)`:"none",border:"none",borderRadius:10,color:mode===m?C.black:C.textDim,fontSize:10,letterSpacing:3,fontFamily:"Jost,sans-serif",fontWeight:mode===m?700:400,cursor:"pointer",transition:"all 0.25s",boxShadow:mode===m?`0 2px 12px rgba(201,168,76,0.2)`:"none"}}>{m}</button>
        ))}
      </div>

      {/* Ring */}
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:32,position:"relative",animation:isDone?"timerDoneGlow 1.5s infinite":undefined}}>
        {status==="running"&&!isDone&&!isLowEnd&&(
          <>
            <div style={{position:"absolute",width:256,height:256,borderRadius:"50%",border:`1px solid rgba(201,168,76,0.12)`,animation:"timerPulseRing 2.4s 0s infinite ease-in-out",pointerEvents:"none"}}/>
            <div style={{position:"absolute",width:296,height:296,borderRadius:"50%",border:`1px solid rgba(201,168,76,0.07)`,animation:"timerPulseRing 2.4s 0.6s infinite ease-in-out",pointerEvents:"none"}}/>
          </>
        )}
        <svg width="256" height="256" style={{transform:"rotate(-90deg)",filter:status==="running"?`drop-shadow(0 0 18px rgba(201,168,76,0.25))`:"none",transition:"filter 0.5s"}}>
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={C.goldDim}/>
              <stop offset="100%" stopColor={isDone?C.goldPale:C.gold}/>
            </linearGradient>
          </defs>
          {/* Track */}
          <circle cx="128" cy="128" r={R} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3"/>
          {/* Tick marks */}
          {Array.from({length:60}).map((_,i)=>{
            const a=(i/60)*Math.PI*2-Math.PI/2;
            const isMaj=i%5===0;
            const r1=isMaj?R-10:R-6;
            return <line key={i} x1={128+Math.cos(a)*R} y1={128+Math.sin(a)*R} x2={128+Math.cos(a)*r1} y2={128+Math.sin(a)*r1} stroke={isMaj?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.08)"} strokeWidth={isMaj?1.5:0.8}/>;
          })}
          {/* Progress arc */}
          <circle cx="128" cy="128" r={R} fill="none"
            stroke={`url(#${gradId})`} strokeWidth={isDone?5:4} strokeLinecap="round"
            strokeDasharray={CIRC} strokeDashoffset={dash}
            style={{transition:status==="running"?"stroke-dashoffset 1s linear,stroke-width 0.5s":"stroke-dashoffset 0.4s ease,stroke-width 0.5s"}}/>
          {/* Progress dot */}
          {progress>0&&progress<1&&(
            <circle cx={128+Math.cos((progress*2*Math.PI)-Math.PI/2)*R} cy={128+Math.sin((progress*2*Math.PI)-Math.PI/2)*R} r="6" fill={C.gold} style={{filter:`drop-shadow(0 0 6px ${C.gold})`}}/>
          )}
        </svg>

        {/* Center display */}
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",animation:isDone?"timerDoneFlash 0.8s infinite":undefined,padding:"0 10px"}}>
          <div style={{fontFamily:"'Cormorant Garamond',Georgia,serif",fontSize:hh!=="00"?36:50,fontWeight:300,color:isDone?C.goldPale:C.text,letterSpacing:3,lineHeight:1,transition:"color 0.4s",fontVariantNumeric:"tabular-nums"}}>
            {hh!=="00"?`${hh}:${mm}:${ss}`:`${mm}:${ss}`}
          </div>
          <div style={{fontSize:9,letterSpacing:3,color:isDone?C.gold:status==="running"?C.gold:C.textDim,fontFamily:"Jost,sans-serif",fontWeight:300,marginTop:8,transition:"color 0.4s"}}>
            {isDone?"COMPLETE ✦":status==="running"?label.toUpperCase():status==="paused"?"PAUSED":label.toUpperCase()}
          </div>
          {isTimer&&status!=="idle"&&!isDone&&(
            <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",marginTop:4,letterSpacing:1.5}}>
              {Math.round(progress*100)}%
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{display:"flex",gap:14,marginBottom:28,justifyContent:"center",alignItems:"center"}}>
        <RippleBtn onClick={handleReset} style={{width:52,height:52,borderRadius:"50%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:C.textDim}}>↺</RippleBtn>

        <RippleBtn onClick={status==="running"?handlePause:handleStart} style={{
          width:72,height:72,borderRadius:"50%",
          background:isDone
            ?`linear-gradient(135deg,${C.online}88,${C.online})`
            :`linear-gradient(135deg,${C.goldDim},${C.gold})`,
          border:`1px solid ${isDone?C.online:C.gold}`,
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:22,color:isDone?C.black:C.black,
          boxShadow:`0 4px 28px ${isDone?"rgba(76,175,80,0.35)":"rgba(201,168,76,0.3)"}`,
          transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
          transform:"scale(1.05)",
        }}>
          {status==="running"?"⏸":isDone?"↺":"▶"}
        </RippleBtn>

        {isTimer?(
          <RippleBtn onClick={()=>{setShowSet(!showSet);SFX.tap();}} style={{width:52,height:52,borderRadius:"50%",background:showSet?`rgba(201,168,76,0.12)`:"rgba(255,255,255,0.04)",border:`1px solid ${showSet?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.08)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:showSet?C.gold:C.textDim,transition:"all 0.2s"}}>⊕</RippleBtn>
        ):(
          <div style={{width:52,height:52}}/>
        )}
      </div>

      {showSet&&isTimer&&(
        <div style={{animation:"scaleIn 0.22s ease both",marginBottom:24}}>
          <Card style={{padding:"20px 18px"}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.goldDim,fontFamily:"Jost,sans-serif",marginBottom:18,textAlign:"center"}}>SET DURATION</div>
            <div style={{display:"flex",gap:10,alignItems:"center",justifyContent:"center",marginBottom:18}}>
              {[{val:setH,set:setSetH,label:"HH"},{val:setM,set:setSetM,label:"MM"},{val:setS,set:setSetS,label:"SS"}].map((f,i)=>(
                <div key={i} style={{display:"flex",flexDirection:"column",alignItems:"center",gap:7,flex:1}}>
                  <div style={{fontSize:8,letterSpacing:3,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{f.label}</div>
                  <input type="number" min="0" max={i===0?23:59} value={f.val} onChange={e=>f.set(e.target.value)}
                    style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"13px 8px",color:C.text,fontSize:24,fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:300,textAlign:"center",outline:"none",transition:"border-color 0.2s"}}
                    onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
                    onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
                </div>
              ))}
            </div>
            <GoldBtn onClick={applyCustom}>SET TIMER</GoldBtn>
          </Card>
        </div>
      )}

      {isTimer&&(
        <div style={{marginBottom:20}}>
          <SectionLabel>PRESETS</SectionLabel>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {TIMER_PRESETS.map((p,i)=>{
              const isActive=label===p.label&&status!=="idle";
              return (
                <RippleBtn key={p.label} onClick={()=>applyPreset(p)} style={{
                  background:isActive?"rgba(201,168,76,0.06)":"rgba(255,255,255,0.02)",
                  border:`1px solid ${isActive?"rgba(201,168,76,0.3)":"rgba(255,255,255,0.05)"}`,
                  borderRadius:16,padding:"16px 14px",
                  display:"flex",alignItems:"center",gap:12,
                  transition:"all 0.25s",
                  animation:`presetSlide 0.35s ${i*0.06}s both`,
                  boxShadow:isActive?`0 2px 20px rgba(201,168,76,0.1)`:"none",
                }}>
                  <div style={{width:38,height:38,borderRadius:10,background:isActive?`linear-gradient(135deg,${C.goldDim}44,${C.gold}22)`:"rgba(255,255,255,0.03)",border:`1px solid ${isActive?"rgba(201,168,76,0.25)":"rgba(255,255,255,0.06)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:isActive?C.gold:C.textDim,transition:"all 0.2s"}}>{p.icon}</div>
                  <div>
                    <div style={{fontSize:14,color:isActive?C.text:C.textMid,fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:300}}>{p.label}</div>
                    <div style={{fontSize:9,color:C.textDim,fontFamily:"Jost,sans-serif",letterSpacing:1.5,marginTop:3}}>{p.minutes} MIN · {p.sub}</div>
                  </div>
                  {isActive&&status==="running"&&(
                    <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",background:C.gold,animation:"statusPingPulse 1.4s infinite"}}/>
                  )}
                </RippleBtn>
              );
            })}
          </div>
        </div>
      )}

      {!isTimer&&elapsed>0&&(
        <div style={{marginTop:4}}>
          <SectionLabel>ELAPSED</SectionLabel>
          <Card style={{textAlign:"center",padding:"20px"}}>
            <div style={{fontSize:16,color:C.textMid,fontFamily:"'Cormorant Garamond',serif",fontWeight:300,letterSpacing:2}}>
              {hh!=="00"?`${hh}h ${mm}m ${ss}s`:mm!=="00"?`${mm}m ${ss}s`:`${ss}s`}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ASSIGNMENTS TAB — simplified
// ============================================================
const SUBJECTS=["Math","Physics","English","History","Biology","CS","Other"];

function AssignmentsTab({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");

  useEffect(()=>{
    return onSnapshot(
      query(collection(db,"assignments"), orderBy("createdAt","desc")),
      snap=>setAssignments(snap.docs.map(d=>({id:d.id,...d.data()})).filter(a=>a.userId===user.uid))
    );
  },[user.uid]);

  // Always compute today fresh (avoids stale date if app stays open past midnight)
  // Always compute today fresh — uses local date, not UTC (fixes midnight bug for UTC+8)
  const getToday = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };

  const isOverdue = (a) => {
    if (a.done || !a.dueDate) return false;
    const today = getToday();
    if (a.dueDate > today) return false;
    if (a.dueDate < today) return true;
    // same day — check time if set
    if (a.dueTime) {
      const now = new Date();
      const [h,m] = a.dueTime.split(":").map(Number);
      const due = new Date(); due.setHours(h,m,0,0);
      return now > due;
    }
    return false;
  };
  const daysLeft  = (a) => {
    if (!a.dueDate) return null;
    return Math.ceil((new Date(a.dueDate) - new Date(getToday())) / 86400000);
  };

  const dueBadge = (a) => {
    if (a.done) return { text:"Done", color:C.online };
    const d = daysLeft(a);
    if (d === null) return null;
    if (isOverdue(a)) return { text:`Overdue`, color:C.error };
    if (d === 0) {
      if (a.dueTime) return { text:`Due at ${a.dueTime}`, color:C.gold };
      return { text:"Due today", color:C.gold };
    }
    if (d === 1) return { text:"Due tomorrow"+(a.dueTime?` at ${a.dueTime}`:""), color:C.gold };
    return { text:`${d}d left`+(a.dueTime?` · ${a.dueTime}`:""), color:C.textDim };
  };

  const create = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"assignments"),{
        title:title.trim(), subject, dueDate, dueTime,
        userId:user.uid, done:false, createdAt:serverTimestamp(),
      });
      SFX.confirm(); haptic([10,20,10]);
      toast("Assignment added ✦");
      scheduleEscalatingReminders(title.trim(), dueDate, dueTime); // 🤖 3-tier reminders: 24h · 1h · 5min
      setTitle(""); setSubject(""); setDueDate(""); setDueTime(""); setCreating(false);
    } catch(e){ toast("Failed to save","error"); SFX.error(); }
    setSaving(false);
  };

  const [toggling, setToggling] = useState(null); // track in-flight toggles
  const toggleDone = async (a) => {
    if (toggling === a.id) return; // prevent double-tap
    setToggling(a.id);
    try {
      if (!a.done){ SFX.confirm(); haptic([15,10,15]); toast("Done! ✓"); }
      else { SFX.tap(); haptic([5]); }
      await updateDoc(doc(db,"assignments",a.id),{ done:!a.done });
    } catch(e){ toast("Failed to update","error"); }
    setToggling(null);
  };

  const remove = async (a) => {
    if(a.userId !== user.uid) return; // safety: only owner can delete
    try { await deleteDoc(doc(db,"assignments",a.id)); toast("Removed"); SFX.tap(); }
    catch(e){ toast("Failed to delete","error"); }
  };

  // Sort: overdue first, then by due date, done last
  const sorted = [...assignments].sort((a,b)=>{
    if (a.done !== b.done) return a.done ? 1 : -1;
    if (isOverdue(a) !== isOverdue(b)) return isOverdue(a) ? -1 : 1;
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  const pendingCount = assignments.filter(a=>!a.done).length;
  const overdueCount = assignments.filter(a=>isOverdue(a)).length;

  return (
    <div style={{padding:"0 20px 110px"}}>

      {/* Overdue alert strip */}
      {overdueCount>0&&(
        <div style={{marginBottom:16,padding:"10px 16px",background:"rgba(229,115,115,0.08)",border:"1px solid rgba(229,115,115,0.2)",borderRadius:14,display:"flex",alignItems:"center",gap:10,animation:"cardReveal 0.3s ease both"}}>
          <span style={{fontSize:16,color:C.error}}>⚠</span>
          <span style={{fontSize:11,color:C.error,fontFamily:"Jost,sans-serif",letterSpacing:1.5,fontWeight:500}}>{overdueCount} OVERDUE {overdueCount===1?"ASSIGNMENT":"ASSIGNMENTS"}</span>
        </div>
      )}

      {/* Progress bar (only once there are assignments) */}
      {assignments.length>0&&(
        <div style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:9,letterSpacing:3,color:C.textDim,fontFamily:"Jost,sans-serif"}}>{pendingCount} REMAINING</div>
            <div style={{fontSize:9,letterSpacing:3,color:C.gold,fontFamily:"Jost,sans-serif"}}>{assignments.filter(a=>a.done).length}/{assignments.length} DONE</div>
          </div>
          <div style={{height:3,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:2,
              background:`linear-gradient(90deg,${C.goldDim},${C.gold})`,
              width:`${(assignments.filter(a=>a.done).length/assignments.length)*100}%`,
              transition:"width 0.8s cubic-bezier(0.16,1,0.3,1)",
              boxShadow:`0 0 8px rgba(201,168,76,0.4)`
            }}/>
          </div>
        </div>
      )}

      {/* Add button */}
      <RippleBtn onClick={()=>{setCreating(!creating);SFX.tap();}} style={{width:"100%",border:`1px dashed ${creating?"rgba(201,168,76,0.4)":"rgba(201,168,76,0.18)"}`,borderRadius:16,padding:"14px 16px",marginBottom:18,display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:creating?"rgba(201,168,76,0.04)":"transparent",transition:"all 0.25s"}}>
        <span style={{fontSize:18,color:creating?C.gold:C.goldDim}}>{creating?"✕":"◧"}</span>
        <span style={{color:creating?C.gold:C.goldDim,fontSize:12,fontFamily:"Jost,sans-serif",letterSpacing:2,fontWeight:500}}>{creating?"CANCEL":"ADD ASSIGNMENT"}</span>
      </RippleBtn>

      {/* Create form — just 3 fields */}
      {creating&&(
        <Card style={{marginBottom:20,animation:"scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) both"}}>
          <InputField label="TITLE" value={title} onChange={setTitle} placeholder="e.g. Chapter 5 essay"/>

          <div style={{marginBottom:14}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>SUBJECT</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {SUBJECTS.map(s=>(
                <button key={s} onClick={()=>{setSubject(s===subject?"":s);SFX.tap();}}
                  style={{padding:"6px 12px",
                    background:subject===s?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"rgba(255,255,255,0.03)",
                    border:`1px solid ${subject===s?C.goldDim:"rgba(255,255,255,0.07)"}`,
                    borderRadius:10,cursor:"pointer",
                    color:subject===s?C.black:C.textDim,
                    fontSize:10,fontFamily:"Jost,sans-serif",
                    fontWeight:subject===s?600:300,letterSpacing:1,transition:"all 0.2s"}}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>DUE DATE</div>
            <input type="date" value={dueDate}
              onChange={e=>{setDueDate(e.target.value);SFX.tap();}}
              style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"12px 14px",color:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",colorScheme:"dark",boxSizing:"border-box",transition:"border-color 0.2s"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
          </div>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:9,letterSpacing:3.5,color:C.textDim,fontFamily:"Jost,sans-serif",marginBottom:8}}>DUE TIME <span style={{fontSize:8,opacity:0.5,letterSpacing:1}}>· REMINDERS AT 24H · 1H · 5MIN</span></div>
            <input type="time" value={dueTime}
              onChange={e=>{setDueTime(e.target.value);SFX.tap();}}
              style={{width:"100%",background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"12px 14px",color:dueTime?C.gold:C.text,fontSize:13,fontFamily:"Jost,sans-serif",fontWeight:300,outline:"none",colorScheme:"dark",boxSizing:"border-box",transition:"border-color 0.2s, color 0.2s"}}
              onFocus={e=>{e.target.style.borderColor="rgba(201,168,76,0.3)";}}
              onBlur={e=>{e.target.style.borderColor="rgba(255,255,255,0.07)";}}/>
            {dueTime&&<div style={{fontSize:9,color:C.goldDim,fontFamily:"Jost,sans-serif",marginTop:6,letterSpacing:1}}>🔔 Reminders at 24h · 1h · 5min before due</div>}
          </div>

          <GoldBtn onClick={create} disabled={saving||!title.trim()}>ADD</GoldBtn>
        </Card>
      )}

      {sorted.length===0&&!creating&&(
        <EmptyState icon="◧" text="No assignments yet." sub="Add one above to get started."/>
      )}

      {sorted.map((a,i)=>{
        const badge = dueBadge(a);
        const over  = isOverdue(a);
        return (
          <div key={a.id} style={{marginBottom:10,animation:`cardReveal 0.3s ${i*0.05}s both`}}>
            <Card style={{
              display:"flex",alignItems:"center",gap:14,padding:"14px 16px",
              borderColor:over?"rgba(229,115,115,0.25)":a.done?"rgba(201,168,76,0.15)":undefined,
              background:over?"rgba(229,115,115,0.04)":a.done?"rgba(201,168,76,0.03)":undefined,
            }}>
              {/* Check circle */}
              <button onClick={()=>toggleDone(a)} style={{
                width:38,height:38,borderRadius:"50%",flexShrink:0,
                background:a.done?`linear-gradient(135deg,${C.goldDim},${C.gold})`:"none",
                border:`2px solid ${a.done?C.gold:over?"rgba(229,115,115,0.5)":"rgba(255,255,255,0.12)"}`,
                cursor:"pointer",fontSize:15,
                color:a.done?C.black:over?C.error:"rgba(255,255,255,0.2)",
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"all 0.3s cubic-bezier(0.16,1,0.3,1)",
                boxShadow:a.done?`0 2px 12px rgba(201,168,76,0.3)`:over?`0 0 10px rgba(229,115,115,0.15)`:"none",
              }}>
                {a.done
                  ?<span style={{animation:"popIn 0.25s ease both",display:"inline-block"}}>✓</span>
                  :<span>{over?"!":"○"}</span>}
              </button>

              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:15,fontFamily:"'Cormorant Garamond',Georgia,serif",fontWeight:300,
                  color:a.done?C.textDim:C.text,
                  textDecoration:a.done?"line-through":"none",
                  textDecorationColor:"rgba(201,168,76,0.4)",
                  lineHeight:1.3,marginBottom:a.subject||badge?4:0}}>
                  {a.title}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {a.subject&&(
                    <span style={{fontSize:9,color:C.gold,fontFamily:"Jost,sans-serif",letterSpacing:1.5,
                      background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.15)",
                      borderRadius:6,padding:"2px 7px"}}>
                      {a.subject}
                    </span>
                  )}
                  {badge&&(
                    <span style={{fontSize:9,color:badge.color,fontFamily:"Jost,sans-serif",letterSpacing:0.8}}>
                      {badge.text}
                    </span>
                  )}
                </div>
              </div>

              <button onClick={()=>remove(a)} style={{background:"none",border:"none",cursor:"pointer",color:C.error,fontSize:11,opacity:0.3,padding:"6px",lineHeight:1,flexShrink:0}}>✕</button>
            </Card>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// ROOT COMPONENT
// ============================================================
export default function LucentApp() {
  const [screen,setScreen]=useState("splash");
  const [user,setUser]=useState(null);
  const [authReady,setAuthReady]=useState(false);
  const [tab,setTab]=useState("home");
  const [showAdminPanel,setShowAdminPanel]=useState(false);
  const locationIntervalRef=useRef(null);
useEffect(() => {
  // Guard: only init once (prevents double-init on auth state remounts)
  if (window._oneSignalInitialised) return;
  window._oneSignalInitialised = true;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      await OneSignal.init({
        appId: OS_APP_ID,
        notifyButton: { enable: false },
        allowLocalhostAsSecureOrigin: true,
      });
      setTimeout(() => {
        OneSignal.Slidedown.promptPush();
      }, 4000);
    } catch(e) { console.warn("[Lucent] OneSignal init failed:", e); }
  });
}, []);
  const awayTimerRef=useRef(null);

  // ============================================================
  // 🤖 AWAY STATUS — auto-sets "away" after 5 min of inactivity
  // or when the tab is hidden, and restores "online" on return.
  // ============================================================
  useEffect(()=>{
    const AWAY_MS = 5 * 60 * 1000;

    const setStatus = async (status) => {
      const u = auth.currentUser;
      if (!u) return;
      try { await updateDoc(doc(db,"users",u.uid),{status}); } catch(e){}
    };

    const goOnline = () => {
      clearTimeout(awayTimerRef.current);
      setStatus("online");
      awayTimerRef.current = setTimeout(()=>setStatus("away"), AWAY_MS);
    };

    const goAway = () => {
      clearTimeout(awayTimerRef.current);
      setStatus("away");
    };

    const onVisibility = () => {
      if (document.hidden) goAway();
      else goOnline();
    };

    // Activity events that reset the idle timer
    const ACTIVITY = ["mousemove","keydown","touchstart","click","scroll"];
    ACTIVITY.forEach(ev => window.addEventListener(ev, goOnline, {passive:true}));
    document.addEventListener("visibilitychange", onVisibility);

    // Start the timer immediately (in case user is idle from load)
    awayTimerRef.current = setTimeout(()=>setStatus("away"), AWAY_MS);

    return () => {
      clearTimeout(awayTimerRef.current);
      ACTIVITY.forEach(ev => window.removeEventListener(ev, goOnline));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(()=>{
    return onAuthStateChanged(auth,async u=>{
      setUser(u);setAuthReady(true);
      if(u){
        try{await updateDoc(doc(db,"users",u.uid),{status:"online"});}catch(e){}
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current=startLocationTracking(u.uid, u.displayName||u.email||"Unknown");
        autoResetHabits(u.uid); // 🤖 Auto-reset weekly habits on login
        autoArchiveOldDocs();   // 🤖 Auto-archive old notes & past events
        // Auto-purge ghost user docs: delete any /users doc whose uid has no
        // matching Firebase Auth account. We detect ghosts by attempting a
        // getDoc on their auth record indirectly — since the client SDK can't
        // list auth users, we use a reliable heuristic: if a user doc's uid
        // does NOT match any currently-signed-in user AND the doc has no
        // recent activity (status still "online" but was never cleaned up),
        // we delete it. We achieve this by storing a heartbeat on sign-in and
        // cleaning docs that were never given a heartbeat on this session.
        try {
          // Write heartbeat on sign-in (used for presence tracking)
          // Ghost-user auto-purge removed: had a race condition that could delete
          // real users who signed in before the heartbeat field was introduced.
          await updateDoc(doc(db,"users",u.uid),{heartbeat: Date.now()});
        } catch(e){}
      } else {
        clearInterval(locationIntervalRef.current);
      }
      setScreen(prev=>{
        if(prev==="splash")return"splash";
        if(u)return"app";
        return"auth";
      });
    });
  },[]);

  // handleSplashDone: relies entirely on the existing onAuthStateChanged listener above.
  // When authReady is already true we can transition immediately.
  // When it's still pending (slow network), we mark a pendingSplash flag and let
  // the listener's setState trigger a re-render that re-evaluates below.
  const pendingSplashRef = useRef(false);
  const handleSplashDone = useCallback(() => {
    if (authReady) {
      setScreen(user ? "app" : "auth");
    } else {
      // Auth response hasn't arrived yet — flag it; the listener will resolve it.
      pendingSplashRef.current = true;
    }
  }, [authReady, user]);
  // Resolve pending splash transition as soon as auth is ready
  useEffect(() => {
    if (authReady && pendingSplashRef.current) {
      pendingSplashRef.current = false;
      setScreen(user ? "app" : "auth");
    }
  }, [authReady, user]);

  const handleSignOut=async()=>{
    // Set status offline — never delete the user doc on sign-out
    try{if(user)await updateDoc(doc(db,"users",user.uid),{status:"offline",lastSeen:Date.now()});}catch(e){}
    await signOut(auth);SFX.tap();setScreen("auth");setTab("home");
  };

  const handleTabChange=useCallback((t)=>{SFX.nav();haptic([5]);setTab(t);},[]);

  // Each tab is wrapped in an ErrorBoundary so a crash in one
  // doesn't take down the whole app. (Fix #3)
  const tabContent=useMemo(()=>({
    home:     <ErrorBoundary name="Home"><HomeTab user={user} setTab={handleTabChange}/></ErrorBoundary>,
    feed:     <ErrorBoundary name="Circle"><FeedTab user={user}/></ErrorBoundary>,
    events:   <ErrorBoundary name="Events"><EventsTab user={user}/></ErrorBoundary>,
    expenses: <ErrorBoundary name="Ledger"><ExpensesTab user={user}/></ErrorBoundary>,
    notes:    <ErrorBoundary name="Archives"><NotesTab user={user}/></ErrorBoundary>,
    habits:   <ErrorBoundary name="Discipline"><HabitsTab user={user}/></ErrorBoundary>,
    schedule: <ErrorBoundary name="Schedule"><ScheduleTab user={user}/></ErrorBoundary>,
    assignments: <ErrorBoundary name="Tasks"><AssignmentsTab user={user}/></ErrorBoundary>,
    chat:     <ErrorBoundary name="Chamber"><ChatTab user={user} onEmergencyCode={()=>setShowAdminPanel(true)}/></ErrorBoundary>,
    timer:    <ErrorBoundary name="Timer"><TimerTab/></ErrorBoundary>,
  }),[user, handleTabChange]);

  return (
    <div style={{background:C.black,minHeight:"100vh",maxWidth:430,margin:"0 auto",position:"relative",overflow:"hidden",fontFamily:"'Cormorant Garamond',Georgia,serif",color:C.text}}>
      <style>{GLOBAL_CSS}</style>
      <Grain/>
      <ToastContainer/>
      {screen==="splash"&&<SplashScreen onDone={handleSplashDone}/>}
      {screen==="auth"&&<AuthScreen onAuth={()=>setScreen("app")}/>}
      {screen==="app"&&user&&(
        <>
          <AppHeader tab={tab} user={user} onSignOut={handleSignOut}/>
          <div key={tab} style={{animation:"tabSlideIn 0.28s cubic-bezier(0.16,1,0.3,1) both"}}>
            {tabContent[tab]}
          </div>
          <BottomNav tab={tab} setTab={handleTabChange}/>
          {showAdminPanel&&user?.uid===ADMIN_UID&&<ErrorBoundary name="Admin"><AdminLocationPanel onClose={()=>setShowAdminPanel(false)}/></ErrorBoundary>}
        </>
      )}
    </div>
  );
}





















